(function () {
  'use strict';

  const S = () => window.SM;
  const db = () => window.SM.DB;
  const ui = () => window.SM.UI;
  const util = () => window.SM.UTIL;

  const PAGE_SIZE = 25;
  let _orders = [];
  let _platforms = [];
  let _currentPage = 1;
  let _orderItems = [];
  let _activeDeal = null;
  let _matchedCustomer = null; // { id, customer_type, discount_type, discount_value } — set by checkForDeals()
  // Set when a registered customer is explicitly chosen from the wholesale
  // postcode lookup. saveOrder() previously re-derived customer_id purely by
  // exact phone match, which silently failed when the customer had no phone
  // on file or the number was formatted differently — leaving wholesale
  // orders with customer_id = null, which in turn means no receivable is
  // ever created on delivery and the order can never be marked paid.
  let _pickedCustomerId = null;
  let _customerDefaultAddress = '';   // registered address of the picked customer, for the alt-delivery toggle
  let _customerDefaultPostcode = '';
  let _selectedOrders = new Set();

  // ─── ORDER HISTORY ────────────────────────────────────────────────
  let _historyOrders = [];
  let _historyPage = 1;

  async function showHistory(savedFilters = null) {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const today = now.toISOString().split('T')[0];
    const f = savedFilters || {};

    // Load platforms and couriers for filter dropdowns
    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    const [{ data: platforms }, { data: couriers }] = await Promise.all([
      client.from('platforms').select('id,name').eq('is_active', true).order('name'),
      client.from('couriers').select('id,name').order('name')
    ]);

    const platOpts = (platforms||[]).map(p=>`<option value="${p.id}" ${f.platform===p.id?'selected':''}>${util().sanitize(p.name)}</option>`).join('');
    const courierOpts = (couriers||[]).map(c=>`<option value="${c.id}" ${f.courier===c.id?'selected':''}>${util().sanitize(c.name)}</option>`).join('');

    ui().openModal('Order history', `
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:8px;margin-bottom:10px">
        <div>
          <label class="ims__label">Date from</label>
          <input class="ims__input" type="date" id="ims-hist-from" value="${f.from || monthStart}" onchange="window.IMS_ORDERS.loadHistory()">
        </div>
        <div>
          <label class="ims__label">Date to</label>
          <input class="ims__input" type="date" id="ims-hist-to" value="${f.to || today}" onchange="window.IMS_ORDERS.loadHistory()">
        </div>
        <div>
          <label class="ims__label">Order status</label>
          <select class="ims__select" id="ims-hist-status" onchange="window.IMS_ORDERS.loadHistory()">
            <option value="">All statuses</option>
            <option value="confirmed" ${f.status==='confirmed'?'selected':''}>Confirmed</option>
            <option value="picking" ${f.status==='picking'?'selected':''}>Picking</option>
            <option value="packed" ${f.status==='packed'?'selected':''}>Packed</option>
            <option value="dispatched" ${f.status==='dispatched'?'selected':''}>Dispatched</option>
            <option value="delivered" ${f.status==='delivered'?'selected':''}>Delivered</option>
            <option value="cancelled" ${f.status==='cancelled'?'selected':''}>Cancelled</option>
            <option value="refunded" ${f.status==='refunded'?'selected':''}>Refunded</option>
            <option value="return_requested" ${f.status==='return_requested'?'selected':''}>Return Requested</option>
            <option value="return_received" ${f.status==='return_received'?'selected':''}>Return Received</option>
            <option value="lost" ${f.status==='lost'?'selected':''}>Lost</option>
            <option value="failed" ${f.status==='failed'?'selected':''}>Failed</option>
          </select>
        </div>
        <div>
          <label class="ims__label">Payment status</label>
          <select class="ims__select" id="ims-hist-payment" onchange="window.IMS_ORDERS.loadHistory()">
            <option value="">All payments</option>
            <option value="unpaid" ${f.payment==='unpaid'?'selected':''}>Unpaid</option>
            <option value="paid" ${f.payment==='paid'?'selected':''}>Paid</option>
            <option value="partial" ${f.payment==='partial'?'selected':''}>Partial</option>
            <option value="refunded" ${f.payment==='refunded'?'selected':''}>Refunded</option>
          </select>
        </div>
        <div>
          <label class="ims__label">Platform</label>
          <select class="ims__select" id="ims-hist-platform" onchange="window.IMS_ORDERS.loadHistory()">
            <option value="">All platforms</option>${platOpts}
          </select>
        </div>
        <div>
          <label class="ims__label">Courier</label>
          <select class="ims__select" id="ims-hist-courier" onchange="window.IMS_ORDERS.loadHistory()">
            <option value="">All couriers</option>${courierOpts}
          </select>
        </div>
        <div>
          <label class="ims__label">Order type</label>
          <select class="ims__select" id="ims-hist-type" onchange="window.IMS_ORDERS.loadHistory()">
            <option value="">All types</option>
            <option value="retail" ${f.type==='retail'?'selected':''}>Retail</option>
            <option value="wholesale" ${f.type==='wholesale'?'selected':''}>Wholesale</option>
          </select>
        </div>
        <div>
          <label class="ims__label">Search (order #, customer, postcode, SKU, ref)</label>
          <input class="ims__input" type="text" id="ims-hist-search" placeholder="Any keyword..." value="${util().sanitize(f.search||'')}">
        </div>
        <div style="display:flex;align-items:flex-end;gap:6px">
          <button class="ims__btn ims__btn--primary" style="flex:1" onclick="window.IMS_ORDERS.loadHistory()"><i class="ti ti-search"></i> Search</button>
          <button class="ims__btn ims__btn--ghost" onclick="window.IMS_ORDERS.clearHistoryFilters()"><i class="ti ti-x"></i></button>
        </div>
      </div>

      <div id="ims-hist-stats" style="display:flex;gap:8px;margin-bottom:10px;flex-wrap:wrap"></div>

      <div class="ims__table-wrap" style="max-height:380px;overflow-y:auto;overflow-x:auto;-webkit-overflow-scrolling:touch">
        <table class="ims__table" style="min-width:900px">
          <thead><tr>
            <th>Order #</th><th>Ref</th><th>Customer</th><th>Platform</th>
            <th>Products</th><th>Qty</th><th>Value</th><th>Date</th>
            <th>Status</th><th>Payment</th><th>Courier</th><th></th>
          </tr></thead>
          <tbody id="ims-hist-tbody">
            <tr><td colspan="12" class="ims__table-empty"><i class="ti ti-loader-2 ims__spin"></i></td></tr>
          </tbody>
        </table>
      </div>
      <div id="ims-hist-pagination" style="display:flex;gap:4px;margin-top:10px;align-items:center;flex-wrap:wrap"></div>
    `, `
      <button class="ims__btn ims__btn--secondary" onclick="window.IMS_ORDERS.exportHistory()" title="One row per order"><i class="ti ti-download"></i> Export orders</button>
      <button class="ims__btn ims__btn--secondary" onclick="window.IMS_ORDERS.exportHistoryItems()" title="One row per item: SKU, product, quantity and price"><i class="ti ti-download"></i> Export items</button>
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Close</button>
    `, 'xl');

    loadHistory();

    setTimeout(() => {
      document.getElementById('ims-hist-search')?.addEventListener('keydown', e => {
        if (e.key === 'Enter') loadHistory();
      });
    }, 200);
  }

  // Opening an order from History replaces History's own modal content
  // entirely (the app only ever has one modal open at a time), so simply
  // closing the order back out previously closed everything, losing
  // whatever date range and filters were set. This captures them first,
  // then swaps the order detail's Close button (only for orders opened
  // this specific way) to return to History with those same filters
  // restored, rather than closing out completely.
  async function viewOrderFromHistory(orderId){
    const savedFilters = {
      from: document.getElementById('ims-hist-from')?.value || '',
      to: document.getElementById('ims-hist-to')?.value || '',
      status: document.getElementById('ims-hist-status')?.value || '',
      payment: document.getElementById('ims-hist-payment')?.value || '',
      platform: document.getElementById('ims-hist-platform')?.value || '',
      courier: document.getElementById('ims-hist-courier')?.value || '',
      type: document.getElementById('ims-hist-type')?.value || '',
      search: document.getElementById('ims-hist-search')?.value || ''
    };
    await openOrder(orderId);
    setTimeout(() => {
      const closeBtn = document.querySelector('#ims-modal-footer button[onclick*="closeModal"]');
      if(closeBtn) closeBtn.onclick = () => showHistory(savedFilters);
    }, 150);
  }

  function clearHistoryFilters() {
    ['ims-hist-status','ims-hist-payment','ims-hist-platform','ims-hist-courier','ims-hist-type','ims-hist-search'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.value = '';
    });
    loadHistory();
  }

      async function loadHistory() {
    const tbody = document.getElementById('ims-hist-tbody');
    if (!tbody) return;
    tbody.innerHTML = `<tr><td colspan="12" class="ims__table-empty"><i class="ti ti-loader-2 ims__spin"></i></td></tr>`;

    const from       = document.getElementById('ims-hist-from')?.value || '';
    const to         = document.getElementById('ims-hist-to')?.value || '';
    const search     = document.getElementById('ims-hist-search')?.value?.trim().toLowerCase() || '';
    const status     = document.getElementById('ims-hist-status')?.value || '';
    const payment    = document.getElementById('ims-hist-payment')?.value || '';
    const platformId = document.getElementById('ims-hist-platform')?.value || '';
    const courierId  = document.getElementById('ims-hist-courier')?.value || '';
    const orderType  = document.getElementById('ims-hist-type')?.value || '';

    const companyId = window.SM.AUTH.company?.id;
    const histOpts = {
      select: 'id,order_number,external_ref,customer_name,order_type,fulfillment_type,status,payment_status,payment_method,total_value,created_at,delivered_at,dispatched_at,postcode,return_reason,refund_amount,platform_id,platforms(name),shipments(tracking_number,courier_id,is_return_collection,couriers(name))',
      order: { col: 'created_at', asc: false },
      noCompanyFilter: true,
      neq: { fulfillment_type: 'dropship' },
      eq: { company_id: companyId }
    };
    if (from)       histOpts.gte = { created_at: from+'T00:00:00' };
    if (to)         histOpts.lte = { created_at: to+'T23:59:59' };
    if (status)     histOpts.eq = { ...histOpts.eq, status: status };
    if (payment)    histOpts.eq = { ...histOpts.eq, payment_status: payment };
    if (platformId) histOpts.eq = { ...histOpts.eq, platform_id: platformId };
    if (orderType)  histOpts.eq = { ...histOpts.eq, order_type: orderType };

    const { data, error } = await _histReadAll('orders', histOpts);
    if (error) {
      tbody.innerHTML = `<tr><td colspan="12" class="ims__table-empty">Error: ${error.message}</td></tr>`;
      return;
    }

    // order_items has no company_id column — fetch separately with raw client
    const histOrderIds = (data||[]).map(o => o.id);
    let histItemsMap = {};
    if (histOrderIds.length) {
      const histItems = await _histItemsFor(histOrderIds);
      (histItems||[]).forEach(i => {
        if (!histItemsMap[i.order_id]) histItemsMap[i.order_id] = [];
        histItemsMap[i.order_id].push(i);
      });
    }
    let orders = (data||[]).map(o => ({
      ...o,
      items: histItemsMap[o.id] || [],
      courier: (o.shipments||[]).find(x=>!x.is_return_collection)?.couriers?.name || '',
      tracking: (o.shipments||[]).find(x=>!x.is_return_collection)?.tracking_number || '',
      _courierId: (o.shipments||[]).find(x=>!x.is_return_collection)?.courier_id || ''
    }));

    // Client-side filters
    if (courierId) orders = orders.filter(o => o._courierId === courierId);
    if (search) {
      orders = orders.filter(o => {
        const skus = o.items.map(i=>i.sku).join(' ').toLowerCase();
        const prods = o.items.map(i=>i.product_name).join(' ').toLowerCase();
        return (
          o.order_number?.toLowerCase().includes(search) ||
          o.customer_name?.toLowerCase().includes(search) ||
          o.postcode?.toLowerCase().includes(search) ||
          o.external_ref?.toLowerCase().includes(search) ||
          o.tracking?.toLowerCase().includes(search) ||
          skus.includes(search) ||
          prods.includes(search)
        );
      });
    }

    _historyOrders = orders;
    _historyPage = 1;

    // Stats
    const statsEl = document.getElementById('ims-hist-stats');
    if (statsEl) {
      const totalVal  = orders.reduce((s,o)=>s+(o.total_value||0),0);
      const delivered = orders.filter(o=>o.status==='delivered').length;
      const cancelled = orders.filter(o=>o.status==='cancelled').length;
      const refunded  = orders.filter(o=>o.status==='refunded').length;
      const unpaid    = orders.filter(o=>o.payment_status==='unpaid').length;
      statsEl.innerHTML = [
        {l:'Total orders',  v:orders.length,                   t:''},
        {l:'Revenue',       v:util().formatCurrency(totalVal), t:'success'},
        {l:'Delivered',     v:delivered,                       t:'success'},
        {l:'Cancelled',     v:cancelled,                       t:'neutral'},
        {l:'Refunded',      v:refunded,                        t:'danger'},
        {l:'Unpaid',        v:unpaid,                          t:'danger'},
      ].map(s=>`<div class="ims__stat" style="padding:6px 12px;min-width:90px;border:0.5px solid var(--ims-border);border-radius:var(--ims-radius)">
        <div class="ims__stat-label" style="font-size:10px">${s.l}</div>
        <div class="ims__stat-value${s.t?' ims__stat-value--'+s.t:''}" style="font-size:14px">${s.v}</div>
      </div>`).join('');
    }

    renderHistoryTable();
  }

  function renderHistoryTable() {
    const tbody = document.getElementById('ims-hist-tbody');
    const pagEl = document.getElementById('ims-hist-pagination');
    if (!tbody) return;
    const total = _historyOrders.length;
    if (!total) {
      tbody.innerHTML = '<tr><td colspan="12" class="ims__table-empty">No orders found — try adjusting filters</td></tr>';
      if (pagEl) pagEl.innerHTML = '';
      return;
    }
    const start = (_historyPage-1)*PAGE_SIZE;
    const page  = _historyOrders.slice(start, start+PAGE_SIZE);
    tbody.innerHTML = page.map(o=>{
      const products = o.items.map(i=>{
        const name = util().sanitize(i.product_name||'');
        const sku  = util().sanitize(i.sku||'');
        return name ? (sku ? `${name} (${sku})` : name) : sku;
      }).filter(Boolean).join(', ') || '—';
      const qty  = o.items.reduce((s,i)=>s+(i.quantity||0),0) || '—';
      return `<tr>
        <td><span class="ims__link" onclick="window.IMS_ORDERS.viewOrderFromHistory('${util().jsArg(o.id)}')">${util().sanitize(o.order_number)}</span></td>
        <td style="font-size:11px">${util().sanitize(o.external_ref||'—')}</td>
        <td>${util().sanitize(o.customer_name||'—')}</td>
        <td style="font-size:11px">${util().sanitize(o.platforms?.name||'—')}</td>
        <td style="font-size:11px">${products}</td>
        <td style="text-align:right">${qty}</td>
        <td>${util().formatCurrency(o.total_value)}</td>
        <td style="font-size:11px">${util().formatDate(o.created_at)}</td>
        <td>${util().pill(o.status)}</td>
        <td>${o.payment_status?util().pill(o.payment_status):'—'}</td>
        <td style="font-size:11px">${util().sanitize(o.courier||'—')}</td>
        <td>
          <div class="ims__btn-group">
            <button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon" onclick="window.IMS_ORDERS.viewOrderFromHistory('${util().jsArg(o.id)}')"><i class="ti ti-eye"></i></button>
            <button class="ims__btn ims__btn--ghost ims__btn--sm ims__btn--icon" onclick="window.IMS_ORDERS.showActivityLog('${util().jsArg(o.id)}','${util().jsArg(o.order_number)}')" title="Activity log"><i class="ti ti-history"></i></button>
          </div>
        </td>
      </tr>`;
    }).join('');

    if (pagEl) {
      const pages = Math.ceil(total/PAGE_SIZE);
      pagEl.innerHTML = pages<=1 ? '' :
        `<span class="ims__page-info">Showing ${Math.min(start+1,total)}–${Math.min(_historyPage*PAGE_SIZE,total)} of ${total}</span>`+
        Array.from({length:pages},(_,i)=>`<button class="ims__btn ims__btn--sm ${i+1===_historyPage?'ims__btn--primary':'ims__btn--secondary'}" onclick="window.IMS_ORDERS.goHistoryPage(${i+1})">${i+1}</button>`).join('');
    }
  }

  function goHistoryPage(p) { _historyPage=p; renderHistoryTable(); }
  function showActive() { load(); }

  async function showActivityLog(orderId, orderNumber) {
    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    const { data: logs } = await client.from('order_activity_log')
      .select('*')
      .eq('order_id', orderId)
      .order('created_at', {ascending: true});

    const rows = (logs||[]).length ? (logs||[]).map(l=>`
      <tr>
        <td style="font-size:11px;white-space:nowrap">${util().formatDate(l.created_at)}</td>
        <td style="font-size:11px">${util().sanitize(l.user_name||'System')}</td>
        <td style="font-weight:500">${util().sanitize(l.action||'—')}</td>
        <td style="font-size:11px;color:var(--ims-text-muted)">${l.old_value?util().sanitize(l.old_value):'—'}</td>
        <td style="font-size:11px">${l.new_value?util().sanitize(l.new_value):'—'}</td>
        <td style="font-size:11px;color:var(--ims-text-muted)">${l.notes?util().sanitize(l.notes):'—'}</td>
      </tr>`).join('')
    : '<tr><td colspan="6" class="ims__table-empty">No activity recorded for this order</td></tr>';

    ui().openModal(`Activity log: ${orderNumber}`, `
      <div class="ims__table-wrap" style="max-height:420px;overflow-y:auto">
        <table class="ims__table">
          <thead><tr><th>Time</th><th>User</th><th>Action</th><th>From</th><th>To</th><th>Notes</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    `, `<button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Close</button>`, 'lg');
  }

  // ─── ORDER HISTORY: reading every row ─────────────────────────────
  // The database returns at most 1,000 rows per request, so the history
  // reads orders in pages, and their lines in batches of 150 orders, until
  // everything is in (lines were near that limit already).
  async function _histReadAll(table, opts) {
    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY), out = [];
    for (let start = 0; start < 500000; start += 1000) {
      let q = client.from(table).select(opts.select || '*');
      if (!opts.noCompanyFilter && window.SM.AUTH.company?.id) q = q.eq('company_id', window.SM.AUTH.company.id);
      Object.entries(opts.eq || {}).forEach(([k, v]) => { q = q.eq(k, v); });
      Object.entries(opts.neq || {}).forEach(([k, v]) => { q = q.neq(k, v); });
      Object.entries(opts.gte || {}).forEach(([k, v]) => { q = q.gte(k, v); });
      Object.entries(opts.lte || {}).forEach(([k, v]) => { q = q.lte(k, v); });
      if (opts.order) q = q.order(opts.order.col, { ascending: opts.order.asc !== false });
      q = q.order('id', { ascending: true });   // a fixed order, so pages never overlap or skip
      const { data, error } = await q.range(start, start + 999);
      if (error) return { data: out.length ? out : null, error };
      out.push(...(data || []));
      if (!data || data.length < 1000) break;
    }
    return { data: out, error: null };
  }
  async function _histItemsFor(orderIds) {
    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY), out = [];
    for (let i = 0; i < orderIds.length; i += 150) {
      const part = orderIds.slice(i, i + 150);
      for (let start = 0; ; start += 1000) {
        const { data, error } = await client.from('order_items').select('id,order_id,product_name,sku,quantity,unit_price,total_price')
          .in('order_id', part).order('id', { ascending: true }).range(start, start + 999);
        if (error) return out;
        out.push(...(data || []));
        if (!data || data.length < 1000) break;
      }
    }
    return out;
  }

  // One row per item: what was ordered, how many, at what price.
  function exportHistoryItems() {
    if (!_historyOrders.length) { ui().toast('No orders to export','warning'); return; }
    const from = document.getElementById('ims-hist-from')?.value||'';
    const to   = document.getElementById('ims-hist-to')?.value||'';
    const rows = [];
    _historyOrders.forEach(o => {
      const base = [o.order_number, o.external_ref||'', util().formatDate(o.created_at), o.customer_name||'', o.postcode||'', o.platforms?.name||'', o.order_type||'', o.status||'', o.payment_status||''];
      if (!o.items.length) { rows.push([...base, '', '', 0, '', '']); return; }
      o.items.forEach(i => rows.push([...base, i.sku||'', i.product_name||'', i.quantity||0,
        i.unit_price != null ? Number(i.unit_price).toFixed(2) : '', i.total_price != null ? Number(i.total_price).toFixed(2) : ((Number(i.unit_price)||0)*(Number(i.quantity)||0)).toFixed(2)]));
    });
    util().exportCSV(['Order #','External ref','Date','Customer','Postcode','Platform','Order type','Status','Payment','SKU','Product','Qty','Unit price','Line total'],
      rows, `order_items_${from}_to_${to}`);
  }

  async function exportHistory() {
    if (!_historyOrders.length) { ui().toast('No orders to export','warning'); return; }
    const from = document.getElementById('ims-hist-from')?.value||'';
    const to   = document.getElementById('ims-hist-to')?.value||'';
    util().exportCSV(
      ['Order #','External ref','Customer','Postcode','Platform','Order type','SKU(s)','Product(s)','Qty','Value','Date','Status','Payment','Payment method','Courier','Tracking','Return reason'],
      _historyOrders.map(o=>[
        o.order_number,
        o.external_ref||'',
        o.customer_name||'',
        o.postcode||'',
        o.platforms?.name||'',
        o.order_type||'',
        o.items.map(i=>i.sku||'').join(';'),
        o.items.map(i=>i.product_name||'').join(';'),
        o.items.reduce((s,i)=>s+(i.quantity||0),0),
        (o.total_value||0).toFixed(2),
        util().formatDate(o.created_at),
        o.status||'',
        o.payment_status||'',
        o.payment_method||'',
        o.courier||'',
        o.tracking||'',
        o.return_reason||''
      ]),
      `order_history_${from}_to_${to}`
    );
  }


  // ─── ACTIVITY LOG ────────────────────────────────────────────────
  async function logActivity(orderId, action, oldValue='', newValue='', notes='') {
    try {
      const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
      const user = window.SM.AUTH.user;
      await client.from('order_activity_log').insert({
        order_id:   orderId,
        user_id:    user?.id || null,
        user_name:  user?.name || user?.email || 'System',
        action,
        old_value:  oldValue ? String(oldValue) : null,
        new_value:  newValue ? String(newValue) : null,
        notes:      notes || null,
        created_at: new Date().toISOString()
      });
    } catch(e) { console.warn('[Activity log]', e.message); }
  }

  // ─── LOAD ORDERS LIST ────────────────────────────────────────────
  async function load() {
    const tbody = document.getElementById('ims-orders-tbody');
    if (!tbody) return;
    tbody.innerHTML = `<tr><td colspan="11" class="ims__table-empty"><i class="ti ti-loader-2 ims__spin"></i></td></tr>`;
    _selectedOrders.clear();

    const search   = document.getElementById('ims-orders-search')?.value?.trim() || '';
    const status   = document.getElementById('ims-orders-status')?.value || '';
    const platform = document.getElementById('ims-orders-platform')?.value || '';
    const type     = document.getElementById('ims-orders-type')?.value || '';

    const opts = {
      select: 'id,order_number,customer_name,order_type,fulfillment_type,status,total_value,created_at,dispatched_at,postcode,dropship_customer_id,platforms(id,name)',
      order: { col: 'created_at', asc: false }
    };
    opts.neq = { fulfillment_type: 'dropship' };
    if (status && status !== 'delayed') {
      opts.eq = { status };
    } else if (!status) {
      // Default view: only orders still fully owned by Order Management.
      // "Packed" now hands off to Logistics immediately, so it's excluded
      // here too — findable via the "Packed" filter, Logistics, or History.
      opts.in = { status: ['confirmed', 'picking'] };
    }
    if (platform) opts.eq = { ...opts.eq, platform_id: platform };
    if (type) opts.eq = { ...opts.eq, order_type: type };

    let { data, error } = await db().select('orders', opts);
    if (error) { ui().toast('Failed to load orders', 'error'); return; }

    const orderIds = (data||[]).map(o => o.id);
    let itemsMap = {};
    if (orderIds.length) {
      const rawClient = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
      const { data: items } = await rawClient.from('order_items')
        .select('order_id,product_name,sku,quantity')
        .in('order_id', orderIds);
      (items||[]).forEach(i => {
        if (!itemsMap[i.order_id]) itemsMap[i.order_id] = [];
        itemsMap[i.order_id].push(i);
      });
    }

    let orders = (data||[]).map(o => ({ ...o, items: itemsMap[o.id] || [] }));

    if (search) {
      const s = search.toLowerCase();
      orders = orders.filter(o => {
        const prods = o.items.map(i => i.product_name||'').join(' ').toLowerCase();
        const skus  = o.items.map(i => i.sku||'').join(' ').toLowerCase();
        return (
          o.order_number?.toLowerCase().includes(s) ||
          o.customer_name?.toLowerCase().includes(s) ||
          o.postcode?.toLowerCase().includes(s) ||
          prods.includes(s) ||
          skus.includes(s)
        );
      });
    }

    orders = orders.map(o => ({ ...o, _delayed: util().isDelayed(o) }));
    if (status === 'delayed') orders = orders.filter(o => o._delayed);

    _orders = orders;
    _currentPage = 1;
    renderTable();
    loadPlatformFilter();
    ui().updateBadge('ims-badge-orders',
      orders.filter(o => ['confirmed','picking','packed'].includes(o.status) || o._delayed).length
    );
  }

  // ─── UNPAID EXPIRY CHECK ─────────────────────────────────────────
  async function checkUnpaidExpiry() {
    const now = new Date();
    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    const { data: expiring } = await client.from('orders')
      .select('id,order_number,customer_name,total_value,unpaid_expires_at,created_at')
      .eq('payment_status','unpaid')
      .eq('fulfillment_type','retail')
      .not('status','in','("cancelled","refunded","delivered")')
      .lte('unpaid_expires_at', now.toISOString())
      .order('unpaid_expires_at');

    if (!expiring?.length) return;

    // Show action modal for expired unpaid orders
    const rows = expiring.map(o => `
      <tr>
        <td><strong>${util().sanitize(o.order_number)}</strong></td>
        <td>${util().sanitize(o.customer_name||'—')}</td>
        <td>${util().formatCurrency(o.total_value)}</td>
        <td style="color:var(--ims-danger)">${util().formatDate(o.unpaid_expires_at)}</td>
        <td>
          <div class="ims__btn-group">
            <button class="ims__btn ims__btn--success ims__btn--sm" onclick="window.IMS_ORDERS.convertUnpaid('${util().jsArg(o.id)}','paid')">Paid</button>
            <button class="ims__btn ims__btn--accent ims__btn--sm" onclick="window.IMS_ORDERS.convertUnpaid('${util().jsArg(o.id)}','cod')">COD</button>
            <button class="ims__btn ims__btn--danger ims__btn--sm" onclick="window.IMS_ORDERS.cancelUnpaid('${util().jsArg(o.id)}','${util().jsArg(o.order_number)}')">Cancel</button>
          </div>
        </td>
      </tr>`).join('');

    ui().openModal(`⚠ ${expiring.length} unpaid order(s) have expired`, `
      <div class="ims__alert ims__alert--danger" style="margin-bottom:14px">
        <i class="ti ti-clock"></i>
        <span>The following orders have exceeded the 7-day payment window. Please take action on each order — convert to Paid, COD, or cancel.</span>
      </div>
      <div class="ims__table-wrap">
        <table class="ims__table">
          <thead><tr><th>Order #</th><th>Customer</th><th>Value</th><th>Expired</th><th>Action</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    `, `<button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Dismiss</button>`, 'lg');
  }

  async function convertUnpaid(orderId, newStatus) {
    const expiresAt = newStatus === 'unpaid' ? new Date(Date.now() + 7*24*60*60*1000).toISOString() : null;
    await db().update('orders', orderId, {
      payment_status:    newStatus,
      unpaid_expires_at: expiresAt
    });
    await logActivity(orderId, 'Payment status converted', 'unpaid', newStatus, 'Converted from expired unpaid');
    ui().toast(`Order converted to ${newStatus}`, 'success');
    // Remove row from modal
    const modal = document.getElementById('ims-modal-body');
    if (modal) {
      const rows = modal.querySelectorAll('tbody tr');
      rows.forEach(row => {
        if (row.innerHTML.includes(orderId)) row.remove();
      });
      const remaining = modal.querySelectorAll('tbody tr').length;
      if (!remaining) ui().closeModal();
    }
    load();
  }

  async function cancelUnpaid(orderId, orderNumber) {
    ui().confirm(`Cancel unpaid order <strong>${orderNumber}</strong>?<br><br>Stock reservations will be released.`, async () => {
      // Release stock reservations
      const { data: _cancelOrd } = await db().select('orders', { select: 'warehouse_id,order_items(product_id,quantity)', eq: { id: orderId }, maybeSingle: true, noCompanyFilter: true });
      const items = _cancelOrd?.order_items || [];
      const whId = _cancelOrd?.warehouse_id;
      // Release one line at a time. Releasing every line at once meant two
      // lines of the same product both read the same starting figure, so
      // only one release was applied and stock stayed wrongly reserved.
      for (const item of (items||[])) {
        if (!item.product_id) continue;
        const { data: sl } = whId
          ? await db().select('stock_levels', { eq: { product_id: item.product_id, warehouse_id: whId }, maybeSingle: true, noCompanyFilter: true })
          : await db().select('stock_levels', { eq: { product_id: item.product_id }, limit: 1, noCompanyFilter: true }).then(r => ({ data: r.data?.[0] }));
        if (sl) await db().update('stock_levels', sl.id, { reserved_qty: Math.max(0,(sl.reserved_qty||0)-(item.quantity||0)) });
      }
      const _cancelClient2 = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
      await _cancelClient2.from('logistics_jobs').delete().eq('order_id', orderId);

      await db().update('orders', orderId, { status: 'cancelled', payment_status: 'unpaid', unpaid_expires_at: null });
      await logActivity(orderId, 'Order cancelled', 'confirmed', 'cancelled', 'Cancelled — unpaid order expired');
      ui().toast('Order cancelled — stock released', 'success');
      const modal = document.getElementById('ims-modal-body');
      if (modal) {
        const rows = modal.querySelectorAll('tbody tr');
        rows.forEach(row => { if (row.innerHTML.includes(orderId)) row.remove(); });
        if (!modal.querySelectorAll('tbody tr').length) ui().closeModal();
      }
      load();
    }, 'Cancel order', false);
  }

  function renderTable() {
    const tbody = document.getElementById('ims-orders-tbody');
    if (!tbody) return;
    const total = _orders.length;
    if (!total) {
      tbody.innerHTML = '<tr><td colspan="11" class="ims__table-empty">No orders found</td></tr>';
      renderPagination(0);
      updateBulkBar();
      return;
    }
    const start = (_currentPage - 1) * PAGE_SIZE;
    const page  = _orders.slice(start, start + PAGE_SIZE);

    tbody.innerHTML = page.map(o => {
      const status = o._delayed ? 'delayed' : o.status;
      const daysToExpiry = o.payment_status==='unpaid'&&o.unpaid_expires_at
        ? Math.ceil((new Date(o.unpaid_expires_at)-new Date())/(1000*60*60*24)) : null;
      const expiryBadge = daysToExpiry!==null
        ? daysToExpiry<=0
          ? `<span class="ims__pill ims__pill--danger" style="font-size:10px">Expired</span>`
          : daysToExpiry<=1
            ? `<span class="ims__pill ims__pill--danger" style="font-size:10px">Expires today</span>`
            : daysToExpiry<=3
              ? `<span class="ims__pill ims__pill--warning" style="font-size:10px">Exp. ${daysToExpiry}d</span>`
              : ''
        : '';
      const typePill = o.order_type === 'wholesale'
        ? `<span class="ims__pill ims__pill--info">Wholesale</span>`
        : `<span class="ims__pill ims__pill--neutral">Retail</span>`;
      const products = (o.items||[]).map(i => {
        const name = util().sanitize(i.product_name||'');
        const sku  = util().sanitize(i.sku||'');
        return name ? (sku ? `${name} (${sku})` : name) : sku;
      }).filter(Boolean).join(', ') || '—';
      const qty = (o.items||[]).reduce((s, i) => s + (i.quantity||0), 0) || '—';
      return `
        <tr class="${o._delayed ? 'ims__row--delayed' : ''}">
          <td><input type="checkbox" class="ims-order-cb" data-id="${o.id}" ${_selectedOrders.has(o.id)?'checked':''} onchange="window.IMS_ORDERS.toggleSelectOrder('${util().jsArg(o.id)}',this.checked)"></td>
          <td><span class="ims__link" onclick="window.IMS_ORDERS.openOrder('${util().jsArg(o.id)}')">${util().sanitize(o.order_number)}</span></td>
          <td>${util().sanitize(o.customer_name||'—')}</td>
          <td>${util().sanitize(o.platforms?.name||'—')}</td>
          <td>${typePill}</td>
          <td style="font-size:11px;max-width:220px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title="${products}">${products}</td>
          <td style="text-align:right">${qty}</td>
          <td>${util().formatCurrency(o.total_value)}${o.has_price_override?` <span title="Contains price override" style="color:var(--ims-warning);font-size:11px"><i class="ti ti-alert-triangle"></i></span>`:''}</td>
          <td>${util().formatDate(o.created_at)}</td>
          <td>${util().pill(status)}${o.payment_status&&o.order_type==='retail'?` ${util().pill(o.payment_status)}`:''} ${expiryBadge}</td>
          <td>
            <div class="ims__btn-group">
              <button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon" onclick="window.IMS_ORDERS.openOrder('${util().jsArg(o.id)}')" title="View"><i class="ti ti-eye"></i></button>
              ${o._delayed ? `<button class="ims__btn ims__btn--danger ims__btn--sm ims__btn--icon" onclick="window.SM.UI.showPanel('logistics')" title="Chase courier in Logistics → Tracking"><i class="ti ti-truck-delivery"></i></button>` : ''}
              </div>
          </td>
        </tr>`;
    }).join('');
    renderPagination(total);
    updateBulkBar();
    const allCb = document.getElementById('ims-orders-select-all');
    if (allCb) {
      const pageIds = page.map(o => o.id);
      allCb.checked = pageIds.length > 0 && pageIds.every(id => _selectedOrders.has(id));
    }
  }

  function renderPagination(total) {
    const el = document.getElementById('ims-orders-pagination');
    if (!el) return;
    const pages = Math.ceil(total / PAGE_SIZE);
    if (pages <= 1) { el.innerHTML = ''; return; }
    el.innerHTML = `<span class="ims__page-info">Showing ${Math.min((_currentPage-1)*PAGE_SIZE+1,total)}–${Math.min(_currentPage*PAGE_SIZE,total)} of ${total}</span>` +
      Array.from({ length: pages }, (_, i) => `
        <button class="ims__btn ims__btn--sm ${i+1===_currentPage?'ims__btn--primary':'ims__btn--secondary'}"
          onclick="window.IMS_ORDERS.goPage(${i+1})">${i+1}</button>`).join('');
  }

  // ─── BULK SELECT & STATUS UPDATE ──────────────────────────────────
  // Selection tracks order IDs, so it survives page changes within the
  // same filtered list. "Select all" only selects the current page —
  // consistent with the 25-row page size, avoids surprise mass-updates
  // across hundreds of unseen rows. Status choices are deliberately
  // limited to the same forward transitions the single-order dropdown
  // allows (confirmed/picking → picking/packed); anything else has its
  // own dedicated flow with side effects this must not bypass.
  function toggleSelectOrder(id, checked) {
    if (checked) _selectedOrders.add(id); else _selectedOrders.delete(id);
    updateBulkBar();
    const allCb = document.getElementById('ims-orders-select-all');
    if (allCb) {
      const pageIds = _orders.slice((_currentPage-1)*PAGE_SIZE, _currentPage*PAGE_SIZE).map(o=>o.id);
      allCb.checked = pageIds.length>0 && pageIds.every(pid=>_selectedOrders.has(pid));
    }
  }

  function toggleSelectAll(checked) {
    const pageIds = _orders.slice((_currentPage-1)*PAGE_SIZE, _currentPage*PAGE_SIZE).map(o=>o.id);
    pageIds.forEach(id => { if (checked) _selectedOrders.add(id); else _selectedOrders.delete(id); });
    renderTable();
  }

  function clearSelection() {
    _selectedOrders.clear();
    renderTable();
  }

  function updateBulkBar() {
    const bar = document.getElementById('ims-orders-bulk-bar');
    if (!bar) return;
    const count = _selectedOrders.size;
    if (!count) { bar.style.display = 'none'; bar.innerHTML = ''; return; }
    bar.style.display = '';
    bar.innerHTML = `
      <div class="ims__alert ims__alert--info" style="margin-bottom:14px;justify-content:space-between;flex-wrap:wrap">
        <span><i class="ti ti-checkbox"></i> <strong>${count}</strong> order${count===1?'':'s'} selected</span>
        <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap">
          <select class="ims__select" id="ims-bulk-status-sel" style="width:auto;font-size:12px">
            <option value="">Set status to...</option>
            <option value="picking">Picking</option>
            <option value="packed">Packed</option>
          </select>
          <button class="ims__btn ims__btn--primary ims__btn--sm" onclick="window.IMS_ORDERS.applyBulkStatus()">Apply</button>
          <button class="ims__btn ims__btn--ghost ims__btn--sm" onclick="window.IMS_ORDERS.clearSelection()">Clear selection</button>
        </div>
      </div>`;
  }

  async function applyBulkStatus() {
    const sel = document.getElementById('ims-bulk-status-sel');
    const newStatus = sel?.value;
    if (!newStatus) { ui().toast('Please choose a status to apply', 'error'); return; }
    const ids = [..._selectedOrders];
    if (!ids.length) return;

    const bar = document.getElementById('ims-orders-bulk-bar');
    const applyBtn = bar?.querySelector('button.ims__btn--primary');
    const clearBtn = bar?.querySelector('button.ims__btn--ghost');
    if (sel) sel.disabled = true;
    if (applyBtn) { applyBtn.disabled = true; applyBtn.innerHTML = '<i class="ti ti-loader-2 ims__spin"></i> Applying...'; }
    if (clearBtn) clearBtn.disabled = true;

    // Inject a progress bar so a large selection doesn't look frozen —
    // each order requires a status lookup, an update, and an activity
    // log entry (plus a logistics job insert when moving to Packed).
    const total = ids.length;
    if (bar) {
      bar.insertAdjacentHTML('beforeend', `
        <div id="ims-bulk-progress" style="margin-top:8px">
          <div style="display:flex;justify-content:space-between;font-size:11px;color:var(--ims-text-muted);margin-bottom:4px">
            <span id="ims-bulk-progress-label">Starting...</span>
            <span id="ims-bulk-progress-pct">0%</span>
          </div>
          <div style="background:var(--ims-border);border-radius:999px;height:6px;overflow:hidden">
            <div id="ims-bulk-progress-bar" style="height:6px;width:0%;background:var(--ims-accent);border-radius:999px;transition:width .2s"></div>
          </div>
        </div>`);
    }
    const setBulkProgress = (done, label) => {
      const pct = total > 0 ? Math.round((done/total)*100) : 0;
      const barEl = document.getElementById('ims-bulk-progress-bar');
      const pctEl = document.getElementById('ims-bulk-progress-pct');
      const labelEl = document.getElementById('ims-bulk-progress-label');
      if (barEl) barEl.style.width = pct+'%';
      if (pctEl) pctEl.textContent = pct+'%';
      if (labelEl) labelEl.textContent = label;
    };

    let updated = 0, skipped = 0, processed = 0;
    for (const id of ids) {
      processed++;
      setBulkProgress(processed - 1, `Updating order ${processed} of ${total}`);
      const { data: current } = await db().select('orders', { select: 'status', eq: { id }, maybeSingle: true, noCompanyFilter: true });
      if (!['confirmed','picking'].includes(current?.status)) { skipped++; setBulkProgress(processed, `Updated ${processed} of ${total}`); continue; }
      const { error } = await db().update('orders', id, { status: newStatus });
      if (error) { skipped++; setBulkProgress(processed, `Updated ${processed} of ${total}`); continue; }
      await logActivity(id, 'Status updated', current?.status||'', newStatus, 'Bulk status update');
      if (newStatus === 'packed') {
        await db().insert('logistics_jobs', { order_id: id, job_type: 'delivery', status: 'unassigned' });
      }
      updated++;
      setBulkProgress(processed, `Updated ${processed} of ${total}`);
    }

    setBulkProgress(total, 'All done!');
    await new Promise(r => setTimeout(r, 300));

    _selectedOrders.clear();
    ui().toast(`${updated} order(s) updated${skipped?`, ${skipped} skipped (not eligible for this transition)`:''}`, skipped ? 'warning' : 'success', 5000);
    load();
  }

  async function loadPlatformFilter() {
    if (_platforms.length) return;
    const companyId = window.SM.AUTH.company?.id;
    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    const { data } = await client.from('platforms')
      .select('id,name,order_type')
      .eq('company_id', companyId)
      .eq('is_active', true)
      .order('sort_order');
    _platforms = data || [];
    const sel = document.getElementById('ims-orders-platform');
    if (!sel) return;
    _platforms.forEach(p => {
      if (!sel.querySelector(`option[value="${p.id}"]`)) {
        const opt = document.createElement('option');
        opt.value = p.id; opt.textContent = p.name;
        sel.appendChild(opt);
      }
    });
  }

  // ─── NEW ORDER FORM ──────────────────────────────────────────────
  let _batchOrders = [];   // orders saved in current batch session
  let _batchId = null;     // current batch ID

  async function showNewOrder(batchMode=false) {
    _amend = null;   // a New order is never an amendment (amendOrder sets it after opening)
    _orderItems = [];
    _activeDeal = null;
    _matchedCustomer = null;
    _pickedCustomerId = null;
    _customerDefaultAddress = '';
    _customerDefaultPostcode = '';
    const companyId = window.SM.AUTH.company?.id;
    const company   = window.SM.AUTH.company;

    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    const { data: platforms } = await client.from('platforms')
      .select('id,name,order_type')
      .eq('company_id', companyId)
      .eq('is_active', true)
      .order('sort_order');

    const cats = company?.business_category || [];
    const filtered = (platforms || []).filter(p => {
      if (!p.categories || p.categories.includes('all')) return true;
      if (!cats.length) return true;
      return p.categories.some(c => cats.includes(c));
    });

    const platformOpts = filtered.map(p =>
      `<option value="${p.id}" data-type="${p.order_type}">${util().sanitize(p.name)}</option>`
    ).join('');

    const { data: warehouses } = await db().select('warehouses', { eq: { is_active: true }, order: { col: 'is_default', asc: false } });
    const defaultWarehouse = (warehouses||[]).find(w => w.is_default) || (warehouses||[])[0] || null;
    const warehouseOpts = (warehouses||[]).map(w =>
      `<option value="${w.id}" ${defaultWarehouse&&w.id===defaultWarehouse.id?'selected':''}>${util().sanitize(w.name)}${w.is_default?' (default)':''}</option>`
    ).join('');
    ui().openModal('New order', `
      <div class="ims__form-grid">
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Delivery postcode</label>
          <div style="position:relative">
            <input class="ims__input" type="text" id="ims-no-postcode" placeholder="e.g. SW1A 1AA" autocomplete="off">
            <div id="ims-no-postcode-drop" class="ims__ac-dropdown"></div>
          </div>
          <span class="ims__input-hint" id="ims-no-postcode-hint"></span>
        </div>
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Platform</label>
          <select class="ims__select" id="ims-no-platform" onchange="window.IMS_ORDERS.onPlatformChange()">
            <option value="">Select platform</option>${platformOpts}
          </select>
        </div>
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Customer name</label>
          <input class="ims__input" type="text" id="ims-no-custname" placeholder="Customer or business name">
          <span class="ims__input-hint" id="ims-no-custname-hint"></span>
        </div>
        <div class="ims__form-group ims__form-group--full" id="ims-no-quickadd" style="display:none;padding:14px;background:var(--ims-surface2);border:1px solid var(--ims-border);border-radius:var(--ims-radius)">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px">
            <strong style="font-size:13px">New customer</strong>
            <span class="ims__link" style="font-size:12px" onclick="window.IMS_ORDERS.cancelQuickAddCustomer()">Cancel — enter as one-off instead</span>
          </div>
          <div class="ims__form-grid">
            <div class="ims__form-group">
              <label class="ims__label ims__label--req">Business / customer name</label>
              <input class="ims__input" type="text" id="ims-qa-name">
            </div>
            <div class="ims__form-group">
              <label class="ims__label">Contact name</label>
              <input class="ims__input" type="text" id="ims-qa-contact">
            </div>
            <div class="ims__form-group">
              <label class="ims__label">Phone</label>
              <input class="ims__input" type="tel" id="ims-qa-phone">
            </div>
            <div class="ims__form-group">
              <label class="ims__label">Email</label>
              <input class="ims__input" type="email" id="ims-qa-email">
            </div>
            <div class="ims__form-group">
              <label class="ims__label">Postcode</label>
              <input class="ims__input" type="text" id="ims-qa-postcode">
            </div>
            <div class="ims__form-group ims__form-group--full">
              <label class="ims__label">Address</label>
              <input class="ims__input" type="text" id="ims-qa-address">
            </div>
          </div>
          <button class="ims__btn ims__btn--primary ims__btn--sm" style="margin-top:6px" onclick="window.IMS_ORDERS.saveQuickAddCustomer()"><i class="ti ti-user-plus"></i> Save customer &amp; continue</button>
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Order type</label>
          <select class="ims__select" id="ims-no-type-display" disabled>
            <option value="retail">Retail</option>
            <option value="wholesale">Wholesale</option>
          </select>
          <span class="ims__input-hint" id="ims-no-type-hint">Auto-set from platform</span>
        </div>
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Order date</label>
          <input class="ims__input" type="date" id="ims-no-orderdate" value="${new Date().toISOString().split('T')[0]}" max="${new Date().toISOString().split('T')[0]}">
          <span class="ims__input-hint">Defaults to today — change if logging an order taken earlier</span>
        </div>
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Fulfilling warehouse</label>
          <select class="ims__select" id="ims-no-warehouse" onchange="window.IMS_ORDERS.onWarehouseChange()">
            <option value="">Select warehouse</option>${warehouseOpts}
          </select>
          <span class="ims__input-hint">Stock availability below is checked against this warehouse</span>
        </div>
        <div class="ims__form-group ims__form-group--full">
          <div style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:6px">
            <label class="ims__label ims__label--req" style="margin:0">Delivery address</label>
            <label id="ims-no-altdelivery-wrap" style="display:none;align-items:center;gap:7px;font-size:12px;font-weight:500;color:var(--ims-text-sec);cursor:pointer">
              <input type="checkbox" id="ims-no-altdelivery" onchange="window.IMS_ORDERS.toggleAltDelivery()">
              Deliver to a different address
            </label>
          </div>
          <textarea class="ims__textarea" id="ims-no-address" rows="2" placeholder="Full delivery address"></textarea>
          <span class="ims__input-hint" id="ims-no-altdelivery-hint"></span>
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Phone</label>
          <input class="ims__input" type="tel" id="ims-no-phone">
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Email</label>
          <input class="ims__input" type="email" id="ims-no-email">
        </div>
        <div class="ims__form-group">
          <label class="ims__label">External ref (platform order #)</label>
          <input class="ims__input" type="text" id="ims-no-extref" placeholder="e.g. Amazon order number">
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Notes</label>
          <input class="ims__input" type="text" id="ims-no-notes" placeholder="Special instructions">
        </div>
        <div class="ims__form-group ims__form-group--full" id="ims-override-reason-group" style="display:none">
          <label class="ims__label" style="color:var(--ims-warning)"><i class="ti ti-alert-triangle"></i> Price override reason (required)</label>
          <input class="ims__input" type="text" id="ims-no-override-reason" placeholder="e.g. Customer negotiated, promotional price, clearance...">
          <span class="ims__input-hint">One or more item prices differ from the standard catalogue price</span>
        </div>
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label ims__label--req">Payment status</label>
          <div style="display:flex;gap:8px;flex-wrap:wrap" id="ims-no-payment-btns">
            <label style="display:flex;align-items:center;gap:6px;cursor:pointer;padding:8px 14px;border:1.5px solid var(--ims-border);border-radius:var(--ims-radius);flex:1;min-width:120px" id="ims-pay-btn-paid">
              <input type="radio" name="ims-no-payment" value="paid" style="accent-color:var(--ims-success)" checked>
              <span><strong style="color:var(--ims-success)">Paid</strong><br><span style="font-size:11px;color:var(--ims-text-muted)">Payment received</span></span>
            </label>
            <label style="display:flex;align-items:center;gap:6px;cursor:pointer;padding:8px 14px;border:1.5px solid var(--ims-border);border-radius:var(--ims-radius);flex:1;min-width:120px" id="ims-pay-btn-cod">
              <input type="radio" name="ims-no-payment" value="cod">
              <span><strong style="color:var(--ims-accent)">COD</strong><br><span style="font-size:11px;color:var(--ims-text-muted)">Cash on delivery</span></span>
            </label>
            <label style="display:flex;align-items:center;gap:6px;cursor:pointer;padding:8px 14px;border:1.5px solid var(--ims-border);border-radius:var(--ims-radius);flex:1;min-width:120px" id="ims-pay-btn-unpaid">
              <input type="radio" name="ims-no-payment" value="unpaid">
              <span><strong style="color:var(--ims-danger)">Unpaid</strong><br><span style="font-size:11px;color:var(--ims-text-muted)">7-day hold</span></span>
            </label>
          </div>
          <div id="ims-unpaid-warning" style="margin-top:6px;font-size:11px;color:var(--ims-danger);display:none">
            <i class="ti ti-clock"></i> Unpaid orders are held for 7 days then automatically flagged for action
          </div>
        </div>
      </div>
      <hr class="ims__divider">
      <div id="ims-deal-banner" class="ims__alert ims__alert--info" style="display:none;margin-bottom:10px"></div>
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:10px">
        <strong style="font-size:13px">Order items</strong>
        <button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_ORDERS.addItem()"><i class="ti ti-plus"></i> Add item</button>
        <select class="ims__select" id="ims-no-add-service" style="width:auto;font-size:12px" onchange="window.IMS_ORDERS.addService(this.value)"><option value="">+ Add service</option></select>
      </div>
      <div id="ims-no-items">
        <div style="font-size:12px;color:var(--ims-text-muted);padding:10px 0">No items added yet</div>
      </div>
      <hr class="ims__divider">
      <div style="display:flex;justify-content:flex-end;align-items:center;gap:10px;margin-bottom:8px">
        <span style="font-size:13px;color:var(--ims-text-sec)">Prices entered are</span>
        <select class="ims__select" id="ims-no-vat-treatment" style="width:auto;font-size:12px">
          <option value="inclusive">Inclusive of VAT</option>
          <option value="exclusive">Exclusive of VAT (VAT added on invoice)</option>
        </select>
      </div>
      <div style="display:flex;justify-content:flex-end;align-items:center;gap:10px;margin-bottom:8px">
        <span style="font-size:13px;color:var(--ims-text-sec)">Discount</span>
        <select class="ims__select" id="ims-no-disc-type" style="width:auto;font-size:12px" onchange="window.IMS_ORDERS.updateTotal()">
          <option value="none">No discount</option>
          <option value="pct">Percentage %</option>
          <option value="fixed">Fixed £</option>
        </select>
        <input class="ims__input" type="number" min="0" step="0.01" id="ims-no-disc-value"
          placeholder="0" style="width:80px;font-size:12px"
          oninput="window.IMS_ORDERS.updateTotal()">
        <span style="font-size:12px;color:var(--ims-danger);font-weight:600" id="ims-no-disc-display"></span>
      </div>
      <div style="display:flex;justify-content:flex-end;align-items:center;gap:12px">
        <span style="font-size:13px;color:var(--ims-text-sec)">Total:</span>
        <span style="font-size:20px;font-weight:700" id="ims-no-total">${util().formatCurrency(0)}</span>
      </div>
    `, `
      <button class="ims__btn ims__btn--ghost" onclick="window.IMS_ORDERS.cancelBatch()">Cancel</button>
      ${_batchOrders.length>0?`<span style="font-size:12px;color:var(--ims-text-muted);margin-right:4px">${_batchOrders.length} saved in batch</span>`:''}
      <button class="ims__btn ims__btn--secondary" id="ims-save-add-btn" onclick="window.IMS_ORDERS.saveOrder(this,true)">
        <i class="ti ti-plus"></i> Save &amp; add new
      </button>
      <button class="ims__btn ims__btn--primary" id="ims-save-order-btn" onclick="window.IMS_ORDERS.saveOrder(this,false)">
        <i class="ti ti-check"></i> ${_batchOrders.length>0?'Save &amp; exit':'Create order'}
      </button>
    `, 'lg');

    await _loadServices();   // after the form is drawn, so the "+ Add service" list is filled
    bindPostcodeLookup();
    _bindAreaCheck();
    setTimeout(()=>{
      document.querySelectorAll('input[name="ims-no-payment"]').forEach(r=>{
        r.addEventListener('change', ()=>{
          const warn = document.getElementById('ims-unpaid-warning');
          if(warn) warn.style.display = r.value === 'unpaid' && r.checked ? '' : 'none';
        });
      });
      document.getElementById('ims-no-phone')?.addEventListener('blur', checkForDeals);
    }, 200);
  }

  // ─── DEALS ───────────────────────────────────────────────────────
  // Looks up the phone-matched customer and any active deal for the
  // selected platform/customer, applies it to already-added items,
  // and shows a banner. Called on platform change and phone blur.
  async function checkForDeals() {
    const platformId = document.getElementById('ims-no-platform')?.value || null;
    const phone = document.getElementById('ims-no-phone')?.value?.trim();

    _matchedCustomer = null;
    if (phone) {
      const { data: cust } = await db().select('customers', {
        select: 'id,customer_type,discount_type,discount_value',
        eq: { phone, is_active: true }, maybeSingle: true   // deactivated customers are not matched
      });
      if (cust) _matchedCustomer = cust;
    }

    const customerId = _matchedCustomer?.id || null;
    // customers table uses wholesale/retailer/trade; deals module now
    // uses the same vocabulary directly — no translation needed.
    const customerType = _matchedCustomer?.customer_type || null;

    const deals = (await window.IMS_DEALS?.getActiveDealsForOrder(customerId, customerType, platformId)) || [];
    if (!deals.length) {
      _activeDeal = null;
    } else {
      deals.sort((a, b) => {
        const aSpecific = (a.deal_customers||[]).some(dc => dc.customer_id === customerId) ? 1 : 0;
        const bSpecific = (b.deal_customers||[]).some(dc => dc.customer_id === customerId) ? 1 : 0;
        if (aSpecific !== bSpecific) return bSpecific - aSpecific;
        return (b.discount_value||0) - (a.discount_value||0);
      });
      _activeDeal = deals[0];
    }

    updateDealBanner();
    _orderItems.forEach((item, i) => { if (item.productId) applyDealToItem(i); });
    renderItems();
  }

  function applyDealToItem(index) {
    const item = _orderItems[index];
    if (!item) return;
    // A line loaded from an order being amended keeps the price already
    // agreed, unless its product is changed.
    if (item.keepPrice) return;
    const base = item.basePrice ?? item.standardPrice ?? 0;
    if (_activeDeal) {
      const { finalPrice } = window.IMS_DEALS.calculateDealPrice(
        base, _matchedCustomer?.discount_type, _matchedCustomer?.discount_value || 0, _activeDeal
      );
      item.unitPrice = finalPrice;
      item.dealPrice = finalPrice;
      item.dealApplied = true;
    } else {
      item.unitPrice = base;
      item.dealPrice = null;
      item.dealApplied = false;
    }
    // The system has just set this line's price itself, so any earlier
    // manual override on the line no longer applies. Without this, a
    // stale "overridden" flag would be saved against a system price.
    item.isOverride = false;
    item.overridePct = 0;
  }

  function updateDealBanner() {
    const banner = document.getElementById('ims-deal-banner');
    if (!banner) return;
    if (_activeDeal) {
      const discLabel = _activeDeal.discount_type === 'percentage'
        ? `${_activeDeal.discount_value}% off`
        : `£${_activeDeal.discount_value} off`;
      banner.style.display = '';
      banner.innerHTML = `<i class="ti ti-tag"></i> Deal applied: <strong>${util().sanitize(_activeDeal.name)}</strong> — ${discLabel} (${_activeDeal.stacking_behaviour==='stack'?'stacks with':'replaces'} standing discount)`;
    } else {
      banner.style.display = 'none';
      banner.innerHTML = '';
    }
  }

  function onPlatformChange() {
    const sel = document.getElementById('ims-no-platform');
    if (!sel) return;
    const opt = sel.options[sel.selectedIndex];
    const type = opt?.dataset?.type || '';
    const platformName = (opt?.textContent || '').toLowerCase();
    const display = document.getElementById('ims-no-type-display');
    const hint = document.getElementById('ims-no-type-hint');
    if (display) {
      display.value = type === 'wholesale' ? 'wholesale' : 'retail';
      // Walk-in customers sometimes place a bulk order and ask for
      // wholesale rates on the spot — unlock the choice for that platform
      // specifically rather than forcing every walk-in sale to Retail.
      // Every other platform stays locked to its own fixed type, since an
      // Amazon or eBay order should never be manually switched.
      const isWalkIn = platformName.includes('walk');
      display.disabled = !isWalkIn;
      if (hint) hint.textContent = isWalkIn
        ? 'Walk-in customer — choose Retail or Wholesale for this order'
        : 'Auto-set from platform';
    }
    checkForDeals();
    updateCustomerLock();
    // If a postcode was already typed before switching to a wholesale
    // platform, trigger the registered-customer postcode lookup now
    // rather than waiting for another keystroke in the postcode field.
    const postcodeInput = document.getElementById('ims-no-postcode');
    if (type === 'wholesale' && postcodeInput?.value?.trim().length >= 3) {
      postcodeInput.dispatchEvent(new Event('input'));
    }
  }

  // Wholesale orders must be tied to a registered customer, otherwise no
  // receivable is created on delivery and the order can never be marked
  // paid (this is how "DA17 6BS" ended up as a customer name and £3.3k of
  // debt became unrecoverable). Marketplace orders are the opposite —
  // buyer names from Temu/Amazon/eBay will never exist as customer
  // records, so those stay free text.
  function updateCustomerLock() {
    const sel   = document.getElementById('ims-no-platform');
    const input = document.getElementById('ims-no-custname');
    const hint  = document.getElementById('ims-no-custname-hint');
    if (!sel || !input || !hint) return;
    const type = sel.options[sel.selectedIndex]?.dataset?.type || '';

    if (type !== 'wholesale') {
      input.readOnly = false;
      input.style.background = '';
      input.placeholder = 'Customer or business name';
      hint.innerHTML = '';
      return;
    }

    input.readOnly = true;
    input.style.background = 'var(--ims-surface2)';
    input.placeholder = 'Select a registered customer';

    if (_pickedCustomerId) {
      hint.innerHTML = `<span style="color:var(--ims-success)"><i class="ti ti-link"></i> Linked to a registered customer</span>
        &nbsp;·&nbsp;<span class="ims__link" style="cursor:pointer" onclick="window.IMS_ORDERS.clearPickedCustomer()">Change</span>`;
    } else {
      hint.innerHTML = `<span style="color:var(--ims-warning)"><i class="ti ti-alert-triangle"></i> Search by postcode above to link a registered customer</span>
        &nbsp;·&nbsp;${_canAddCustomers()
          ? `<span class="ims__link" style="cursor:pointer" onclick="window.IMS_ORDERS.addNewCustomerFromOrder()">+ Add new customer</span>`
          : `<span style="color:var(--ims-text-muted)">Ask an Admin to add new customers</span>`}`;
    }
  }

  function clearPickedCustomer() {
    _pickedCustomerId = null;
    ['ims-no-custname','ims-no-address','ims-no-phone','ims-no-email'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.value = '';
    });
    updateCustomerLock();
    checkForDeals();
  }

  // Honest limitation: the Customers form is itself a modal, so opening it
  // from inside the new-order modal would replace it and lose the order in
  // progress. Rather than silently discarding work, this asks first.
  // "+ Add new customer" (under the customer name) opens the order form's
  // own quick-add, which adds the customer and links them to this order.
  // It used to leave the order for the Customers screen, losing everything
  // typed so far. The full Customers screen is still in the sidebar for
  // detailed records.
  async function addNewCustomerFromOrder() {
    if (!_canAddCustomers()) { ui().toast('Only an Admin can add new customers', 'error'); return; }
    showQuickAddCustomer();
  }

  // ─── POSTCODE LOOKUP ─────────────────────────────────────────────
  function bindPostcodeLookup() {
    const input   = document.getElementById('ims-no-postcode');
    const drop    = document.getElementById('ims-no-postcode-drop');
    const hint    = document.getElementById('ims-no-postcode-hint');
    if (!input || !drop) return;

    const search = util().debounce(async (val) => {
      const seq = ++_searchSeq.postcode;
      const clean = val.replace(/\s/g,'').toUpperCase();
      if (clean.length < 3) { drop.style.display = 'none'; return; }

      // Registered wholesale/retailer/trade customer accounts matching
      // this postcode — only looked up when the selected platform is a
      // wholesale-type platform (e.g. "Retailer / Wholesaler"), since
      // that's the audience with real customer accounts on file.
      const platSel  = document.getElementById('ims-no-platform');
      const platType = platSel?.options[platSel.selectedIndex]?.dataset?.type || '';
      let accountMatches = [];
      if (platType === 'wholesale') {
        // `clean` has spaces stripped ("BS57ES"), but customers store their
        // postcode WITH the space ("BS5 7ES") — so an ilike against `clean`
        // could never match, and this lookup silently returned nothing for
        // every wholesale order ever created. order_postcode_history stores
        // them normalised, which is why only that half appeared to work.
        //
        // Query on the area code (first two chars — always space-free and
        // present regardless of stored format), then compare properly
        // normalised values client-side, so "BS5 7ES" or "BS57ES" typed by
        // the user matches either way it happens to be stored.
        const area = clean.slice(0, 2);
        const { data } = await db().select('customers', {
          select: 'id,business_name,contact_name,phone,email,address,postcode,customer_type',
          ilike: { postcode: area },
          eq: { is_active: true },
          limit: 50
        });
        if (seq !== _searchSeq.postcode) return;   // a newer search has started
        accountMatches = (data || [])
          .filter(c => (c.postcode || '').replace(/\s/g, '').toUpperCase().startsWith(clean))
          .slice(0, 8);
      }

      const { data: matches } = await db().select('order_postcode_history', {
        ilike: { postcode: clean },
        order: { col: 'order_count', asc: false },
        limit: 8
      });
      if (seq !== _searchSeq.postcode) return;   // a newer search has started
      const historyCount = matches?.length || 0;
      const accountCount = accountMatches.length;

      if (!accountCount && !historyCount) {
        drop.style.display = 'none';
        if (hint) hint.textContent = 'New postcode — no previous deliveries';
        return;
      }

      if (hint) {
        hint.textContent = accountCount
          ? `${accountCount} registered customer(s) at this postcode`
          : `${historyCount} previous customer(s) at this postcode`;
      }

      drop.innerHTML = `
        ${_canAddCustomers() ? `<div class="ims__ac-item" style="font-weight:600;color:var(--ims-accent)"
          onmousedown="window.IMS_ORDERS.showQuickAddCustomer()">
          <i class="ti ti-user-plus"></i> + New customer
        </div>` : ''}
        ${accountCount ? `
          <div style="font-size:10px;font-weight:600;color:var(--ims-text-muted);padding:4px 8px;text-transform:uppercase">Registered customers</div>
          ${accountMatches.map(c => `
            <div class="ims__ac-item"
              onmousedown="window.IMS_ORDERS.fillCustomerFromAccount(${JSON.stringify(c).replace(/"/g,'&quot;')})">
              <strong>${util().sanitize(c.business_name)}</strong>
              <span style="font-size:11px;color:var(--ims-text-muted);margin-left:6px">${util().sanitize(c.customer_type||'')}</span>
            </div>`).join('')}
        ` : ''}
        ${historyCount ? `
          ${accountCount ? '<div style="font-size:10px;font-weight:600;color:var(--ims-text-muted);padding:4px 8px;text-transform:uppercase;border-top:1px solid var(--ims-border);margin-top:4px">Previous orders</div>' : ''}
          ${matches.map(m => `
            <div class="ims__ac-item"
              onmousedown="window.IMS_ORDERS.fillCustomer(${JSON.stringify(m).replace(/"/g,'&quot;')})">
              <strong>${util().sanitize(m.customer_name)}</strong>
              <span style="font-size:11px;color:var(--ims-text-muted);margin-left:6px">${m.order_count} order(s)</span>
            </div>`).join('')}
        ` : ''}`;
      drop.style.display = 'block';
    }, 300);

    input.addEventListener('input', e => search(e.target.value));
    input.addEventListener('blur', () => setTimeout(() => { drop.style.display = 'none'; }, 200));
  }

  function fillCustomerFromAccount(record) {
    _pickedCustomerId = record.id;
    const f = (id, v) => { const el = document.getElementById(id); if (el) el.value = v || ''; };
    f('ims-no-custname', record.business_name);
    f('ims-no-address',  record.address);
    f('ims-no-postcode', record.postcode);
    f('ims-no-phone',    record.phone);
    f('ims-no-email',    record.email);
    const drop = document.getElementById('ims-no-postcode-drop');
    if (drop) drop.style.display = 'none';
    // Re-run deal matching now that the phone field reflects this
    // customer — checkForDeals() derives _matchedCustomer by phone, so
    // this naturally picks up their standing discount/deal without
    // duplicating that lookup logic here.
    checkForDeals();
    updateCustomerLock();
    // Remember this customer's registered address/postcode so the
    // "deliver elsewhere" toggle can restore it if the user unticks, and
    // reveal that toggle now that we have a registered customer to compare
    // against. The customer RECORD is never modified — only what's on this
    // one order changes.
    _customerDefaultAddress  = record.address || '';
    _customerDefaultPostcode = record.postcode || '';
    const altWrap = document.getElementById('ims-no-altdelivery-wrap');
    if (altWrap) altWrap.style.display = 'flex';
    const altChk = document.getElementById('ims-no-altdelivery');
    if (altChk) altChk.checked = false;
    lockDeliveryFields(true);
    ui().toast(`Customer details filled from ${record.business_name}`, 'success');   // toast() escapes its message itself
  }

  // When a registered customer is selected, their delivery address/postcode
  // are locked to their record by default. Ticking "Deliver to a different
  // address" unlocks and clears them for a one-off destination; unticking
  // restores the customer's registered address.
  function toggleAltDelivery() {
    const chk  = document.getElementById('ims-no-altdelivery');
    const hint = document.getElementById('ims-no-altdelivery-hint');
    const addr = document.getElementById('ims-no-address');
    const pc   = document.getElementById('ims-no-postcode');
    if (!chk) return;

    if (chk.checked) {
      lockDeliveryFields(false);
      if (addr) addr.value = '';
      if (pc)   pc.value   = '';
      if (hint) hint.innerHTML = '<span style="color:var(--ims-warning)"><i class="ti ti-map-pin"></i> One-off delivery address — the customer record is not changed.</span>';
      if (pc) pc.focus();
    } else {
      if (addr) addr.value = _customerDefaultAddress;
      if (pc)   pc.value   = _customerDefaultPostcode;
      lockDeliveryFields(true);
      if (hint) hint.innerHTML = '';
    }
  }

  function lockDeliveryFields(locked) {
    const addr = document.getElementById('ims-no-address');
    const pc   = document.getElementById('ims-no-postcode');
    [addr, pc].forEach(el => {
      if (!el) return;
      el.readOnly = locked;
      el.style.background = locked ? 'var(--ims-surface2)' : '';
    });
  }

  function fillCustomer(record) {
    const f = (id, v) => { const el = document.getElementById(id); if (el) el.value = v || ''; };
    f('ims-no-custname', record.customer_name);
    f('ims-no-address',  record.address);
    f('ims-no-postcode', record.postcode);
    f('ims-no-phone',    record.phone);
    f('ims-no-email',    record.email);
    const drop = document.getElementById('ims-no-postcode-drop');
    if (drop) drop.style.display = 'none';
    ui().toast('Customer details filled from history', 'success');
  }

  function clearCustomer() {
    ['ims-no-custname','ims-no-address','ims-no-phone','ims-no-email'].forEach(id => {
      const el = document.getElementById(id); if (el) el.value = '';
    });
    const drop = document.getElementById('ims-no-postcode-drop');
    if (drop) drop.style.display = 'none';
  }

  // ─── QUICK-ADD CUSTOMER ──────────────────────────────────────────
  // The postcode dropdown's "+ New customer" previously just called
  // clearCustomer() — clearing the fields for free typing, with no real
  // customer record ever created and no customer_id set on the order.
  // Every order entered this way was silently unlinked: it wouldn't show
  // up under any customer's history, and there was nothing in Customers
  // to find, because nothing was ever inserted there. This replaces that
  // with an actual inline creation form, so "+ New customer" does what
  // it says — creates a real, linked customer record — without leaving
  // the order form or losing anything already entered.
  function showQuickAddCustomer() {
    if (!_canAddCustomers()) { ui().toast('Only an Admin can add new customers', 'error'); return; }
    const drop = document.getElementById('ims-no-postcode-drop');
    if (drop) drop.style.display = 'none';
    const nameField = document.getElementById('ims-no-custname')?.closest('.ims__form-group');
    if (nameField) nameField.style.display = 'none';
    const quickAdd = document.getElementById('ims-no-quickadd');
    if (quickAdd) quickAdd.style.display = '';
    // Pre-fill from whatever's already typed, so staff don't retype it.
    const postcode = document.getElementById('ims-no-postcode')?.value || '';
    const typedName = document.getElementById('ims-no-custname')?.value || '';
    const qaPostcode = document.getElementById('ims-qa-postcode');
    const qaName = document.getElementById('ims-qa-name');
    if (qaPostcode) qaPostcode.value = postcode;
    if (qaName) qaName.value = typedName;
    qaName?.focus();
  }

  async function saveQuickAddCustomer() {
    const name = document.getElementById('ims-qa-name')?.value?.trim();
    if (!name) { ui().toast('Please enter a business/customer name', 'error'); return; }
    const contact  = document.getElementById('ims-qa-contact')?.value?.trim()  || null;
    const phone    = document.getElementById('ims-qa-phone')?.value?.trim()    || null;
    const email    = document.getElementById('ims-qa-email')?.value?.trim()    || null;
    const postcode = document.getElementById('ims-qa-postcode')?.value?.trim() || null;
    const address  = document.getElementById('ims-qa-address')?.value?.trim() || null;

    // Checked again here, not just by hiding the option.
    if (!_canAddCustomers()) { ui().toast('Only an Admin can add new customers', 'error'); return; }
    // Someone other than an Admin adding a customer: no credit until an
    // Admin sets terms (the same rule as the Customers screen).
    const noCredit = window.SM.AUTH.isAdmin() ? {} : { credit_terms: 'Advance payment', credit_limit: 0 };
    const { data, error } = await db().insert('customers', {
      business_name: name, contact_name: contact,
      phone, email, postcode, address, is_active: true, ...noCredit
    });
    if (error) { ui().toast('Failed to create customer: ' + error.message, 'error'); return; }

    _pickedCustomerId = data.id;
    const set = (id, val) => { const el = document.getElementById(id); if (el) el.value = val || ''; };
    set('ims-no-custname', name);
    set('ims-no-phone', phone);
    set('ims-no-email', email);
    set('ims-no-address', address);
    const pcEl = document.getElementById('ims-no-postcode');
    if (pcEl && !pcEl.value) pcEl.value = postcode || '';

    const quickAdd = document.getElementById('ims-no-quickadd');
    if (quickAdd) quickAdd.style.display = 'none';
    const nameField = document.getElementById('ims-no-custname')?.closest('.ims__form-group');
    if (nameField) nameField.style.display = '';

    updateCustomerLock();
    ui().toast(`${name} added — linked to this order`, 'success');
  }

  // Explicit escape hatch for a genuine one-off sale where a permanent
  // customer record isn't wanted — falls back to the old free-text
  // behaviour, but now only by deliberate choice rather than by default.
  function cancelQuickAddCustomer() {
    const quickAdd = document.getElementById('ims-no-quickadd');
    if (quickAdd) quickAdd.style.display = 'none';
    const nameField = document.getElementById('ims-no-custname')?.closest('.ims__form-group');
    if (nameField) nameField.style.display = '';
    clearCustomer();
  }

  // ─── ORDER ITEMS ─────────────────────────────────────────────────
  // ─── SERVICES ON AN ORDER ─────────────────────────────────────────
  // Services from the price list (Logistics > Services), sold as order
  // lines: no stock effect, the order's VAT setting, no deals or price
  // override rules. Whoever carries them out confirms done / not done.
  let _services = [];
  async function _loadServices() {
    const { data } = await window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY)
      .from('services').select('id,name,customer_price,per').eq('company_id', window.SM.AUTH.company?.id).eq('is_active', true).order('sort_order', { ascending: true });
    _services = data || [];
    const sel = document.getElementById('ims-no-add-service');
    if (sel) sel.innerHTML = `<option value="">+ Add service</option>` + (_services.length ? _services.map(v => `<option value="${v.id}">${util().sanitize(v.name)} (${util().formatCurrency(v.customer_price)})</option>`).join('') : '<option value="" disabled>No services set up yet</option>');
  }
  function addService(serviceId) {
    const v = _services.find(x => x.id === serviceId);
    const sel = document.getElementById('ims-no-add-service'); if (sel) sel.value = '';
    if (!v) return;
    _orderItems.push({ productId: '', productName: v.name, sku: '', qty: 1, unitPrice: Number(v.customer_price) || 0, costPrice: 0,
      basePrice: Number(v.customer_price) || 0, dealApplied: false, keepPrice: true, serviceId: v.id, per: v.per });
    renderItems();
  }

  // ─── OUTSIDE OUR DELIVERY AREA? ───────────────────────────────────
  // A warning (not a block) under the postcode when no courier with zones
  // delivers there (every one has it in its "Not delivered" zone), so
  // nobody promises a delivery we cannot make. The latest check wins, so
  // fast typing never shows an old answer.
  let _areaSeq = 0;
  function _bindAreaCheck() {
    const input = document.getElementById('ims-no-postcode');
    if (!input || input.dataset.areaBound === '1') return;
    input.dataset.areaBound = '1';
    let t = null;
    input.addEventListener('input', () => { clearTimeout(t); t = setTimeout(_checkDeliveryArea, 400); });
    input.addEventListener('change', _checkDeliveryArea);
  }
  async function _checkDeliveryArea() {
    const input = document.getElementById('ims-no-postcode'); if (!input) return;
    let hint = document.getElementById('ims-no-area-hint');
    if (!hint) {
      hint = document.createElement('div'); hint.id = 'ims-no-area-hint'; hint.style.cssText = 'font-size:12px;margin-top:4px';
      (document.getElementById('ims-no-postcode-hint') || input).insertAdjacentElement('afterend', hint);
    }
    const seq = ++_areaSeq;
    const pc = String(input.value || '').toUpperCase().replace(/\s+/g, '');
    if (pc.length < 5) { hint.innerHTML = ''; return; }
    const out = pc.slice(0, -3), area = (out.match(/^[A-Z]{1,2}/) || [''])[0];
    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    const { data: zones } = await client.from('courier_zones').select('id,courier_id,kind').eq('company_id', window.SM.AUTH.company?.id);
    const couriers = [...new Set((zones || []).map(z => z.courier_id))];
    if (!couriers.length) { if (seq === _areaSeq) hint.innerHTML = ''; return; }
    const { data: pcs } = await client.from('courier_zone_postcodes').select('courier_id,zone_id,prefix').in('prefix', [out, area]);
    if (seq !== _areaSeq) return;   // a newer check has started
    const notServed = couriers.filter(cid => {
      const mine = (pcs || []).filter(p => p.courier_id === cid);
      const hit = mine.find(p => p.prefix === out) || mine.find(p => p.prefix === area);
      return (zones || []).find(z => z.id === hit?.zone_id)?.kind === 'not_served';
    });
    hint.innerHTML = notServed.length === couriers.length
      ? `<span style="color:var(--ims-warning)"><i class="ti ti-alert-triangle"></i> ${util().sanitize(out)} is outside our delivery area. Check before promising a delivery.</span>`
      : '';
  }

  function addItem() {
    _orderItems.push({ productId: '', productName: '', sku: '', qty: 1, unitPrice: 0, costPrice: 0, basePrice: 0, dealApplied: false });
    renderItems();
  }

  function renderItems() {
    const container = document.getElementById('ims-no-items');
    if (!container) return;
    if (!_orderItems.length) {
      container.innerHTML = '<div style="font-size:12px;color:var(--ims-text-muted);padding:10px 0">No items added yet</div>';
      syncOverrideReason();
      updateTotal();
      return;
    }
    container.innerHTML = _orderItems.map((item, i) => item.serviceId ? `
      <div style="display:grid;grid-template-columns:2fr 70px 110px auto;gap:8px;align-items:center;margin-bottom:8px">
        <div style="display:flex;gap:6px;align-items:center"><span class="ims__pill ims__pill--info" style="font-size:10px">Service</span><span>${util().sanitize(item.productName)}</span></div>
        <input class="ims__input" type="number" min="1" value="${item.qty}" ${item.per === 'order' ? 'disabled title="Charged once per order"' : ''}
          oninput="window.IMS_ORDERS.updateItem(${i},'qty',+this.value||1)">
        <input class="ims__input" type="number" min="0" step="0.01" value="${item.unitPrice}" placeholder="Price (&pound;)" id="ims-price-${i}"
          oninput="window.IMS_ORDERS.updateItem(${i},'unitPrice',+this.value||0)">
        <button class="ims__btn ims__btn--danger ims__btn--sm ims__btn--icon"
          onclick="window.IMS_ORDERS.removeItem(${i})"><i class="ti ti-trash"></i></button>
      </div>` : `
      <div style="display:grid;grid-template-columns:2fr 70px 110px auto;gap:8px;align-items:center;margin-bottom:8px">
        <div style="position:relative">
          <input class="ims__input" type="text" placeholder="Product name or SKU"
            value="${util().sanitize(item.productName)}" autocomplete="off"
            oninput="window.IMS_ORDERS.updateItem(${i},'productName',this.value);window.IMS_ORDERS.searchProduct(${i},this.value,this)">
          <div id="ims-prod-drop-${i}" class="ims__ac-dropdown"></div>
        </div>
        <input class="ims__input" type="number" min="1" value="${item.qty}" placeholder="Qty"
          oninput="window.IMS_ORDERS.updateItem(${i},'qty',+this.value||1)">
        <div style="position:relative">
          <input class="ims__input" type="number" min="0" step="0.01"
            value="${item.unitPrice}" placeholder="Unit price (&pound;)"
            id="ims-price-${i}"
            oninput="window.IMS_ORDERS.updateItem(${i},'unitPrice',+this.value||0);window.IMS_ORDERS.checkOverride(${i})"
            style="${item.isOverride?'border-color:var(--ims-warning);background:var(--ims-warning-bg)':item.dealApplied?'border-color:var(--ims-accent)':''}">
          ${item.productId ? `<span style="font-size:10px;color:var(--ims-text-muted);position:absolute;top:-16px;left:0">Available: <span id="ims-avail-${i}">...</span></span>` : ''}
          <div id="ims-price-note-${i}">${_priceNoteHtml(item)}</div>
        </div>
        <button class="ims__btn ims__btn--danger ims__btn--sm ims__btn--icon"
          onclick="window.IMS_ORDERS.removeItem(${i})"><i class="ti ti-trash"></i></button>
      </div>
      <div id="ims-alloc-panel-${i}" style="margin:-4px 0 8px 0"></div>`).join('');
    // Load available stock for each product against the selected warehouse
    const warehouseId = document.getElementById('ims-no-warehouse')?.value || '';
    _orderItems.forEach((item, i) => {
      if (item.productId) loadAvailStock(i, item.productId, warehouseId);
    });
    syncOverrideReason();
    updateTotal();
  }

  function onWarehouseChange() {
    const warehouseId = document.getElementById('ims-no-warehouse')?.value || '';
    // A marked allocation release belongs to the warehouse it was made in,
    // so changing warehouse cancels it (nothing was released yet).
    const hadReleases = _orderItems.some(i => i.pendingReleases?.length);
    _orderItems.forEach(i => { i.pendingReleases = []; });
    if (hadReleases) ui().toast('Warehouse changed, so the marked allocation releases were cancelled', 'info', 6000);
    _orderItems.forEach((item, i) => {
      const panel = document.getElementById(`ims-alloc-panel-${i}`);
      if (panel) { panel.innerHTML = ''; panel.dataset.expanded = '0'; }
      if (item.productId) loadAvailStock(i, item.productId, warehouseId);
    });
  }

  // ─── RELEASE FROM DROPSHIP ALLOCATION ───────────────────────────
  // When normal stock runs out but a dropship partner has units allocated
  // in this warehouse, staff can release some to this order. The release
  // is only MARKED on the order line (item.pendingReleases) and carried
  // out by saveOrder() after the order has saved. Before, it happened the
  // moment Release was clicked, so closing the form without saving took
  // the units away from the partner for nothing.

  // Units marked for release on this order, for a product in a warehouse.
  function _pendingReleaseQty(productId, warehouseId) {
    return _orderItems.reduce((sum, item) => sum + (item.pendingReleases || [])
      .filter(r => r.productId === productId && r.warehouseId === warehouseId)
      .reduce((s, r) => s + (r.qty || 0), 0), 0);
  }

  // Units already marked for release from one allocation on this order.
  function _pendingForAllocation(allocId) {
    return _orderItems.reduce((sum, item) => sum + (item.pendingReleases || [])
      .filter(r => r.allocId === allocId)
      .reduce((s, r) => s + (r.qty || 0), 0), 0);
  }

  // Refreshes the stock figure and panel on every line of this product,
  // since a release marked on one line changes what the others can see.
  function _refreshAvailForProduct(productId) {
    const warehouseId = document.getElementById('ims-no-warehouse')?.value || '';
    _orderItems.forEach((item, i) => {
      if (item.productId === productId) loadAvailStock(i, productId, warehouseId);
    });
  }

  async function loadAvailStock(index, productId, warehouseId) {
    const el = document.getElementById(`ims-avail-${index}`);
    if (!warehouseId) {
      if (el) { el.textContent = 'Select a warehouse'; el.style.color = 'var(--ims-warning)'; }
      return;
    }
    const { data: sl } = await db().select('stock_levels', {
      eq: { product_id: productId, warehouse_id: warehouseId }, maybeSingle: true, noCompanyFilter: true
    });
    const qty      = sl?.quantity || 0;
    const reserved = sl?.reserved_qty || 0;
    // Units marked for release on this order count as free already, so
    // staff see the effect straight away.
    const alloc    = Math.max(0, (sl?.reserved_by_allocations || 0) - _pendingReleaseQty(productId, warehouseId));
    const trueAvail  = qty - reserved - alloc;
    const looseAvail = qty - reserved; // ignoring dropship allocations
    if (el) {
      el.textContent = trueAvail;
      el.style.color = trueAvail <= 0 ? 'var(--ims-danger)' : trueAvail <= 5 ? 'var(--ims-warning)' : '';
    }

    const item = _orderItems[index];
    const needed = item?.qty || 1;
    const panel = document.getElementById(`ims-alloc-panel-${index}`);
    if (!panel) return;
    if (panel.dataset.expanded === '1') return; // the release list is open; leave it

    if (item?.pendingReleases?.length) {
      const total = item.pendingReleases.reduce((s, r) => s + (r.qty || 0), 0);
      const partners = [...new Set(item.pendingReleases.map(r => r.partner))].join(', ');
      panel.innerHTML = `
        <div class="ims__alert ims__alert--info" style="margin:0;padding:6px 10px;font-size:11px;display:flex;align-items:center;justify-content:space-between;gap:8px">
          <span><i class="ti ti-arrow-back-up"></i> ${total} unit(s) will be released from ${util().sanitize(partners)} when this order is saved.</span>
          <button class="ims__btn ims__btn--ghost ims__btn--sm" onclick="window.IMS_ORDERS.undoRelease(${index})">Undo</button>
        </div>`;
      return;
    }

    if (trueAvail < needed && alloc > 0 && looseAvail >= needed) {
      const shortfall = needed - trueAvail;
      panel.innerHTML = `
        <div class="ims__alert ims__alert--warning" style="margin:0;padding:6px 10px;font-size:11px;display:flex;align-items:center;justify-content:space-between;gap:8px">
          <span><i class="ti ti-alert-triangle"></i> ${alloc} unit(s) here are held for dropship allocation. Releasing some could cover this order.</span>
          <button class="ims__btn ims__btn--warning ims__btn--sm" onclick="window.IMS_ORDERS.toggleAllocationPanel(${index},'${util().jsArg(productId)}','${util().jsArg(warehouseId)}',${shortfall})">Release from allocation</button>
        </div>`;
    } else {
      panel.innerHTML = '';
    }
  }

  async function toggleAllocationPanel(index, productId, warehouseId, shortfall) {
    const panel = document.getElementById(`ims-alloc-panel-${index}`);
    if (!panel) return;
    if (panel.dataset.expanded === '1') {
      panel.dataset.expanded = '0';
      loadAvailStock(index, productId, warehouseId);
      return;
    }
    const { data: allocs } = await db().select('dropship_allocations', {
      select: 'id,remaining_qty,dropship_customers(business_name)',
      eq: { product_id: productId, warehouse_id: warehouseId },
      gt: { remaining_qty: 0 },
      noCompanyFilter: true
    });
    // Leave out units already marked for release on another line.
    const rows = (allocs||[])
      .map(a => ({ ...a, free: (a.remaining_qty||0) - _pendingForAllocation(a.id) }))
      .filter(a => a.free > 0);
    panel.dataset.expanded = '1';
    if (!rows.length) {
      panel.innerHTML = `<div class="ims__alert ims__alert--info" style="margin:0;padding:6px 10px;font-size:11px">No dropship allocations found for this product in this warehouse.</div>`;
      return;
    }
    panel.innerHTML = `
      <div class="ims__card" style="padding:8px;margin:0">
        <div style="font-size:11px;color:var(--ims-text-muted);margin-bottom:6px">Need ${shortfall} more unit(s). Choose how many to release from each partner. Nothing is released until you save the order.</div>
        ${rows.map(a => `
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px">
            <span style="font-size:12px;flex:1">${util().sanitize(a.dropship_customers?.business_name||'Unknown customer')}: ${a.free} allocated</span>
            <input class="ims__input" type="number" min="1" max="${a.free}" value="${Math.min(shortfall, a.free)}" id="ims-alloc-qty-${index}-${a.id}" style="width:70px;font-size:12px">
            <button class="ims__btn ims__btn--warning ims__btn--sm" onclick="window.IMS_ORDERS.releaseFromAllocation(${index},'${util().jsArg(a.id)}','${util().jsArg(productId)}','${util().jsArg(warehouseId)}')">Release</button>
          </div>`).join('')}
        <button class="ims__btn ims__btn--ghost ims__btn--sm" style="margin-top:4px" onclick="window.IMS_ORDERS.toggleAllocationPanel(${index},'${util().jsArg(productId)}','${util().jsArg(warehouseId)}',${shortfall})">Close</button>
      </div>`;
  }

  // Marks a release on this order line. Nothing changes in the database
  // until the order is saved (see saveOrder).
  async function releaseFromAllocation(index, allocId, productId, warehouseId) {
    const item = _orderItems[index];
    if (!item) return;
    const input = document.getElementById(`ims-alloc-qty-${index}-${allocId}`);
    const releaseQty = parseInt(input?.value) || 0;
    if (!releaseQty || releaseQty <= 0) { ui().toast('Enter a quantity to release', 'error'); return; }

    const { data: alloc } = await db().select('dropship_allocations', {
      select: 'remaining_qty,dropship_customers(business_name)', eq: { id: allocId }, maybeSingle: true, noCompanyFilter: true
    });
    if (!alloc) { ui().toast('Allocation not found', 'error'); return; }
    const free = (alloc.remaining_qty||0) - _pendingForAllocation(allocId);
    const qty = Math.min(releaseQty, free);
    if (qty <= 0) { ui().toast('Nothing left to release on this allocation', 'error'); return; }
    const partner = alloc.dropship_customers?.business_name || 'dropship partner';

    item.pendingReleases = [...(item.pendingReleases || []), { allocId, productId, warehouseId, qty, partner }];
    ui().toast(`${qty} unit(s) will be released from ${partner} when you save this order`, 'info', 5000);
    const panel = document.getElementById(`ims-alloc-panel-${index}`);
    if (panel) panel.dataset.expanded = '0';
    _refreshAvailForProduct(productId);
  }

  // Cancels the releases marked on one order line.
  function undoRelease(index) {
    const item = _orderItems[index];
    if (!item) return;
    item.pendingReleases = [];
    ui().toast('Release cancelled', 'info', 3000);
    if (item.productId) _refreshAvailForProduct(item.productId);
  }

  function updateItem(index, field, value) {
    if (_orderItems[index]) {
      _orderItems[index][field] = value;
      if (field === 'qty' || field === 'unitPrice') updateTotal();
    }
  }

  function removeItem(index) {
    _orderItems.splice(index, 1);
    renderItems();
  }

  function updateTotal() {
    const subtotal = _orderItems.reduce((s, i) => s + ((i.qty||1) * (i.unitPrice||0)), 0);
    const discType  = document.getElementById('ims-no-disc-type')?.value || 'none';
    const discVal   = parseFloat(document.getElementById('ims-no-disc-value')?.value) || 0;
    let discAmt = 0;
    if(discType === 'pct' && discVal > 0)   discAmt = subtotal * (discVal / 100);
    if(discType === 'fixed' && discVal > 0) discAmt = Math.min(discVal, subtotal);
    const total = Math.max(0, subtotal - discAmt);
    const discDisplay = document.getElementById('ims-no-disc-display');
    if(discDisplay) discDisplay.textContent = discAmt > 0 ? `− ${util().formatCurrency(discAmt)}` : '';
    const el = document.getElementById('ims-no-total');
    if(el) el.textContent = util().formatCurrency(total);
  }

  // Note under a line's price box: the override warning, or the deal
  // label, or nothing. Shared by renderItems() and checkOverride() so the
  // first draw and every later in-place update always look the same.
  function _priceNoteHtml(item) {
    if (item.isOverride) {
      return `<div style="font-size:10px;margin-top:2px;color:var(--ims-warning)">
          <i class="ti ti-alert-triangle"></i>
          Standard: ${util().formatCurrency(item.standardPrice)}
          (${item.overridePct > 0 ? '+' : ''}${(item.overridePct || 0).toFixed(1)}%)
        </div>`;
    }
    if (item.dealApplied) {
      return `<div style="font-size:10px;margin-top:2px;color:var(--ims-accent)">
          <i class="ti ti-tag"></i>
          Deal price (was ${util().formatCurrency(item.basePrice || item.standardPrice)})
        </div>`;
    }
    return '';
  }

  // Shows the "Price override reason (required)" field only while at
  // least one line is overridden.
  function syncOverrideReason() {
    const hasAnyOverride = _orderItems.some(i => i.isOverride);
    const group = document.getElementById('ims-override-reason-group');
    if (group) group.style.display = hasAnyOverride ? '' : 'none';
  }

  // Runs on every keystroke in a price box.
  //
  // A line counts as overridden when staff type a price that differs from
  // the price the system put there: the deal price if a deal applied,
  // otherwise the standard catalogue price. Re-typing the deal price is
  // therefore not an override. The saved percentage is always measured
  // against the standard catalogue price, because that is what the Price
  // Overrides report compares against (its Standard / Charged columns).
  //
  // This must NOT call renderItems(): that rebuilds every row, which throws
  // the cursor out of the box after each character and wipes any open
  // allocation panels. It updates only this row, then the reason field.
  function checkOverride(index) {
    const item = _orderItems[index];
    if (!item) return;
    const standard = Number(item.standardPrice) || 0;
    if (standard > 0) {
      const expected = (item.dealApplied && item.dealPrice != null)
        ? Number(item.dealPrice)
        : standard;
      const charged = Number(item.unitPrice) || 0;
      item.isOverride = Math.abs(charged - expected) > 0.005;
      item.overridePct = item.isOverride ? ((charged - standard) / standard) * 100 : 0;
    } else {
      // No catalogue price on this product, so there is nothing to
      // override against.
      item.isOverride = false;
      item.overridePct = 0;
    }
    const input = document.getElementById(`ims-price-${index}`);
    if (input) {
      input.style.borderColor = item.isOverride ? 'var(--ims-warning)' : (item.dealApplied ? 'var(--ims-accent)' : '');
      input.style.background  = item.isOverride ? 'var(--ims-warning-bg)' : '';
    }
    const note = document.getElementById(`ims-price-note-${index}`);
    if (note) note.innerHTML = _priceNoteHtml(item);
    syncOverrideReason();
  }

  // Search guards. Every keystroke starts a new search, and the answers can
  // come back in any order. Each search takes a number, and only the latest
  // one may update the list, so an older, slower search can never replace
  // the newer results (or hide the list). Seen as a match that sometimes
  // failed to appear while typing.
  const _searchSeq = { product: {}, postcode: 0 };

  async function searchProduct(index, query, inputEl) {
    const seq = (_searchSeq.product[index] = (_searchSeq.product[index] || 0) + 1);
    if (!query || query.length < 2) {
      const d = document.getElementById(`ims-prod-drop-${index}`);
      if (d) d.style.display = 'none';
      return;
    }
    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    const [{ data: byName }, { data: bySku }] = await Promise.all([
      db().select('products', { ilike: { name: query }, select: 'id,name,sku', limit: 5 }),
      db().select('products', { ilike: { sku: query }, select: 'id,name,sku', limit: 5 })
    ]);
    if (seq !== _searchSeq.product[index]) return;   // a newer search has started
    const combined = [...(byName||[]), ...(bySku||[])];
    const products = combined.filter((p,i,arr) => arr.findIndex(x=>x.id===p.id)===i).slice(0,8);
    const drop = document.getElementById(`ims-prod-drop-${index}`);
    if (!drop || !products?.length) { if (drop) drop.style.display = 'none'; return; }
    drop.innerHTML = products.map(p =>
      `<div class="ims__ac-item"
        onmousedown="window.IMS_ORDERS.selectProduct(${index},${JSON.stringify({id:p.id,name:p.name,sku:p.sku}).replace(/"/g,'&quot;')})">
        <strong>${util().sanitize(p.name)}</strong>
        <span style="font-size:11px;color:var(--ims-text-muted);margin-left:6px">${p.sku}</span>
      </div>`
    ).join('');
    drop.style.display = 'block';
  }

  async function selectProduct(index, product) {
    if (_orderItems[index]) {
      _orderItems[index].keepPrice   = false;   // a different product: priced afresh
      _orderItems[index].productId   = product.id;
      _orderItems[index].productName = product.name;
      _orderItems[index].sku         = product.sku;

      const platSel = document.getElementById('ims-no-platform');
      const platformId = platSel?.value;
      const orderType = document.getElementById('ims-no-type-display')?.value || 'retail';

      const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
      let price = 0;

      if (platformId) {
        const { data: platPrices } = await client.from('product_prices')
          .select('price').eq('product_id', product.id)
          .eq('price_type', 'platform').eq('platform_id', platformId).limit(1);
        if (platPrices?.[0]) price = platPrices[0].price;
      }
      if (!price) {
        const priceType = orderType === 'retail' ? 'rrp' : 'wholesale';
        const { data: basePrices } = await client.from('product_prices')
          .select('price').eq('product_id', product.id)
          .eq('price_type', priceType).is('platform_id', null).limit(1);
        if (basePrices?.[0]) price = basePrices[0].price;
      }
      if (!price) {
        const fallbackType = orderType === 'retail' ? 'wholesale' : 'rrp';
        const { data: fallback } = await client.from('product_prices')
          .select('price').eq('product_id', product.id)
          .eq('price_type', fallbackType).is('platform_id', null).limit(1);
        if (fallback?.[0]) price = fallback[0].price;
      }

      _orderItems[index].basePrice     = price || 0;
      _orderItems[index].unitPrice     = price || 0;
      _orderItems[index].standardPrice = price || 0;
      _orderItems[index].dealApplied   = false;
      // A different product was picked, so any release marked for the
      // previous product on this line no longer applies.
      _orderItems[index].pendingReleases = [];

      const { data: costRows } = await client.from('product_prices')
        .select('price').eq('product_id', product.id)
        .eq('price_type', 'cost').is('platform_id', null).limit(1);
      if (costRows?.[0]) _orderItems[index].costPrice = costRows[0].price;

      applyDealToItem(index);
    }
    renderItems();
    const drop = document.getElementById(`ims-prod-drop-${index}`);
    if (drop) drop.style.display = 'none';
  }

  // ─── WARNING BOX ON TOP OF THE ORDER FORM ───────────────────────
  // A small "go back / go ahead" box shown ABOVE the open order form,
  // used by every warning in saveOrder(). It replaces two things:
  //  - the shared UI.confirm(), which swaps the order form out for the
  //    question. The duplicate-order warning used it, so the form was
  //    wiped and saveOrder() then read empty fields (payment fell back to
  //    "unpaid"; phone, email and notes were saved blank).
  //  - the browser's own confirm(), where OK is always the default, so
  //    pressing Enter out of habit went ahead.
  // Here "Go back" is the default: it has focus, and Enter or Escape both
  // mean go back. Tab only moves between the two buttons. Resolves true
  // only if the go-ahead button is chosen. Pass okLabel: null for a hard
  // block (only "Go back" is shown). Callers must sanitise any
  // user-entered text they put in `message`.
  // Adding customers (quick-add and "+ Add new customer"): Super Admin and
  // Admin, or anyone granted "Can add new customers" in Users. The Customers
  // screen and the database apply the same rule.
  const _canAddCustomers = () => !!window.SM.AUTH.canAddCustomers?.();

  // With reasonLabel, the box also asks for a written reason: the go-ahead
  // button stays disabled until one is typed, and the promise resolves to
  // that reason (or false for "Go back"). Enter inside the reason box adds
  // a new line; it never submits or cancels.
  function _askOverForm({ title, message, okLabel = 'Save anyway', danger = false, reasonLabel = null }) {
    return new Promise(resolve => {
      const wrap = document.createElement('div');
      wrap.style.cssText = 'position:fixed;inset:0;z-index:100000;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;padding:16px';
      wrap.innerHTML = `
        <div role="alertdialog" aria-modal="true" style="background:var(--ims-surface);color:var(--ims-text);border:1px solid var(--ims-border);border-radius:var(--ims-radius);max-width:460px;width:100%;box-shadow:0 20px 50px rgba(0,0,0,.3);font-family:var(--ims-font);font-size:14px">
          <div style="padding:16px 18px 6px;font-weight:700;display:flex;align-items:center;gap:8px"><i class="ti ti-alert-triangle" style="color:var(--ims-warning)"></i>${util().sanitize(title)}</div>
          <div style="padding:6px 18px 14px;color:var(--ims-text-sec);line-height:1.5">${message}</div>
          ${reasonLabel ? `<div style="padding:0 18px 14px"><label style="display:block;font-size:12px;font-weight:600;margin-bottom:6px">${util().sanitize(reasonLabel)}</label><textarea data-act-reason rows="3" class="ims__textarea" style="width:100%"></textarea></div>` : ''}
          <div style="padding:12px 18px;border-top:1px solid var(--ims-border);display:flex;justify-content:flex-end;gap:8px">
            <button type="button" class="ims__btn ims__btn--ghost" data-act="cancel">Go back</button>
            ${okLabel ? `<button type="button" class="ims__btn ims__btn--${danger ? 'danger' : 'warning'}" data-act="ok">${util().sanitize(okLabel)}</button>` : ''}
          </div>
        </div>`;
      const cancelBtn = () => wrap.querySelector('[data-act="cancel"]');
      const okBtn = () => wrap.querySelector('[data-act="ok"]');
      const reasonBox = () => wrap.querySelector('[data-act-reason]');
      const reasonText = () => (reasonBox()?.value || '').trim();
      const finish = (answer) => {
        document.removeEventListener('keydown', onKey, true);
        wrap.remove();
        resolve(answer && reasonLabel ? reasonText() : answer);
      };
      const onKey = (e) => {
        if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); finish(false); }
        else if (e.key === 'Enter') {
          if (document.activeElement === reasonBox()) return;   // new line in the reason
          e.preventDefault(); e.stopPropagation(); finish(!!okBtn() && !okBtn().disabled && document.activeElement === okBtn());
        }
        else if (e.key === 'Tab') {
          e.preventDefault(); e.stopPropagation();
          const order = [reasonBox(), cancelBtn(), okBtn()].filter(el => el && !el.disabled);
          const at = order.indexOf(document.activeElement);
          (order[(at + 1) % order.length] || cancelBtn()).focus();
        }
      };
      wrap.addEventListener('click', e => {
        const act = e.target.closest('[data-act]')?.dataset.act;
        if (act === 'ok' && okBtn()?.disabled) return;
        if (act) finish(act === 'ok');
      });
      if (reasonLabel) {
        wrap.addEventListener('input', () => { if (okBtn()) okBtn().disabled = !reasonText(); });
      }
      document.addEventListener('keydown', onKey, true);
      // Placed inside the app container: the app's colours (--ims-surface
      // and the rest, light and dark) are defined on it, not on the page,
      // so a box attached to the page itself had no background at all.
      (document.getElementById('ims-app') || document.body).appendChild(wrap);
      if (reasonLabel && okBtn()) okBtn().disabled = true;
      (reasonBox() || cancelBtn()).focus();
    });
  }

  // ─── SAVE ORDER ──────────────────────────────────────────────────
  // ─── AMEND ORDER ─────────────────────────────────────────────────
  // A normal order can be amended (customer and delivery details, items,
  // warehouse, notes) while it is Confirmed, Picking or Packed, or on a
  // manifest whose van has not departed ("Van departed" in Logistics). It
  // uses the New order form, prefilled, so every rule still applies: stock
  // check, deals, price overrides, wholesale block, credit limit and who may
  // add customers. Stock reservations move by the difference only.
  let _amend = null;   // { order, items, manifest } while amending

  async function _amendState(order) {
    if (!order) return { ok: false, why: 'Order not found.' };
    if (order.fulfillment_type === 'dropship') return { ok: false, why: 'Dropship orders are changed from the Dropship screen.' };
    if (['confirmed', 'picking', 'packed'].includes(order.status)) return { ok: true };
    if (order.status === 'dispatched') {
      const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
      const { data: jobs } = await client.from('logistics_jobs')
        .select('manifest_id,manifests(manifest_number,departed_at)').eq('order_id', order.id);
      const onManifest = (jobs || []).find(j => j.manifest_id && j.manifests);
      if (onManifest && !onManifest.manifests.departed_at) return { ok: true, manifest: onManifest.manifests.manifest_number };
      return { ok: false, why: onManifest ? `The van for ${onManifest.manifests.manifest_number} has departed.` : 'It has already been sent.' };
    }
    return { ok: false, why: `It is ${order.status}.` };
  }

  // Did the lines change (product, quantity or price)?
  function _itemsChanged(oldItems, newItems) {
    const key = (pid, name, qty, price) => `${pid || ''}|${pid ? '' : String(name || '').toLowerCase()}|${Number(qty) || 0}|${(Number(price) || 0).toFixed(2)}`;
    const a = (oldItems || []).map(i => key(i.product_id, i.product_name, i.quantity, i.unit_price)).sort().join(';');
    const b = (newItems || []).map(i => key(i.productId, i.productName, i.qty || 1, i.unitPrice)).sort().join(';');
    return a !== b;
  }

  // Plain-English list of what changed, for the activity log.
  function _amendSummary(old, oldItems, newItems, fields) {
    const lines = [];
    const byKey = {};
    (oldItems || []).forEach(i => { const k = i.product_id || `n:${i.product_name}`; byKey[k] = byKey[k] || { name: i.product_name, oldQty: 0, newQty: 0, oldPrice: i.unit_price, newPrice: null }; byKey[k].oldQty += (i.quantity || 0); });
    (newItems || []).forEach(i => { const k = i.productId || `n:${i.productName}`; byKey[k] = byKey[k] || { name: i.productName, oldQty: 0, newQty: 0, oldPrice: null, newPrice: i.unitPrice }; byKey[k].newQty += (i.qty || 1); byKey[k].newPrice = i.unitPrice; });
    Object.values(byKey).forEach(v => {
      if (!v.oldQty) lines.push(`Added ${v.newQty} x ${v.name}`);
      else if (!v.newQty) lines.push(`Removed ${v.oldQty} x ${v.name}`);
      else {
        if (v.oldQty !== v.newQty) lines.push(`${v.name}: ${v.oldQty} to ${v.newQty}`);
        if (v.newPrice != null && v.oldPrice != null && Math.abs(v.newPrice - v.oldPrice) > 0.004) lines.push(`${v.name} price £${Number(v.oldPrice).toFixed(2)} to £${Number(v.newPrice).toFixed(2)}`);
      }
    });
    const labels = { customer_name: 'Customer', address: 'Address', postcode: 'Postcode', phone: 'Phone', email: 'Email', external_ref: 'Reference', warehouse_id: 'Warehouse' };
    Object.entries(labels).forEach(([k, label]) => { if ((old[k] || '') !== (fields[k] || '')) lines.push(`${label} changed`); });
    if (Math.abs((old.total_value || 0) - (fields.total_value || 0)) > 0.004) lines.push(`Total £${Number(old.total_value || 0).toFixed(2)} to £${Number(fields.total_value || 0).toFixed(2)}`);
    return lines.join('; ') || 'No changes';
  }

  async function amendOrder(orderId) {
    const { data: order } = await db().select('orders', { eq: { id: orderId }, maybeSingle: true, noCompanyFilter: true });
    const st = await _amendState(order);
    if (!st.ok) { ui().toast(`${order?.order_number || 'This order'} can no longer be amended. ${st.why}`, 'warning', 8000); return; }
    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    const { data: items } = await client.from('order_items').select('*').eq('order_id', orderId);

    await showNewOrder(false);
    const set = (id, v) => { const el = document.getElementById(id); if (el) el.value = v ?? ''; };
    set('ims-no-platform', order.platform_id);
    onPlatformChange();
    await checkForDeals();                       // before the lines load, so it cannot re-price them
    const typeSel = document.getElementById('ims-no-type-display');
    if (typeSel && order.order_type) typeSel.value = order.order_type;
    set('ims-no-warehouse', order.warehouse_id);
    set('ims-no-postcode', order.postcode);
    _checkDeliveryArea();
    set('ims-no-custname', order.customer_name);
    set('ims-no-address', order.address);
    set('ims-no-phone', order.phone);
    set('ims-no-email', order.email);
    set('ims-no-extref', order.external_ref);
    set('ims-no-notes', order.notes);
    set('ims-no-orderdate', String(order.created_at || '').split('T')[0]);
    set('ims-no-vat-treatment', order.vat_treatment || 'inclusive');
    _pickedCustomerId = order.customer_id || null;
    updateCustomerLock();

    // Payment is handled from the order itself (Mark as paid, Refund), so
    // the payment choice is set to the order's own and hidden.
    const pay = document.querySelector(`input[name="ims-no-payment"][value="${order.payment_status}"]`);
    if (pay) pay.checked = true;
    const payGroup = document.getElementById('ims-no-payment-btns')?.closest('.ims__form-group');
    if (payGroup) payGroup.innerHTML = `<label class="ims__label">Payment</label><div style="font-size:13px">${util().sanitize(order.payment_status || 'unpaid')}. Payments and refunds are recorded from the order itself.</div>`
      + `<input type="radio" name="ims-no-payment" value="${util().sanitize(order.payment_status || 'unpaid')}" checked style="display:none">`;

    _orderItems = (items || []).map(i => ({
      productId: i.product_id || '', productName: i.product_name || '', sku: i.sku || '',
      qty: i.quantity || 1, unitPrice: i.unit_price || 0, costPrice: i.cost_price || 0,
      basePrice: i.standard_price ?? i.unit_price ?? 0, standardPrice: i.standard_price ?? null,
      isOverride: !!i.price_overridden, overridePct: i.override_pct || 0,
      dealApplied: false, keepPrice: true,
      serviceId: i.service_id || null, per: i.service_id ? (_services.find(v => v.id === i.service_id)?.per || 'order') : undefined
    }));
    const reasonLine = (items || []).find(i => i.price_overridden && i.override_reason);
    if (reasonLine) set('ims-no-override-reason', reasonLine.override_reason);
    // Keep any order-level discount: the saved total is below the lines.
    const linesTotal = _orderItems.reduce((t, i) => t + (i.qty || 1) * (i.unitPrice || 0), 0);
    if (linesTotal - (order.total_value || 0) > 0.005) { set('ims-no-disc-type', 'fixed'); set('ims-no-disc-value', (linesTotal - order.total_value).toFixed(2)); }
    renderItems();
    updateTotal();

    const titleEl = document.getElementById('ims-modal-title');
    if (titleEl) titleEl.textContent = `Amend ${order.order_number}${st.manifest ? ` (on ${st.manifest})` : ''}`;
    document.getElementById('ims-save-add-btn')?.remove();
    const saveBtn = document.getElementById('ims-save-order-btn');
    if (saveBtn) saveBtn.innerHTML = '<i class="ti ti-check"></i> Save changes';
    _amend = { order, items: items || [], manifest: st.manifest || null };
  }

  async function _saveAmendment(c) {
    const old = _amend.order;
    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    // Checked again: the van may have departed while the form was open.
    const { data: fresh } = await db().select('orders', { eq: { id: old.id }, maybeSingle: true, noCompanyFilter: true });
    const st = await _amendState(fresh);
    if (!st.ok) { ui().toast(`${c.orderNumber} can no longer be amended. ${st.why} Nothing was changed.`, 'warning', 9000); c.restoreBtn(); return; }

    // 1. New lines first. If they cannot be saved, nothing has changed yet.
    const itemRows = _orderItems.map(item => ({
      order_id: old.id, product_id: item.productId || null, service_id: item.serviceId || null, product_name: item.productName, sku: item.sku || '',
      quantity: item.qty || 1, unit_price: item.unitPrice || 0, total_price: (item.qty || 1) * (item.unitPrice || 0),
      cost_price: item.costPrice || 0, standard_price: item.standardPrice || null,
      price_overridden: item.isOverride || false, override_reason: item.isOverride ? c.overrideReason : null,
      override_pct: item.isOverride ? Math.round((item.overridePct || 0) * 100) / 100 : null
    }));
    const { data: newLines, error: insErr } = await client.from('order_items').insert(itemRows).select('id');
    if (insErr) { ui().toast(`Changes not saved. The item lines could not be saved, so nothing was changed. Reason: ${insErr.message}`, 'error', 10000); c.restoreBtn(); return; }
    // 2. Then the old lines go. If that fails, the new lines are taken out
    //    again, so the order is exactly as it was.
    const oldIds = _amend.items.map(i => i.id);
    if (oldIds.length) {
      const { error: delErr } = await client.from('order_items').delete().in('id', oldIds);
      if (delErr) {
        await client.from('order_items').delete().in('id', (newLines || []).map(r => r.id));
        ui().toast(`Changes not saved. The old item lines could not be replaced, so nothing was changed. Reason: ${delErr.message}`, 'error', 10000);
        c.restoreBtn(); return;
      }
    }
    // 3. The order itself.
    const fields = {
      platform_id: c.platformId, order_type: c.orderType, customer_name: c.custName, customer_id: c.matchedCustomerId,
      warehouse_id: c.warehouseId, postcode: c.postcode, address: c.address, phone: c.g('ims-no-phone'), email: c.g('ims-no-email'),
      external_ref: c.g('ims-no-extref') || old.external_ref || null,   // an empty box keeps the order's existing reference
      notes: [c.g('ims-no-notes'), ..._orderItems.flatMap(i => (i.pendingReleases || []).map(r =>
        `Released ${r.qty} x ${i.productName} from ${r.partner} allocation to fulfil this order.`))].filter(Boolean).join(' | '),
      total_value: c.total, has_price_override: c.hasAnyOverride, vat_treatment: c.vatTreatment,
      deal_id: _activeDeal?.id || old.deal_id || null, deal_name_snapshot: _activeDeal?.name || old.deal_name_snapshot || null,
      amended_at: new Date().toISOString()
    };
    // Money: a paid order that now costs more has more to collect; one that
    // costs less needs a refund (done with Refund on the order, never here).
    let payNote = '';
    if (old.payment_status === 'paid' && c.total > (old.total_value || 0) + 0.005) {
      fields.payment_status = 'partial';
      payNote = ` £${(c.total - old.total_value).toFixed(2)} more to collect (payment now partial).`;
    } else if (old.payment_status === 'paid' && c.total < (old.total_value || 0) - 0.005) {
      payNote = ` £${(old.total_value - c.total).toFixed(2)} to refund: use Refund on the order.`;
    }
    const summary = _amendSummary(old, _amend.items, _orderItems, fields);
    const { error: upErr } = await db().update('orders', old.id, fields);
    if (upErr) ui().toast(`${c.orderNumber}: the items were changed, but the order details could not be saved (${upErr.message}). Please check the order.`, 'warning', 12000);

    // 4. Allocation releases marked on new lines (same as a new order).
    for (const item of _orderItems) {
      for (const rel of (item.pendingReleases || [])) {
        if (rel.warehouseId !== c.warehouseId) continue;
        const { data: alloc } = await db().select('dropship_allocations', { select: 'allocated_qty,remaining_qty', eq: { id: rel.allocId }, maybeSingle: true, noCompanyFilter: true });
        const actual = Math.min(rel.qty, alloc?.remaining_qty || 0);
        if (actual <= 0) continue;
        const { error: allocErr } = await db().update('dropship_allocations', rel.allocId, {
          allocated_qty: Math.max(0, (alloc.allocated_qty || 0) - actual), remaining_qty: Math.max(0, (alloc.remaining_qty || 0) - actual)
        });
        if (allocErr) continue;
        const { data: relSl } = await db().select('stock_levels', { eq: { product_id: rel.productId, warehouse_id: rel.warehouseId }, maybeSingle: true, noCompanyFilter: true });
        if (relSl) await db().update('stock_levels', relSl.id, { reserved_by_allocations: Math.max(0, (relSl.reserved_by_allocations || 0) - actual) });
        await logActivity(old.id, 'Released from allocation', '', '', `${actual} x ${item.productName} released from ${rel.partner} allocation`);
      }
    }

    // 5. Reservations move by the difference only (or move warehouse),
    //    one product at a time.
    const oldQ = {}, newQ = {};
    _amend.items.forEach(i => { if (i.product_id) oldQ[i.product_id] = (oldQ[i.product_id] || 0) + (i.quantity || 0); });
    _orderItems.forEach(i => { if (i.productId) newQ[i.productId] = (newQ[i.productId] || 0) + (i.qty || 1); });
    const moves = [];
    if (old.warehouse_id !== c.warehouseId) {
      Object.entries(oldQ).forEach(([p, q]) => moves.push([p, old.warehouse_id, -q]));
      Object.entries(newQ).forEach(([p, q]) => moves.push([p, c.warehouseId, q]));
    } else {
      new Set([...Object.keys(oldQ), ...Object.keys(newQ)]).forEach(p => { const d = (newQ[p] || 0) - (oldQ[p] || 0); if (d) moves.push([p, c.warehouseId, d]); });
    }
    const reserveFailed = [];
    for (const [p, wh, d] of moves) {
      if (!wh) continue;
      const { data: sl } = await db().select('stock_levels', { eq: { product_id: p, warehouse_id: wh }, maybeSingle: true, noCompanyFilter: true });
      if (!sl) { if (d > 0) reserveFailed.push(p); continue; }
      const { error } = await db().update('stock_levels', sl.id, { reserved_qty: Math.max(0, (sl.reserved_qty || 0) + d) });
      if (error) reserveFailed.push(p);
    }

    await logActivity(old.id, 'Order amended', old.status, old.status, summary);

    // 6. Reminders.
    const notes = [];
    if (payNote) notes.push(payNote.trim());
    if (st.manifest) notes.push(`It is on ${st.manifest}: reprint the manifest for the driver.`);
    else if (['picking', 'packed'].includes(old.status)) notes.push('It was already being picked or packed: tell the warehouse.');
    const { data: ship } = await client.from('shipments').select('id').eq('order_id', old.id).eq('is_return_collection', false).limit(1);
    if (ship?.length) notes.push('A courier label exists: update it with the courier.');
    if (reserveFailed.length) notes.push('Some stock reservations could not be updated: please check stock levels.');

    _amend = null;
    _orderItems = [];
    ui().closeModal();
    ui().toast(`${c.orderNumber} amended. ${summary}.${notes.length ? ' ' + notes.join(' ') : ''}`, notes.length ? 'warning' : 'success', notes.length ? 12000 : 6000);
    load();
  }

  async function saveOrder(btn, addAnother=false) {
    const saveBtn = btn || document.getElementById(addAnother?'ims-save-add-btn':'ims-save-order-btn');
    if(saveBtn){ saveBtn.disabled=true; saveBtn.innerHTML=`<i class="ti ti-loader-2 ims__spin"></i> Saving...`; }
    const restoreBtn=()=>{ if(saveBtn && _amend){ saveBtn.disabled=false; saveBtn.innerHTML='<i class="ti ti-check"></i> Save changes'; return; } if(saveBtn){ saveBtn.disabled=false; saveBtn.innerHTML=addAnother?'<i class="ti ti-plus"></i> Save &amp; add new':'<i class="ti ti-check"></i> '+((_batchOrders.length>0)?'Save &amp; exit':'Create order'); } };
    const g = id => document.getElementById(id)?.value?.trim() || '';
    const postcode   = g('ims-no-postcode').toUpperCase().replace(/\s+/g,' ');
    const platformId = g('ims-no-platform');
    const custName   = g('ims-no-custname');
    const address    = g('ims-no-address');
    const orderDate  = g('ims-no-orderdate');
    const warehouseId = g('ims-no-warehouse');

    if (!postcode)   { ui().toast('Postcode is required', 'error'); restoreBtn(); return; }
    // A marketplace order (Amazon, eBay, Temu, B&Q...) must carry the
    // marketplace's own order number, so it can be matched with the
    // marketplace later. (Left empty, StockMaestro used to put its own
    // order number there instead.)
    {
      const platName = (document.getElementById('ims-no-platform')?.selectedOptions?.[0]?.textContent || '').trim().toLowerCase();
      const isMarketplace = (window.SM.EMAIL?.EXCLUDED_PLATFORMS || []).includes(platName);
      const extRefVal = g('ims-no-extref');
      if (isMarketplace && (!extRefVal || (_amend && extRefVal === _amend.order.order_number))) {
        ui().toast(`Please enter the ${document.getElementById('ims-no-platform')?.selectedOptions?.[0]?.textContent?.trim() || 'marketplace'} order number in External reference.`, 'error', 7000);
        document.getElementById('ims-no-extref')?.focus();
        restoreBtn(); return;
      }
    }
    if (!orderDate)  { ui().toast('Order date is required', 'error'); restoreBtn(); return; }
    if (!warehouseId) { ui().toast('Please select a fulfilling warehouse', 'error'); restoreBtn(); return; }
    if (!platformId) { ui().toast('Please select a platform', 'error'); restoreBtn(); return; }
    if (!custName)   { ui().toast('Customer name is required', 'error'); restoreBtn(); return; }
    if (!address)    { ui().toast('Delivery address is required', 'error'); restoreBtn(); return; }
    if (!_orderItems.length) { ui().toast('Please add at least one item', 'error'); restoreBtn(); return; }

    const emptyItems = _orderItems.filter(i => !i.productName);
    if (emptyItems.length) { ui().toast('All items must have a product name', 'error'); restoreBtn(); return; }

    const orderType = document.getElementById('ims-no-type-display')?.value || 'retail';
    const vatTreatment = document.getElementById('ims-no-vat-treatment')?.value || 'inclusive';
    const subtotal  = _orderItems.reduce((s, i) => s + ((i.qty||1) * (i.unitPrice||0)), 0);
    const discType  = document.getElementById('ims-no-disc-type')?.value || 'none';
    const discVal   = parseFloat(document.getElementById('ims-no-disc-value')?.value) || 0;
    let discAmt = 0;
    if(discType === 'pct' && discVal > 0)   discAmt = subtotal * (discVal / 100);
    if(discType === 'fixed' && discVal > 0) discAmt = Math.min(discVal, subtotal);
    const total = Math.max(0, subtotal - discAmt);

    // Filter to ORD- prefixed orders only — 'orders' also holds DSO- (dropship)
    // and other-prefixed rows, so an unfiltered "most recent" lookup can pick
    // up the wrong prefix and generate a number that collides with an
    // existing ORD- order.
    let orderNumber;
    if (_amend) {
      orderNumber = _amend.order.order_number;   // amending: the order keeps its number
      // An order with a sales invoice keeps its items as invoiced (customer
      // and delivery details can still change). Otherwise the invoice
      // already sent would silently differ from the order.
      if (_amend.order.invoice_id && _itemsChanged(_amend.items, _orderItems)) {
        await _askOverForm({
          title: 'Items are invoiced',
          message: `${util().sanitize(orderNumber)} already has a sales invoice, so its items cannot be changed here. Credit or cancel the invoice in Accounts first. Customer and delivery details can still be changed.`,
          okLabel: null
        });
        restoreBtn(); return;
      }
    } else {
      const { data: lastOrder } = await db().select('orders', {
        select: 'order_number', ilike: { order_number: 'ORD-' }, order: { col: 'order_number', asc: false }, limit: 1
      });
      orderNumber = util().generateRef('ORD', lastOrder?.[0]?.order_number);
    }

    // Link this order to an existing customer record if one matches by phone —
    // needed for customer order history/counts and (for wholesale) AR automation.
    let matchedCustomerId = null;
    // An explicitly picked customer always wins over phone-matching, which
    // is only a best-effort fallback for orders typed in without using the
    // postcode lookup.
    let cust = null;
    if (_pickedCustomerId) {
      const { data: picked } = await db().select('customers', {
        eq: { id: _pickedCustomerId }, maybeSingle: true
      });
      cust = picked || null;
    }
    const phoneForLookup = g('ims-no-phone');
    if (!cust && phoneForLookup) {
      const { data: byPhone } = await db().select('customers', {
        eq: { phone: phoneForLookup.trim(), is_active: true }, maybeSingle: true   // deactivated customers are not matched
      });
      cust = byPhone || null;
    }
    if (true) {
      if (cust) {
        matchedCustomerId = cust.id;
        if (orderType === 'wholesale' && cust.credit_limit > 0) {
          const { data: recs } = await db().select('accounts_receivable', {
            eq: { customer_id: cust.id, status: 'unpaid' }
          });
          const outstanding = (recs||[]).reduce((s,r) => s + ((r.amount||0)-(r.paid_amount||0)), 0);
          const remaining = cust.credit_limit - outstanding;
          if (total > remaining) {
            const proceed = await _askOverForm({
              title: 'Credit limit warning',
              message: `Customer: <strong>${util().sanitize(cust.business_name)}</strong><br>Credit limit: £${cust.credit_limit.toFixed(2)}<br>Outstanding: £${outstanding.toFixed(2)}<br>This order: £${total.toFixed(2)}<br>Over limit by: <strong>£${(total - remaining).toFixed(2)}</strong>`,
              okLabel: 'Save anyway'
            });
            if (!proceed) { restoreBtn(); return; }
          }
        }
      }
    }

    // A wholesale order with no linked customer can never produce a
    // receivable on delivery, so it would be permanently stuck as unpaid
    // with no way to clear it. Only relevant if anything is actually
    // going to be owed — a walk-in customer paying cash/card in full on
    // the spot has nothing outstanding, so no receivable is needed and
    // this warning would just be noise for that case.
    const paymentStatusCheck = document.querySelector('input[name="ims-no-payment"]:checked')?.value || 'unpaid';
    // This is a hard block, not a warning. Saved anyway, the order would
    // get no receivable on delivery (every delivery path needs a linked
    // customer), so the money owed would never appear in Accounts, and it
    // could not be marked paid either ("Mark as paid" is retail only).
    // The only way out was a Super Admin linking a customer afterwards.
    if (orderType === 'wholesale' && !matchedCustomerId && paymentStatusCheck !== 'paid') {
      await _askOverForm({
        title: 'Link a registered customer first',
        message: 'A wholesale order that is not paid in full must be linked to a registered customer. Otherwise nothing owed is recorded in Accounts when it is delivered, and the payment cannot be recorded.<br><br>To continue, either:<br>&bull; search the customer by <strong>postcode</strong> and select them, or<br>' +
          (_canAddCustomers()
            ? '&bull; if they are new, add them with <strong>+ New customer</strong> in the postcode search, or<br>'
            : '&bull; if they are new, <strong>ask an Admin</strong> to add them, or<br>') +
          '&bull; if they have paid in full now, set payment to <strong>Paid</strong>.',
        okLabel: null
      });
      restoreBtn();
      return;
    }

    const hasAnyOverride = _orderItems.some(i => i.isOverride);
    const overrideReason = document.getElementById('ims-no-override-reason')?.value?.trim() || null;
    if (hasAnyOverride && !overrideReason) { ui().toast('Please enter a reason for the price override', 'error'); restoreBtn(); return; }

    // Stock check. Before saving, warn if any product on this order needs
    // more than is free in the chosen warehouse: on hand, minus reserved,
    // minus what is held for dropship partners (after any releases marked
    // on this order). Not a hard block, because staff sometimes sell ahead
    // of an incoming delivery on purpose, but it can no longer happen by
    // accident. Before this, an order could quietly over-promise stock,
    // including units held for a partner.
    const needByProduct = {};
    _orderItems.forEach(i => {
      if (!i.productId) return;
      if (!needByProduct[i.productId]) needByProduct[i.productId] = { name: i.productName, qty: 0 };
      needByProduct[i.productId].qty += (i.qty || 1);
    });
    const shortLines = [];
    let anyHeld = false;
    for (const [productId, need] of Object.entries(needByProduct)) {
      const { data: sl } = await db().select('stock_levels', {
        eq: { product_id: productId, warehouse_id: warehouseId }, maybeSingle: true, noCompanyFilter: true
      });
      const held = Math.max(0, (sl?.reserved_by_allocations || 0) - _pendingReleaseQty(productId, warehouseId));
      // When amending, this order's own existing reservation (same
      // warehouse) is already inside "reserved", so it counts as free for
      // this order: only the extra units need to be available.
      const ownHeld = (_amend && _amend.order.warehouse_id === warehouseId)
        ? _amend.items.filter(i => i.product_id === productId).reduce((t, i) => t + (i.quantity || 0), 0) : 0;
      const free = (sl?.quantity || 0) - (sl?.reserved_qty || 0) - held + ownHeld;
      if (need.qty <= free) continue;
      let heldText = '';
      if (held > 0) {
        anyHeld = true;
        // DB.select has no "greater than" filter, so empty allocations are
        // dropped here instead.
        const { data: holders } = await db().select('dropship_allocations', {
          select: 'remaining_qty,dropship_customers(business_name)',
          eq: { product_id: productId, warehouse_id: warehouseId }, noCompanyFilter: true
        });
        const names = [...new Set((holders || []).filter(h => (h.remaining_qty || 0) > 0)
          .map(h => h.dropship_customers?.business_name).filter(Boolean))];
        heldText = ` (${held} held for ${names.length ? names.join(', ') : 'dropship partners'})`;
      }
      shortLines.push(`${need.name}: needs ${need.qty}, ${Math.max(0, free)} available${heldText}`);
    }
    if (shortLines.length) {
      const proceed = await _askOverForm({
        title: 'Not enough stock',
        message: shortLines.map(l => util().sanitize(l)).join('<br>') +
          (anyHeld ? '<br><br>To use stock held for a partner, choose <strong>Go back</strong> and use <strong>Release from allocation</strong> on that line.' : ''),
        okLabel: 'Save anyway'
      });
      if (!proceed) { restoreBtn(); return; }
    }

    // Duplicate detection. A duplicate is the same customer (the linked
    // account, or the same name AND postcode) with the same products and
    // quantities, created in the last 30 minutes (cancelled orders ignored).
    // It used to be the same name and the same total within 60 seconds, so
    // a real duplicate a minute later, or with a typo, slipped through,
    // while a different order of the same value was flagged.
    // "Create anyway" needs a written reason, kept in the activity log.
    const thirtyMinsAgo = new Date(Date.now() - 30 * 60000).toISOString();
    const { data: recentOrders } = _amend ? { data: [] } : await db().select('orders', {   // not when amending: it is the same order
      select: 'id,order_number,customer_id,customer_name,postcode,status,order_items(product_id,product_name,quantity)',
      gte: { created_at: thirtyMinsAgo },
      order: { col: 'created_at', asc: false },
      limit: 50
    });
    const normText = v => String(v || '').toLowerCase().replace(/\s+/g, ' ').trim();
    const normPc   = v => String(v || '').toUpperCase().replace(/\s+/g, '');
    const basket = lines => {
      const m = {};
      lines.forEach(l => {
        const k = l.pid ? `p:${l.pid}` : `n:${normText(l.name)}`;
        m[k] = (m[k] || 0) + (Number(l.qty) || 0);
      });
      return JSON.stringify(Object.keys(m).sort().map(k => [k, m[k]]));
    };
    const thisBasket = basket(_orderItems.map(i => ({ pid: i.productId, name: i.productName, qty: i.qty || 1 })));
    const duplicateOf = (recentOrders || []).find(o => {
      if (o.status === 'cancelled') return false;
      const sameCustomer = (matchedCustomerId && o.customer_id === matchedCustomerId)
        || (normText(o.customer_name) === normText(custName) && normPc(o.postcode) === normPc(postcode));
      if (!sameCustomer) return false;
      return basket((o.order_items || []).map(i => ({ pid: i.product_id, name: i.product_name, qty: i.quantity }))) === thisBasket;
    });
    let duplicateReason = null;
    if (duplicateOf) {
      const reason = await _askOverForm({
        title: 'Possible duplicate order',
        message: `<strong>${util().sanitize(duplicateOf.order_number)}</strong> was created in the last 30 minutes for <strong>${util().sanitize(custName)}</strong> with the same products and quantities. If this is a genuine separate order, say why.`,
        okLabel: 'Create anyway',
        danger: true,
        reasonLabel: 'Reason this is not a duplicate (required)'
      });
      if (!reason) { restoreBtn(); return; }
      duplicateReason = `${reason} (similar to ${duplicateOf.order_number})`;
    }

    if (_amend) { await _saveAmendment({ saveBtn, restoreBtn, g, orderNumber, custName, matchedCustomerId, warehouseId, platformId,
      orderType, vatTreatment, postcode, address, total, hasAnyOverride, overrideReason }); return; }

    const paymentStatus = document.querySelector('input[name="ims-no-payment"]:checked')?.value || 'unpaid';
    const unpaidExpiresAt = paymentStatus === 'unpaid'
      ? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
      : null;

    const now = new Date();
    const orderDateTime = new Date(`${orderDate}T${now.toTimeString().split(' ')[0]}`);

    let order, error;
    for (let attempt = 0; attempt < 3; attempt++) {
      const result = await db().insert('orders', {
        order_number:       orderNumber,
        platform_id:        platformId,
        order_type:         orderType,
        fulfillment_type:   'retail',
        customer_name:      custName,
        customer_id:        matchedCustomerId,
        warehouse_id:       warehouseId,
        postcode,
        address,
        phone:              g('ims-no-phone'),
        email:              g('ims-no-email'),
        external_ref:       g('ims-no-extref') || orderNumber,
        // Staff notes, plus a line for each allocation release marked on this order.
        notes:              [g('ims-no-notes'), ..._orderItems.flatMap(i => (i.pendingReleases || []).map(r =>
                              `Released ${r.qty} x ${i.productName} from ${r.partner} allocation to fulfil this order.`))]
                              .filter(Boolean).join(' | '),
        status:             'confirmed',
        created_at:         orderDateTime.toISOString(),
        payment_status:     paymentStatus,
        unpaid_expires_at:  unpaidExpiresAt,
        total_value:        total,
        has_price_override: hasAnyOverride,
        vat_treatment:      vatTreatment,
        deal_id:            _activeDeal?.id || null,
        deal_name_snapshot: _activeDeal?.name || null,
        deal_discount_applied: _orderItems.some(i => i.dealApplied) ? 1 : 0,
        created_by:         window.SM.AUTH.user?.id
      });
      order = result.data;
      error = result.error;
      if (!error) break;
      const isDupe = error.code === '23505' || /duplicate key/i.test(error.message||'');
      if (!isDupe) break;
      // Order number collided — bump past it and retry
      orderNumber = util().generateRef('ORD', orderNumber);
    }

    if (error) { ui().toast('Failed to create order: ' + error.message, 'error'); restoreBtn(); return; }
    // Save every item line in ONE insert. Postgres runs a multi-row insert
    // as a single statement, so either every line saves or none do. Before
    // this, each line was saved on its own and the result was never
    // checked, so a failed line vanished while the order still showed as
    // saved. order_items has no company_id column, so the raw client is used.
    const _itemsClient = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    const itemRows = _orderItems.map(item => ({
      order_id:         order.id,
      product_id:       item.productId || null,
      service_id:       item.serviceId || null,
      product_name:     item.productName,
      sku:              item.sku || '',
      quantity:         item.qty || 1,
      unit_price:       item.unitPrice || 0,
      total_price:      (item.qty||1) * (item.unitPrice||0),
      cost_price:       item.costPrice || 0,
      standard_price:   item.standardPrice || null,
      price_overridden: item.isOverride || false,
      override_reason:  item.isOverride ? overrideReason : null,
      override_pct:     item.isOverride ? Math.round((item.overridePct || 0) * 100) / 100 : null
    }));
    const { error: itemsError } = await _itemsClient.from('order_items').insert(itemRows);
    if (itemsError) {
      // Undo the half-saved order. At this point no stock has been reserved,
      // no activity has been logged and nothing else points at the order,
      // so removing the order row is the whole undo.
      const { error: undoError } = await db().remove('orders', order.id);
      if (undoError) {
        ui().toast(`Order ${orderNumber} was created but its items could not be saved, and it could not be removed automatically. Please cancel ${orderNumber} and enter it again. Reason: ${itemsError.message}`, 'error', 15000);
        load();
      } else {
        ui().toast(`Order not saved. The item lines could not be saved, so nothing was recorded. Please try again. Reason: ${itemsError.message}`, 'error', 10000);
      }
      restoreBtn();
      return;
    }
    await logActivity(order.id, 'Order created', '', 'confirmed', `Customer: ${custName}, Value: £${total.toFixed(2)}`);
    if (duplicateReason) await logActivity(order.id, 'Duplicate warning overridden', '', 'confirmed', duplicateReason);

    // Carry out any "release from allocation" marked on this order. This
    // runs only now, once the order and its items have saved, so an order
    // that is abandoned or fails to save never takes stock from a partner.
    // The allocation shrinks (Allocated AND Remaining), so the partner's
    // "Sold" figure (Allocated minus Remaining) stays true: released units
    // were not sold to them.
    const releaseProblems = [];
    for (const item of _orderItems) {
      for (const rel of (item.pendingReleases || [])) {
        if (rel.warehouseId !== warehouseId) continue;
        const { data: alloc } = await db().select('dropship_allocations', {
          select: 'allocated_qty,remaining_qty', eq: { id: rel.allocId }, maybeSingle: true, noCompanyFilter: true
        });
        const actual = Math.min(rel.qty, alloc?.remaining_qty || 0);
        if (actual <= 0) { releaseProblems.push(`${item.productName}: nothing left to release from ${rel.partner}`); continue; }
        const { error: allocErr } = await db().update('dropship_allocations', rel.allocId, {
          allocated_qty: Math.max(0, (alloc.allocated_qty || 0) - actual),
          remaining_qty: Math.max(0, (alloc.remaining_qty || 0) - actual)
        });
        if (allocErr) { releaseProblems.push(`${item.productName}: release from ${rel.partner} failed`); continue; }
        const { data: relSl } = await db().select('stock_levels', {
          eq: { product_id: rel.productId, warehouse_id: rel.warehouseId }, maybeSingle: true, noCompanyFilter: true
        });
        if (relSl) {
          await db().update('stock_levels', relSl.id, {
            reserved_by_allocations: Math.max(0, (relSl.reserved_by_allocations || 0) - actual)
          });
        }
        await logActivity(order.id, 'Released from allocation', '', '', `${actual} x ${item.productName} released from ${rel.partner} allocation`);
        if (actual < rel.qty) releaseProblems.push(`${item.productName}: only ${actual} of ${rel.qty} released from ${rel.partner}`);
      }
    }
    if (releaseProblems.length) {
      ui().toast(`Order ${orderNumber} saved, but some allocation releases did not go through in full: ${releaseProblems.join('; ')}. Please check stock for these products.`, 'warning', 15000);
    }

    // Reserve stock one line at a time. Doing every line at once meant two
    // lines of the same product both read the same starting figure, so one
    // reservation was lost. Failures are now reported instead of ignored.
    const reserveFailed = [];
    for (const item of _orderItems) {
      if (!item.productId) continue;
      const { data: sl, error: slError } = await db().select('stock_levels', {
        eq: { product_id: item.productId, warehouse_id: warehouseId }, maybeSingle: true, noCompanyFilter: true
      });
      if (slError) { reserveFailed.push(item.productName); continue; }
      if (sl) {
        const { error: reserveError } = await db().update('stock_levels', sl.id, {
          reserved_qty: (sl.reserved_qty || 0) + (item.qty || 1)
        });
        if (reserveError) reserveFailed.push(item.productName);
      }
    }
    if (reserveFailed.length) {
      await logActivity(order.id, 'Stock reservation failed', '', '', `Not reserved: ${reserveFailed.join(', ')}`);
      ui().toast(`Order ${orderNumber} saved, but stock could not be reserved for: ${reserveFailed.join(', ')}. Please check stock levels.`, 'warning', 12000);
    }

    

    await db().upsert('order_postcode_history', {
      company_id:    window.SM.AUTH.company?.id,
      postcode,
      customer_name: custName,
      address,
      phone:         g('ims-no-phone'),
      email:         g('ims-no-email'),
      order_count:   1,
      last_used:     new Date().toISOString()
    }, 'company_id,postcode,customer_name');

    _orderItems = [];

    // Batch mode handling
    _batchOrders.push({ id: order.id, orderNumber, customerName: custName, total, paymentStatus });

    if (addAnother) {
      // Save and open fresh form for next order
      ui().toast(`${orderNumber} saved — add next order`, 'success', 3000);
      showNewOrder(true);
    } else {
      // Save and exit — if batch has multiple orders, show payment review
      if (_batchOrders.length > 1) {
        ui().closeModal();
        showBatchPaymentReview();
      } else {
        // Single order — done
        ui().closeModal();
        ui().toast(`Order ${orderNumber} created successfully`, 'success');
        window.SM.EMAIL.send(order.id, 'confirmed');
        _batchOrders = [];
        _batchId = null;
        _activeDeal = null;
        _matchedCustomer = null;
        load();
      }
    }
  }

  function cancelBatch() {
    _batchOrders = [];
    _batchId = null;
    _orderItems = [];
    _activeDeal = null;
    _matchedCustomer = null;
    ui().closeModal();
  }

  async function showBatchPaymentReview() {
    // Generate batch number
    const { data: lastBatch } = await db().select('order_batches', {
      select: 'batch_number', order: { col: 'created_at', asc: false }, limit: 1
    });
    const batchNumber = util().generateRef('BTH', lastBatch?.[0]?.batch_number);

    // Create batch record
    const { data: batch } = await db().insert('order_batches', {
      batch_number: batchNumber,
      company_id:   window.SM.AUTH.company?.id,
      created_by:   window.SM.AUTH.user?.id,
      status:       'open'
    });

    // Link all orders in batch
    if (batch) {
      _batchId = batch.id;
      await Promise.all(_batchOrders.map(o =>
        db().update('orders', o.id, { batch_id: batch.id })
      ));
    }

    const rows = _batchOrders.map((o,i) => `
      <tr>
        <td style="font-weight:600">${util().sanitize(o.orderNumber)}</td>
        <td>${util().sanitize(o.customerName)}</td>
        <td>${util().formatCurrency(o.total)}</td>
        <td>
          <div style="display:flex;gap:6px">
            <label style="display:flex;align-items:center;gap:4px;cursor:pointer;padding:4px 10px;border:1.5px solid var(--ims-border);border-radius:var(--ims-radius);font-size:12px">
              <input type="radio" name="pay-${i}" value="paid" style="accent-color:var(--ims-success)" ${o.paymentStatus==='paid'?'checked':''}>
              <span style="color:var(--ims-success)">Paid</span>
            </label>
            <label style="display:flex;align-items:center;gap:4px;cursor:pointer;padding:4px 10px;border:1.5px solid var(--ims-border);border-radius:var(--ims-radius);font-size:12px">
              <input type="radio" name="pay-${i}" value="cod" style="accent-color:var(--ims-accent)" ${o.paymentStatus==='cod'?'checked':''}>
              <span style="color:var(--ims-accent)">COD</span>
            </label>
            <label style="display:flex;align-items:center;gap:4px;cursor:pointer;padding:4px 10px;border:1.5px solid var(--ims-border);border-radius:var(--ims-radius);font-size:12px">
              <input type="radio" name="pay-${i}" value="unpaid" style="accent-color:var(--ims-danger)" ${o.paymentStatus==='unpaid'?'checked':''}>
              <span style="color:var(--ims-danger)">Unpaid</span>
            </label>
          </div>
        </td>
      </tr>`).join('');

    ui().openModal(`Batch ${batchNumber} — confirm payment status`, `
      <div class="ims__alert ims__alert--info" style="margin-bottom:14px">
        <i class="ti ti-info-circle"></i>
        <span><strong>${_batchOrders.length} orders</strong> saved in batch <strong>${batchNumber}</strong>. Confirm payment status for each order. Paid and COD orders will be available for processing. Unpaid orders will be held for 7 days.</span>
      </div>
      <table class="ims__table">
        <thead><tr><th>Order #</th><th>Customer</th><th>Value</th><th>Payment status</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    `, `
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_ORDERS.confirmBatchPayment()">
        <i class="ti ti-check"></i> Confirm &amp; process batch
      </button>
    `, 'lg');
  }

  async function confirmBatchPayment() {
    const updates = _batchOrders.map((o, i) => {
      const paymentStatus = document.querySelector(`input[name="pay-${i}"]:checked`)?.value || o.paymentStatus;
      const unpaidExpiresAt = paymentStatus === 'unpaid'
        ? new Date(Date.now() + 7*24*60*60*1000).toISOString() : null;
      return { id: o.id, paymentStatus, unpaidExpiresAt };
    });

    await Promise.all(updates.map(u =>
      db().update('orders', u.id, {
        payment_status:    u.paymentStatus,
        unpaid_expires_at: u.unpaidExpiresAt
      })
    ));

    const paid   = updates.filter(u=>u.paymentStatus==='paid').length;
    const cod    = updates.filter(u=>u.paymentStatus==='cod').length;
    const unpaid = updates.filter(u=>u.paymentStatus==='unpaid').length;

    // Update batch status
    if (_batchId) await db().update('order_batches', _batchId, { status: 'processed' });

    ui().closeModal();
    ui().toast(
      `Batch processed — ${paid} paid, ${cod} COD, ${unpaid} unpaid (7-day hold)`,
      'success', 5000
    );

    _batchOrders = [];
    _batchId = null;
    load();
  }


  // ─── ORDER DETAIL ────────────────────────────────────────────────
  // ─── ORDER DETAIL ────────────────────────────────────────────────
  async function openOrder(id) {
    const { data: order, error } = await db().select('orders', {
      select: '*, platforms(name), order_items(*), dropship_customers(business_name), shipments(tracking_number, courier_order_ref, is_return_collection, couriers(name,tracking_url_base,requires_postcode,postcode_param,tracking_form_only)), logistics_jobs(id,job_type,status)',
      eq: { id }, maybeSingle: true, noCompanyFilter: true
    });
    if (error || !order) { ui().toast('Order not found', 'error'); return; }
    // Commission only shown to cost-authorised roles — it reveals margin.
    let _comm = { hasCommission: false };
    if (window.SM.AUTH.canSeeCost?.() && order.platform_id) {
      const rates = await util().getCommissionRates();
      _comm = util().calcCommission(order.total_value, rates[order.platform_id]);
    }
    const isDropship = order.fulfillment_type === 'dropship';
    const isDelayed  = !isDropship && util().isDelayed(order);
    const shipment   = (order.shipments||[]).find(x=>!x.is_return_collection);   // the delivery (not a return collection)
    const trackUrl   = shipment ? util().buildTrackingUrl(shipment.couriers, shipment.tracking_number, order.postcode) : null;
    const TERMINAL   = ['delivered','cancelled','refunded'];
    const isTerminal = TERMINAL.includes(order.status);
    const isReturnable = order.status === 'delivered';
    const isReturnInProgress = ['return_requested','return_received'].includes(order.status);
    const STATUS_SEQUENCE = ['confirmed', 'picking', 'packed'];
    const STATUS_LABELS    = { picking: 'Picking', packed: 'Packed' };
    const canCancelHere    = ['confirmed', 'picking', 'packed'].includes(order.status);
    const seqIdx           = STATUS_SEQUENCE.indexOf(order.status);
    const nextSteps        = seqIdx >= 0 ? STATUS_SEQUENCE.slice(seqIdx + 1) : [];

    // Who created the order: the name recorded at that moment (activity
    // log), plus the account's current name if it has changed since (an
    // account handed over to someone else keeps its history correct).
    const _who = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    const _svcTag = i => !i.service_id ? '' : ` <span class="ims__pill ims__pill--info" style="font-size:10px">Service</span>${i.service_status === 'done' ? ` <span class="ims__pill ims__pill--success" style="font-size:10px">Done</span>${i.service_cost != null ? ` <span style="font-size:11px;color:var(--ims-text-muted)">own-van cost ${util().formatCurrency(i.service_cost)} &middot; margin ${util().formatCurrency((Number(i.unit_price) || 0) * (Number(i.quantity) || 1) - Number(i.service_cost))}</span>` : ''}` : i.service_status === 'not_done' ? ' <span class="ims__pill ims__pill--danger" style="font-size:10px">Not done: refund if paid</span>' : ''}`;
    const { data: createdLog } = await _who.from('order_activity_log').select('user_name,created_at')
      .eq('order_id', order.id).eq('action', 'Order created').order('created_at', { ascending: true }).limit(1);
    let creatorNow = null;
    if (order.created_by) {
      const { data: cu } = await _who.from('users').select('name').eq('id', order.created_by).maybeSingle();
      creatorNow = cu?.name || null;
    }
    const createdName = createdLog?.[0]?.user_name || creatorNow;
    const createdLine = createdName
      ? `${util().sanitize(createdName)}${creatorNow && creatorNow !== createdName ? ` <span style="color:var(--ims-text-muted)">(account now: ${util().sanitize(creatorNow)})</span>` : ''}<div style="font-size:11px;color:var(--ims-text-muted)">${util().formatDateTime(createdLog?.[0]?.created_at || order.created_at)}</div>`
      : '-';

    const html = `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:16px">
        <div><span class="ims__label">Order number</span><div style="font-weight:700;font-size:16px">${util().sanitize(order.order_number)}</div></div>
        <div><span class="ims__label">Status</span><div style="display:flex;gap:8px;align-items:center">
          ${util().pill(isDelayed?'delayed':order.status)}
          ${order.payment_status&&order.order_type!=='wholesale'?util().pill(order.payment_status):''}
        </div></div>
        <div><span class="ims__label">Customer</span><div>${util().sanitize(order.customer_name||'—')}</div></div>
        <div><span class="ims__label">Platform</span><div>${util().sanitize(order.platforms?.name||'-')}</div></div>
        <div><span class="ims__label">Created by</span><div>${createdLine}</div></div>
        <div><span class="ims__label">Order type</span><div>${
          isDropship ? `<span class="ims__pill ims__pill--accent">Dropship</span>`
          : order.order_type==='wholesale' ? '<span class="ims__pill ims__pill--info">Wholesale</span>'
          : '<span class="ims__pill ims__pill--neutral">Retail</span>'
        }</div></div>
        <div><span class="ims__label">Postcode</span><div>${util().sanitize(order.postcode||order.end_postcode||'—')}</div></div>
        <div style="grid-column:1/-1"><span class="ims__label">Delivery address</span><div>${util().sanitize(order.address||order.end_address||'—')}</div></div>
        <div><span class="ims__label">Order date</span><div>${util().formatDate(order.created_at)}</div></div>
        <div><span class="ims__label">Phone</span><div>${util().sanitize(order.phone||order.end_customer_phone||'—')}</div></div>
        ${order.order_type==='retail'?`
          <div><span class="ims__label">Payment</span><div style="display:flex;gap:8px;align-items:center">
            ${util().pill(order.payment_status||'unpaid')}
            ${order.payment_received_at?`<span style="font-size:11px;color:var(--ims-text-muted)">${util().formatDate(order.payment_received_at)}</span>`:''}
            ${order.payment_method?`<span style="font-size:11px;color:var(--ims-text-muted)">${util().sanitize(order.payment_method)}</span>`:''}
          </div></div>
          <div><span class="ims__label">External ref</span><div>${util().sanitize(order.external_ref||'—')}</div></div>`:''}
        ${isDropship && order.dropship_customers ? `
          <div><span class="ims__label">Dropship customer</span><div style="font-weight:600">${util().sanitize(order.dropship_customers.business_name)}</div></div>
          <div><span class="ims__label">End customer</span><div>${util().sanitize(order.end_customer_name||'—')}</div></div>` : ''}
        ${shipment ? `
          <div><span class="ims__label">Courier</span><div>${util().sanitize(shipment.couriers?.name||'—')}</div></div>
          <div><span class="ims__label">Tracking #</span><div>${trackUrl?`<a href="${trackUrl}" target="_blank" class="ims__link">${util().sanitize(shipment.tracking_number||'—')}</a>`:util().sanitize(shipment.tracking_number||'—')}</div></div>
          ${shipment.courier_order_ref ? `<div><span class="ims__label">Courier order no.</span><div>${util().sanitize(shipment.courier_order_ref)}</div></div>` : ''}` : ''}
        ${order.collected_by ? `
          <div><span class="ims__label">Collected by</span><div>${util().sanitize(order.collected_by)}</div></div>
          <div><span class="ims__label">Vehicle reg</span><div>${util().sanitize(order.collection_vehicle||'—')}</div></div>
          <div><span class="ims__label">Collected at</span><div>${util().formatDate(order.collected_at)}</div></div>` : ''}
        ${order.return_reason ? `
          <div><span class="ims__label">Return reason</span><div>${util().sanitize(order.return_reason)}</div></div>
          <div><span class="ims__label">Return requested</span><div>${util().formatDate(order.return_requested_at)}</div></div>` : ''}
        ${order.refund_amount ? `
          <div><span class="ims__label">Refund amount</span><div style="font-weight:600;color:var(--ims-danger)">£${(order.refund_amount||0).toFixed(2)}</div></div>
          <div><span class="ims__label">Refunded at</span><div>${util().formatDate(order.refunded_at)}</div></div>` : ''}
        ${order.notes ? `<div style="grid-column:1/-1"><span class="ims__label">Notes</span><div>${util().sanitize(order.notes)}</div></div>` : ''}
      </div>
      <hr class="ims__divider">
      <strong style="font-size:13px">Items</strong>
      <div class="ims__table-wrap" style="margin-top:8px">
        <table class="ims__table">
          <thead><tr><th>Product</th><th>SKU</th><th>Qty</th><th>Unit price</th><th>Total</th></tr></thead>
          <tbody>
            ${(order.order_items||[]).map(i => `
              <tr><td>${util().sanitize(i.product_name||'-')}${_svcTag(i)}</td><td>${util().sanitize(i.sku||'-')}</td>
              <td>${i.quantity}</td><td>${util().formatCurrency(i.unit_price)}</td><td>${util().formatCurrency(i.total_price)}</td></tr>`).join('')}
            <tr><td colspan="4" style="text-align:right;font-weight:600">${_comm.hasCommission ? 'Gross' : 'Total'}</td><td style="font-weight:700">${util().formatCurrency(order.total_value)}</td></tr>
            ${_comm.hasCommission ? `
            <tr><td colspan="4" style="text-align:right;color:var(--ims-danger)">Marketplace commission</td><td style="color:var(--ims-danger)">−${util().formatCurrency(_comm.commission)}</td></tr>
            <tr><td colspan="4" style="text-align:right;font-weight:700">Net after commission</td><td style="font-weight:800;color:var(--ims-success)">${util().formatCurrency(_comm.net)}</td></tr>` : ''}
          </tbody>
        </table>
      </div>
      <div id="ims-ord-pod-${util().sanitize(order.id)}"></div>
      <div id="ims-ord-col-${util().sanitize(order.id)}"></div>
      ${isDelayed ? `<div class="ims__alert ims__alert--warning" style="margin-top:12px"><i class="ti ti-alert-triangle"></i><span>Delayed — dispatched ${util().daysSince(order.dispatched_at)} days ago with no delivery confirmation.</span></div>` : ''}
      ${isTerminal ? `<div class="ims__alert ims__alert--info" style="margin-top:12px"><i class="ti ti-lock"></i><span>Order is <strong>${order.status}</strong>: status is final.</span></div>` : ''}
      ${order.amended_at ? `<div class="ims__alert ims__alert--info" style="margin-top:12px"><i class="ti ti-pencil"></i><span>Amended on ${util().formatDateTime ? util().formatDateTime(order.amended_at) : util().formatDate(order.amended_at)}. See the activity log for what changed.</span></div>` : ''}`;

    // Amend: while Confirmed, Picking or Packed, or on a manifest whose van
    // has not departed. Anyone with Orders access.
    const amendSt = await _amendState(order);
    const footer = `
      <div class="ims__btn-group" style="flex-wrap:wrap">
        <button class="ims__btn ims__btn--ghost" onclick="window.IMS_ORDERS.printOrder('${util().jsArg(order.id)}')"><i class="ti ti-printer"></i> Print</button>
        <button class="ims__btn ims__btn--ghost" onclick="window.IMS_ORDERS.showActivityLog('${util().jsArg(order.id)}','${util().jsArg(order.order_number)}')" title="Everything done to this order, by whom and when"><i class="ti ti-history"></i> Activity</button>
        ${amendSt.ok ? `<button class="ims__btn ims__btn--primary" onclick="window.IMS_ORDERS.amendOrder('${util().jsArg(order.id)}')"><i class="ti ti-pencil"></i> Amend order</button>` : ''}
        ${window.SM.AUTH.isSuperAdmin() ? `<button class="ims__btn ims__btn--secondary" onclick="window.IMS_ORDERS.editOrderCustomer('${util().jsArg(order.id)}')"><i class="ti ti-edit"></i> Edit customer &amp; refs</button>` : ''}
        ${isDelayed ? `<button class="ims__btn ims__btn--danger" onclick="window.SM.UI.closeModal();window.SM.UI.showPanel('logistics')"><i class="ti ti-truck-delivery"></i> Chase in Logistics</button>` : ''}
        ${order.status==='lost' ? `<div class="ims__alert ims__alert--info" style="margin:0;padding:8px 12px;font-size:12px"><i class="ti ti-file-alert"></i> Claim management for lost orders is handled from <strong>Logistics → Tracking → Lost Orders</strong>.</div>` : ''}
        ${!isDropship && !isTerminal && !isReturnInProgress ? canCancelHere ? `
          ${nextSteps.length ? `
          <select class="ims__select" id="ims-order-status-sel" style="font-size:12px;width:auto">
            <option value="">Update status...</option>
            ${nextSteps.map(s => `<option value="${s}">${STATUS_LABELS[s]}</option>`).join('')}
          </select>
          <button class="ims__btn ims__btn--primary" onclick="window.IMS_ORDERS.updateStatus('${util().jsArg(order.id)}')">Update</button>` : `
          <div class="ims__alert ims__alert--info" style="margin:0;padding:8px 12px;font-size:12px;flex:1 1 100%"><i class="ti ti-list-check"></i> Sent to Logistics as an unassigned job. It will move to Despatched automatically once a courier, own truck, or self-collection is assigned.</div>`}
          <button class="ims__btn ims__btn--danger" onclick="window.IMS_ORDERS.cancelOrder('${util().jsArg(order.id)}','${util().jsArg(order.order_number)}')"><i class="ti ti-x"></i> Cancel order</button>` : `
          <div class="ims__alert ims__alert--info" style="margin:0;padding:8px 12px;font-size:12px;flex:1 1 100%"><i class="ti ti-truck"></i> This order has been despatched — delivery, failed delivery, lost and return updates are now managed from <strong>Logistics</strong>.</div>` : ''}
        ${order.status === 'return_requested' ? `<button class="ims__btn ims__btn--secondary" onclick="window.SM.UI.closeModal();window.IMS_LOGISTICS?.bookCourierCollection('${util().jsArg(order.id)}','${util().jsArg(order.order_number)}')" title="Book a courier to collect this return"><i class="ti ti-truck-delivery"></i> Book courier collection</button>` : ''}
        ${order.status === 'return_requested' ? `<button class="ims__btn ims__btn--secondary" onclick="window.IMS_ORDERS.arrangeVanPickup('${util().jsArg(order.id)}','${util().jsArg(order.order_number)}')" title="Collect this return with our own van"><i class="ti ti-truck-return"></i> Arrange van pick-up</button>` : ''}
        ${isReturnable ? `<button class="ims__btn ims__btn--warning" onclick="window.IMS_ORDERS.showReturnModal('${util().jsArg(order.id)}','${util().jsArg(order.order_number)}',${order.total_value||0})"><i class="ti ti-arrow-back-up"></i> Request return</button>` : ''}
        ${isReturnable ? `<button class="ims__btn ims__btn--secondary" onclick="window.IMS_ORDERS.showRefundNoReturn('${util().jsArg(order.id)}','${util().jsArg(order.order_number)}',${order.total_value||0})"><i class="ti ti-currency-pound"></i> Refund (no return)</button>` : ''}
        ${order.status==='return_requested' ? `<button class="ims__btn ims__btn--info" onclick="window.IMS_ORDERS.markReturnReceived('${util().jsArg(order.id)}')"><i class="ti ti-package-import"></i> Mark return received</button>` : ''}
        ${order.status==='return_received' ? `<button class="ims__btn ims__btn--success" onclick="window.IMS_ORDERS.processRefund('${util().jsArg(order.id)}',${order.total_value||0})"><i class="ti ti-currency-pound"></i> Process refund</button>` : ''}
        ${order.order_type==='retail'&&(order.payment_status==='unpaid'||!order.payment_status)&&order.status!=='cancelled' ? `<button class="ims__btn ims__btn--success" onclick="window.IMS_ORDERS.markPaid('${util().jsArg(order.id)}',${order.total_value||0})"><i class="ti ti-check"></i> Mark paid</button>` : ''}
        ${order.order_type==='wholesale'&&(order.payment_status==='unpaid'||order.payment_status==='partial')&&order.status!=='cancelled' ? `
          <div class="ims__alert ims__alert--info" style="margin:0;padding:8px 12px;font-size:12px;flex:1 1 100%">
            <i class="ti ti-receipt"></i>
            <span>Wholesale orders are paid on credit terms — record the payment in <strong>Accounts → Receivables</strong>, which will mark this order paid automatically.</span>
            <button class="ims__btn ims__btn--secondary ims__btn--sm" style="margin-left:auto" onclick="window.SM.UI.closeModal();window.SM.UI.showPanel('accounts');setTimeout(()=>window.IMS_ACCOUNTS?.switchTab('receivables'),400)"><i class="ti ti-arrow-right"></i> Go to Receivables</button>
          </div>` : ''}
        ${isDropship && !['collected','cancelled'].includes(order.status) ? `
          <button class="ims__btn ims__btn--success" onclick="window.SM.UI.closeModal();window.IMS_DROPSHIP?.showCollectionModal('${util().jsArg(order.id)}','${util().jsArg(order.order_number)}','${util().jsArg(order.dropship_customers?.business_name||'')}')">
            <i class="ti ti-truck-loading"></i> Confirm collection</button>` : ''}
        <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Close</button>
      </div>`;
    ui().openModal(`Order ${order.order_number}`, html, footer, 'lg');
    // Proof of delivery recorded at the stop (photo, signature, name...).
    window.IMS_LOGISTICS?.renderOrderPod?.(order.id, `ims-ord-pod-${order.id}`);
    // A courier collection booked for this return (Mark collected, Edit, Cancel).
    window.IMS_LOGISTICS?.renderReturnCollection?.(order.id, `ims-ord-col-${order.id}`, order.status);
  }

  async function updateStatus(orderId, claimId=null) {
    const sel = document.getElementById('ims-order-status-sel');
    if (!sel?.value) return;
    const newStatus = sel.value;

    // ── Normal status update (Picking / Packed only) ────────────────
    const { data: current } = await db().select('orders', { select: 'status', eq: { id: orderId }, maybeSingle: true, noCompanyFilter: true });
    if (!['confirmed','picking'].includes(current?.status)) {
      ui().toast('This order can no longer be updated from here', 'error');
      return;
    }
    const { error } = await db().update('orders', orderId, { status: newStatus });
    if (error) { ui().toast('Update failed', 'error'); return; }
    await logActivity(orderId, 'Status updated', current?.status||'', newStatus);

    if (newStatus === 'packed') {
      await db().insert('logistics_jobs', {
        order_id: orderId,
        job_type: 'delivery',
        status:   'unassigned'
      });
    }

    ui().closeModal();
    ui().toast('Order status updated', 'success');
    load();
  }

  async function cancelOrder(orderId, orderNumber) {
    ui().confirm(`Cancel order <strong>${orderNumber}</strong>?<br><br>Stock reservations will be released and the order will move to the Cancelled list.`, async () => {
      const { data: cancelOrd } = await db().select('orders', {
        select: 'status,warehouse_id,order_items(product_id,quantity)', eq: { id: orderId }, maybeSingle: true, noCompanyFilter: true
      });
      if (!['confirmed','picking','packed'].includes(cancelOrd?.status)) {
        ui().toast('This order can no longer be cancelled from here', 'error');
        return;
      }
      const items = cancelOrd?.order_items || [];
      const whId = cancelOrd?.warehouse_id;
      // Release one line at a time. Releasing every line at once meant two
      // lines of the same product both read the same starting figure, so
      // only one release was applied and stock stayed wrongly reserved.
      for (const item of (items||[])) {
        if (!item.product_id) continue;
        const { data: sl } = whId
          ? await db().select('stock_levels', { eq: { product_id: item.product_id, warehouse_id: whId }, maybeSingle: true, noCompanyFilter: true })
          : await db().select('stock_levels', { eq: { product_id: item.product_id }, limit: 1, noCompanyFilter: true }).then(r => ({ data: r.data?.[0] }));
        if (sl) await db().update('stock_levels', sl.id, { reserved_qty: Math.max(0,(sl.reserved_qty||0)-(item.quantity||0)) });
      }
      // Remove any Logistics job tied to this order — orders that reached
      // 'packed' have one, and Logistics' own cancel path always deletes it.
      // Without this, a stale unassigned job keeps pointing at an order that
      // no longer exists in any actionable state.
      const _cancelClient = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
      await _cancelClient.from('logistics_jobs').delete().eq('order_id', orderId);

      await db().update('orders', orderId, { status: 'cancelled' });
      await logActivity(orderId, 'Order cancelled', cancelOrd?.status||'', 'cancelled', 'Cancelled from Order Management');
      ui().closeModal();
      ui().toast('Order cancelled — stock released', 'success');
      load();
    }, 'Cancel order', true);
  }

  async function markPaid(orderId, totalValue) {
    ui().openModal('Mark order as paid', `
      <div class="ims__alert ims__alert--info" style="margin-bottom:14px"><i class="ti ti-currency-pound"></i>
        <span>Record payment of <strong>${util().formatCurrency(totalValue)}</strong> for this order.</span></div>
      <div class="ims__form-grid">
        <div class="ims__form-group"><label class="ims__label ims__label--req">Payment method</label>
          <select class="ims__select" id="ims-pay-method">
            <option value="bank_transfer">Bank transfer</option><option value="card">Card payment</option>
            <option value="cash">Cash</option><option value="cheque">Cheque</option>
            <option value="paypal">PayPal</option><option value="other">Other</option>
          </select></div>
        <div class="ims__form-group"><label class="ims__label ims__label--req">Payment date</label>
          <input class="ims__input" type="date" id="ims-pay-date" value="${new Date().toISOString().split('T')[0]}"></div>
        <div class="ims__form-group ims__form-group--full"><label class="ims__label">Amount received (£)</label>
          <input class="ims__input" type="number" step="0.01" id="ims-pay-amount" value="${totalValue.toFixed(2)}">
          <span class="ims__input-hint">Change to a lower amount if partial payment received</span></div>
        <div class="ims__form-group ims__form-group--full"><label class="ims__label">Payment reference</label>
          <input class="ims__input" type="text" id="ims-pay-ref" placeholder="e.g. bank transfer ref, receipt number"></div>
      </div>`,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--success" onclick="window.IMS_ORDERS.confirmPayment('${util().jsArg(orderId)}',${totalValue})">
        <i class="ti ti-check"></i> Confirm payment</button>`,'sm');
  }

  async function confirmPayment(orderId, totalValue) {
    const method  = document.getElementById('ims-pay-method')?.value;
    const dateVal = document.getElementById('ims-pay-date')?.value;
    const amount  = parseFloat(document.getElementById('ims-pay-amount')?.value)||0;
    const ref     = document.getElementById('ims-pay-ref')?.value?.trim();
    if (!amount) { ui().toast('Please enter the amount received', 'error'); return; }
    const isPartial = amount < totalValue;
    await db().update('orders', orderId, { payment_status: isPartial?'partial':'paid', payment_received_at: new Date(`${dateVal}T00:00:00`).toISOString(), payment_method: method });
    const { data: ord } = await db().select('orders', { select: 'order_number,payment_status', eq: { id: orderId }, maybeSingle: true, noCompanyFilter: true });
    await db().insert('transactions', { date: dateVal, type: 'income', category: 'Sales revenue', description: `Payment received — ${ord?.order_number||'order'} (${method})`, amount, reference: ref||ord?.order_number });
    await logActivity(orderId, 'Payment recorded', ord?.payment_status||'unpaid', isPartial?'partial':'paid', `£${amount.toFixed(2)} via ${method}`);
    ui().closeModal(); ui().toast(`Payment of ${util().formatCurrency(amount)} recorded${isPartial?' — partial payment':''}`,'success',4000); load();
  }

  async function showReturnModal(orderId, orderNumber, totalValue) {
    ui().openModal(`Request return: ${orderNumber}`,`
      <div class="ims__alert ims__alert--warning" style="margin-bottom:14px"><i class="ti ti-alert-triangle"></i>
        <span>Initiating a return changes status to <strong>Return Requested</strong>. Stock restocked once return received and inspected.</span></div>
      <div class="ims__form-grid">
        <div class="ims__form-group ims__form-group--full"><label class="ims__label ims__label--req">Return reason</label>
          <select class="ims__select" id="ims-ret-reason"><option value="">Select reason...</option>
            <option value="damaged_in_transit">Damaged in transit</option><option value="wrong_item_sent">Wrong item sent</option>
            <option value="not_as_described">Not as described</option><option value="customer_changed_mind">Customer changed mind</option>
            <option value="faulty_product">Faulty product</option><option value="other">Other</option>
          </select></div>
        <div class="ims__form-group"><label class="ims__label ims__label--req">Return method</label>
          <select class="ims__select" id="ims-ret-method">
            <option value="courier_collection">Courier collection (we arrange)</option>
            <option value="customer_dropoff">Customer drops off</option>
            <option value="customer_courier">Customer arranges courier</option>
            <option value="own_van_pickup">Collect with our van</option>
          </select></div>
        <div class="ims__form-group"><label class="ims__label ims__label--req">Refund amount (£)</label>
          <input class="ims__input" type="number" step="0.01" id="ims-ret-refund" value="${totalValue.toFixed(2)}">
          <span class="ims__input-hint">Adjust for restocking fee if applicable</span></div>
        <div class="ims__form-group ims__form-group--full"><label class="ims__label">Notes</label>
          <input class="ims__input" type="text" id="ims-ret-notes" placeholder="Additional details about the return"></div>
      </div>`,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--warning" onclick="window.IMS_ORDERS.submitReturn('${util().jsArg(orderId)}',${totalValue})">
        <i class="ti ti-arrow-back-up"></i> Initiate return</button>`,'sm');
  }

  // Collect a requested return with our own van (it joins the Own Truck Queue).
  async function arrangeVanPickup(orderId, orderNumber) {
    if (!window.IMS_LOGISTICS?.arrangeVanPickup) { ui().toast('Logistics is not available', 'error'); return; }
    const done = await window.IMS_LOGISTICS.arrangeVanPickup(orderId, orderNumber);
    if (done) openOrder(orderId);
  }

  async function submitReturn(orderId, totalValue) {
    const reason = document.getElementById('ims-ret-reason')?.value;
    const refund = parseFloat(document.getElementById('ims-ret-refund')?.value)||totalValue;
    const notes  = document.getElementById('ims-ret-notes')?.value?.trim();
    if (!reason) { ui().toast('Please select a return reason', 'error'); return; }
    // The return method is now saved (it used to be asked for, then lost).
    const method = document.getElementById('ims-ret-method')?.value || null;
    const { error } = await db().update('orders', orderId, { status: 'return_requested', return_reason: reason, return_requested_at: new Date().toISOString(), refund_amount: refund, notes: notes||null, return_method: method });
    if (error) { ui().toast('Failed: ' + error.message, 'error'); return; }
    await logActivity(orderId, 'Return initiated', 'delivered', 'return_requested', reason);
    ui().closeModal();
    if (method === 'own_van_pickup' && window.IMS_LOGISTICS?.arrangeVanPickup) {
      // Collected by our van: the pick-up goes straight into the Own Truck Queue.
      await window.IMS_LOGISTICS.arrangeVanPickup(orderId, null);
    } else if (method === 'courier_collection' && window.IMS_LOGISTICS?.bookCourierCollection) {
      // Collected by a courier: book it now (courier, date, charge, email).
      load();
      return window.IMS_LOGISTICS.bookCourierCollection(orderId, null);
    } else {
      ui().toast('Return initiated: awaiting the item back at the warehouse', 'info', 4000);
    }
    load();
  }

  async function markReturnReceived(orderId) {
    ui().confirm('Confirm that the returned item has been received and inspected at the warehouse?', async () => {
      await db().update('orders', orderId, { status: 'return_received', return_received_at: new Date().toISOString() });
      await logActivity(orderId, 'Return received', 'return_requested', 'return_received');
      ui().toast('Return received — ready to process refund', 'success'); load();
    }, 'Confirm received', false);
  }

  // ─── REFUNDS: shared safety checks ──────────────────────────────
  // Both refund paths below used to run every step without checking any
  // of them, then always say "Refund processed". Now:
  //  - an order that is already refunded is refused (no second refund);
  //  - the order is marked refunded FIRST, and only while it is not
  //    already refunded, so a double click or two screens at once cannot
  //    both go through; if that step fails, nothing else happens;
  //  - every later step is checked, and anything that did not work is
  //    named in the message so it can be put right by hand;
  //  - whichever list is open (Orders, or Logistics > Tracking and Lost
  //    Orders) is refreshed, so the order does not linger on screen.

  function _authClient() {
    return window.supabase.createClient(
      window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY,
      { global: { headers: { Authorization: `Bearer ${window.SM.AUTH.session?.access_token}` } } }
    );
  }

  // Marks the order refunded, but only if it is not refunded already.
  // Returns { ok: true } if this call did it, { already: true } if it was
  // already refunded, or { error } if the update failed.
  async function _markRefunded(orderId, extraFields) {
    const { data, error } = await _authClient().from('orders')
      .update({ status: 'refunded', payment_status: 'refunded', ...extraFields })
      .eq('id', orderId)
      .or('payment_status.is.null,payment_status.neq.refunded')
      .select('id');
    if (error) return { error };
    if (!data?.length) return { already: true };
    return { ok: true };
  }

  function _refreshAfterRefund() {
    load();
    if (window.SM.UI.activePanel === 'logistics') window.IMS_LOGISTICS?.loadTracking?.();
  }

  // The money steps, run after the order is marked refunded. Each one is
  // checked. Returns a list of plain-English problems (empty if all ok).
  async function _refundMoneySteps(orderId, orderNumber, amount, dateVal, creditNoteReason, refundDescription) {
    const problems = [];
    // If a sales invoice was already generated for this order, issue a
    // credit note against it rather than leaving it sitting there as if
    // still owed.
    let noteNum = null;
    try {
      noteNum = await window.IMS_ACCOUNTS.reverseOrderInvoice(orderId, amount, dateVal, creditNoteReason);
    } catch (e) {
      problems.push(`the credit note could not be issued (${e.message})`);
    }
    // If a wholesale receivable was raised on delivery, clear it, so
    // Accounts does not keep chasing payment on a refunded order.
    const { error: arErr } = await _authClient().from('accounts_receivable').delete().eq('order_id', orderId);
    if (arErr) problems.push(`the amount owed in Accounts could not be cleared (${arErr.message})`);
    // The refund is recorded ONCE, as a reduction of sales (standard
    // practice, as in Xero and Sage). When a credit note was issued above,
    // it already recorded that entry; otherwise record it here. (It used to
    // add a "Refunds" expense as well, counting invoiced refunds twice.)
    if (!noteNum) {
      const { error: txErr } = await db().insert('transactions', {
        date: dateVal, type: 'income', category: 'Sales returns and refunds',
        description: refundDescription, amount: -amount, vat_amount: 0, reference: orderNumber
      });
      if (txErr) problems.push(`the refund could not be recorded in Accounts (${txErr.message})`);
    }
    return problems;
  }

  async function processRefund(orderId, totalValue) {
    const { data: ord } = await db().select('orders', {
      select: 'order_number,refund_amount,return_reason,status,payment_status',
      eq: { id: orderId }, maybeSingle: true, noCompanyFilter: true
    });
    if (!ord) { ui().toast('Order not found', 'error'); return; }
    const orderNumber = ord.order_number || '';
    if (ord.payment_status === 'refunded' || ord.status === 'refunded') {
      ui().toast(`${orderNumber} has already been refunded. Nothing was changed.`, 'warning', 6000);
      _refreshAfterRefund();
      return;
    }
    const refundAmt = ord.refund_amount || totalValue || 0;
    const reason = ord.return_reason || 'return';
    ui().confirm(`Process refund of <strong>${util().formatCurrency(refundAmt)}</strong>?<br><br>This will mark the order as Refunded, restock items, and record a refund transaction.`, async () => {
      const dateVal = new Date().toISOString().split('T')[0];
      const mark = await _markRefunded(orderId, { refunded_at: new Date().toISOString() });
      if (mark.error) {
        ui().closeModal();
        ui().toast(`Refund not processed: ${orderNumber} could not be marked as refunded (${mark.error.message}). Nothing was changed.`, 'error', 10000);
        return;
      }
      if (mark.already) {
        ui().closeModal();
        ui().toast(`${orderNumber} has already been refunded. Nothing was changed.`, 'warning', 6000);
        _refreshAfterRefund();
        return;
      }
      await logActivity(orderId, 'Refund processed', ord.status || '', 'refunded', `£${refundAmt.toFixed(2)}`);

      const problems = [];
      // Restock. smartRestock (ims-logistics.js) runs at most once per
      // order, so this is safe even if the goods were already put back
      // when the return was received.
      try {
        await window.IMS_LOGISTICS.smartRestock(orderId, orderNumber, `Refund processed (${reason})`);
      } catch (e) {
        problems.push(`stock could not be updated (${e.message})`);
      }
      problems.push(...await _refundMoneySteps(orderId, orderNumber, refundAmt, dateVal,
        `Refund: order returned (${reason})`, `Refund: ${orderNumber} (${reason})`));

      ui().closeModal();
      if (problems.length) {
        ui().toast(`Refund of ${util().formatCurrency(refundAmt)} recorded on ${orderNumber}, but ${problems.join('; ')}. Please put this right by hand.`, 'warning', 15000);
      } else {
        ui().toast(`Refund of ${util().formatCurrency(refundAmt)} processed`, 'success', 5000);
      }
      _refreshAfterRefund();
    }, 'Process refund', false);
  }

  // ─── REFUND WITHOUT RETURN ────────────────────────────────────────
  // Covers the case where the customer keeps the item and still gets
  // refunded. No restock, since nothing physical is coming back.
  function showRefundNoReturn(orderId, orderNumber, totalValue) {
    const todayStr = new Date().toISOString().split('T')[0];
    ui().openModal(`Refund without return: ${orderNumber}`, `
      <div class="ims__alert ims__alert--warning" style="margin-bottom:14px">
        <i class="ti ti-alert-triangle"></i>
        <span>Customer keeps the item, so <strong>no stock will be restocked</strong>. Use this for goodwill refunds, partial-fault compensation, or any case where the goods are not coming back.</span>
      </div>
      <div class="ims__form-grid">
        <div class="ims__form-group"><label class="ims__label ims__label--req">Refund amount (£)</label><input class="ims__input" type="number" step="0.01" min="0" id="ims-rnr-amount" value="${(totalValue||0).toFixed(2)}"></div>
        <div class="ims__form-group"><label class="ims__label ims__label--req">Date</label><input class="ims__input" type="date" id="ims-rnr-date" value="${todayStr}" max="${todayStr}"></div>
        <div class="ims__form-group ims__form-group--full"><label class="ims__label ims__label--req">Reason</label><input class="ims__input" type="text" id="ims-rnr-reason" placeholder="e.g. Goodwill gesture, minor cosmetic fault, short delivery..."></div>
      </div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--success" onclick="window.IMS_ORDERS.confirmRefundNoReturn('${util().jsArg(orderId)}','${util().jsArg(orderNumber)}')"><i class="ti ti-currency-pound"></i> Confirm refund</button>
    `,'sm');
  }

  async function confirmRefundNoReturn(orderId, orderNumber) {
    const amount = parseFloat(document.getElementById('ims-rnr-amount')?.value)||0;
    const dateVal = document.getElementById('ims-rnr-date')?.value;
    const reason = document.getElementById('ims-rnr-reason')?.value?.trim();
    if(!amount||amount<=0){ ui().toast('Please enter a valid amount','error'); return; }
    if(!dateVal){ ui().toast('Please select a date','error'); return; }
    if(!reason){ ui().toast('Please enter a reason','error'); return; }

    const { data: ord } = await db().select('orders', {
      select: 'status,payment_status', eq: { id: orderId }, maybeSingle: true, noCompanyFilter: true
    });
    if (ord?.payment_status === 'refunded' || ord?.status === 'refunded') {
      ui().closeModal();
      ui().toast(`${orderNumber} has already been refunded. Nothing was changed.`, 'warning', 6000);
      _refreshAfterRefund();
      return;
    }

    // No restock here: the customer is keeping the item.
    const mark = await _markRefunded(orderId, {
      refunded_at: new Date(dateVal).toISOString(),
      return_reason: `Refund without return: ${reason}`
    });
    if (mark.error) {
      ui().toast(`Refund not processed: ${orderNumber} could not be marked as refunded (${mark.error.message}). Nothing was changed.`, 'error', 10000);
      return;
    }
    if (mark.already) {
      ui().closeModal();
      ui().toast(`${orderNumber} has already been refunded. Nothing was changed.`, 'warning', 6000);
      _refreshAfterRefund();
      return;
    }
    await logActivity(orderId, 'Refund without return', ord?.status || 'delivered', 'refunded', `£${amount.toFixed(2)}: ${reason}`);

    const problems = await _refundMoneySteps(orderId, orderNumber, amount, dateVal,
      `Refund without return: ${reason}`, `Refund without return: ${orderNumber} (${reason})`);

    ui().closeModal();
    if (problems.length) {
      ui().toast(`Refund of ${util().formatCurrency(amount)} recorded on ${orderNumber}, but ${problems.join('; ')}. Please put this right by hand.`, 'warning', 15000);
    } else {
      ui().toast(`Refund of ${util().formatCurrency(amount)} processed. Customer kept the item.`, 'success', 5000);
    }
    _refreshAfterRefund();
  }


  // ─── FOLLOW UP ───────────────────────────────────────────────────
  // Courier follow-ups are now handled solely in Logistics → Tracking.
  // Order Management previously had its own independent followUp()/
  // sendFollowUp() pair writing to a separate `courier_followups` table,
  // which neither Logistics nor the dashboard could see — so the same
  // parcel could be chased twice with no shared history. Both functions
  // and their exports have been removed; delayed orders now link across
  // to Logistics instead. The `courier_followups` table is retained for
  // historical records and is still read by the dashboard's chased check.

  // ─── EDIT ORDER CUSTOMER (Super Admin only) ──────────────────────
  // Deliberately limited to customer details and customer linkage — it does
  // NOT touch line items, quantities or prices, because changing those after
  // stock has been reserved/picked/dispatched would silently corrupt
  // inventory unless the reservations are correctly unwound. That's a
  // separate, larger job.
  let _editCustPicked = null;

  async function editOrderCustomer(orderId) {
    if (!window.SM.AUTH.isSuperAdmin()) { ui().toast('Only Super Admin can edit orders', 'error'); return; }
    const { data: order } = await db().select('orders', {
      eq: { id: orderId }, maybeSingle: true, noCompanyFilter: true
    });
    if (!order) { ui().toast('Order not found', 'error'); return; }
    _editCustPicked = order.customer_id || null;

    const isWholesale = order.order_type === 'wholesale';

    ui().openModal(`Edit customer & references — ${util().sanitize(order.order_number)}`, `
      <div class="ims__alert ims__alert--warning" style="margin-bottom:12px">
        <i class="ti ti-alert-triangle"></i>
        <span>Editing customer details only. Line items, quantities and pricing cannot be changed here.</span>
      </div>
      ${isWholesale ? `
      <div class="ims__form-group" style="margin-bottom:12px">
        <label class="ims__label ims__label--req">Link to registered customer</label>
        <div style="position:relative">
          <input class="ims__input" type="text" id="ims-eoc-search" placeholder="Start typing a business name or postcode, then pick from the list" autocomplete="off"
            value="${util().sanitize(order.customer_name || '')}"
            oninput="window.IMS_ORDERS.searchEditCustomer(this.value)">
          <div id="ims-eoc-drop" class="ims__ac-dropdown"></div>
        </div>
        <span class="ims__input-hint" id="ims-eoc-linked">${order.customer_id
          ? '<span style="color:var(--ims-success)"><i class="ti ti-link"></i> Currently linked to a customer record</span>'
          : '<span style="color:var(--ims-danger)"><i class="ti ti-unlink"></i> Not linked — you must <strong>pick a customer from this list</strong>. Typing a name below does not link it.</span>'}</span>
      </div>` : ''}
      <div class="ims__form-grid">
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label ims__label--req">Customer name</label>
          <input class="ims__input" type="text" id="ims-eoc-name" value="${util().sanitize(order.customer_name || '')}"
            ${isWholesale ? 'readonly style="background:var(--ims-surface2)"' : ''}>
          ${isWholesale ? '<span class="ims__input-hint">Set automatically from the registered customer you pick above.</span>' : ''}
        </div>
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label">Address</label>
          <textarea class="ims__textarea" id="ims-eoc-address" rows="2">${util().sanitize(order.address || '')}</textarea>
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Postcode</label>
          <input class="ims__input" type="text" id="ims-eoc-postcode" value="${util().sanitize(order.postcode || '')}">
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Phone</label>
          <input class="ims__input" type="text" id="ims-eoc-phone" value="${util().sanitize(order.phone || '')}">
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Email</label>
          <input class="ims__input" type="email" id="ims-eoc-email" value="${util().sanitize(order.email || '')}">
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Marketplace / external ref</label>
          <input class="ims__input" type="text" id="ims-eoc-extref" value="${util().sanitize(order.external_ref || '')}">
        </div>
      </div>
    `, `
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_ORDERS.saveOrderCustomer('${util().jsArg(orderId)}')"><i class="ti ti-check"></i> Save changes</button>
    `, 'lg');

    // Pre-filling an input's `value` does not fire `oninput` — that only
    // happens when a human types — so the pre-filled search sat there doing
    // nothing. Run the lookup once after the modal is in the DOM so matching
    // customers are offered immediately.
    if (isWholesale && order.customer_name) {
      setTimeout(() => searchEditCustomer(order.customer_name), 150);
    }
  }

  async function searchEditCustomer(query) {
    const drop = document.getElementById('ims-eoc-drop');
    if (!drop) return;
    if (!query || query.trim().length < 2) { drop.style.display = 'none'; return; }
    const q = query.trim();
    const [byName, byPostcode] = await Promise.all([
      // Deactivated customers are not offered.
      db().select('customers', { ilike: { business_name: q }, eq: { is_active: true }, select: 'id,business_name,address,postcode,phone,email', limit: 5 }),
      db().select('customers', { ilike: { postcode: q }, eq: { is_active: true }, select: 'id,business_name,address,postcode,phone,email', limit: 5 })
    ]);
    const combined = [...(byName.data || []), ...(byPostcode.data || [])]
      .filter((c, i, arr) => arr.findIndex(x => x.id === c.id) === i).slice(0, 8);
    if (!combined.length) { drop.style.display = 'none'; return; }
    drop.innerHTML = combined.map(c => `
      <div class="ims__ac-item" onmousedown='window.IMS_ORDERS.selectEditCustomer(${JSON.stringify(c).replace(/'/g, "&#39;")})'>
        <strong>${util().sanitize(c.business_name)}</strong>
        <div style="font-size:11px;color:var(--ims-text-muted)">${util().sanitize(c.postcode || '')}</div>
      </div>`).join('');
    drop.style.display = 'block';
  }

  function selectEditCustomer(c) {
    _editCustPicked = c.id;
    const f = (id, v) => { const el = document.getElementById(id); if (el) el.value = v || ''; };
    f('ims-eoc-name', c.business_name);
    f('ims-eoc-address', c.address);
    f('ims-eoc-postcode', c.postcode);
    f('ims-eoc-phone', c.phone);
    f('ims-eoc-email', c.email);
    const drop = document.getElementById('ims-eoc-drop');
    if (drop) drop.style.display = 'none';
    const search = document.getElementById('ims-eoc-search');
    if (search) search.value = c.business_name;
    const linked = document.getElementById('ims-eoc-linked');
    if (linked) linked.innerHTML = `<span style="color:var(--ims-success)"><i class="ti ti-link"></i> Will be linked to <strong>${util().sanitize(c.business_name)}</strong></span>`;
  }

  async function saveOrderCustomer(orderId) {
    if (!window.SM.AUTH.isSuperAdmin()) { ui().toast('Only Super Admin can edit orders', 'error'); return; }
    const g = id => document.getElementById(id)?.value?.trim() || '';
    const name = g('ims-eoc-name');
    if (!name) { ui().toast('Customer name is required', 'error'); return; }

    const { data: order } = await db().select('orders', {
      eq: { id: orderId }, maybeSingle: true, noCompanyFilter: true
    });
    if (!order) { ui().toast('Order not found', 'error'); return; }

    const { error } = await db().update('orders', orderId, {
      customer_name: name,
      address:       g('ims-eoc-address') || null,
      postcode:      g('ims-eoc-postcode') || null,
      phone:         g('ims-eoc-phone') || null,
      email:         g('ims-eoc-email') || null,
      external_ref:  g('ims-eoc-extref') || null,
      customer_id:   _editCustPicked || null
    });
    if (error) { ui().toast('Failed to save: ' + error.message, 'error'); return; }

    // If this is a delivered wholesale order that now finally has a customer
    // linked, create the receivable that was skipped at delivery time —
    // otherwise it stays permanently unpaid with no way to clear it. Only
    // if the order is genuinely still unpaid, though — if it was already
    // settled (e.g. genuine COD collected on delivery), no receivable is
    // needed regardless of the newly-linked customer's default terms.
    let arCreated = false;
    if (_editCustPicked && order.order_type === 'wholesale' && order.status === 'delivered') {
      const { data: existingAR } = await db().select('accounts_receivable', {
        eq: { order_id: orderId }, maybeSingle: true, noCompanyFilter: true
      });
      if (!existingAR) {
        const { data: cust } = await db().select('customers', { select: 'credit_terms', eq: { id: _editCustPicked }, maybeSingle: true, noCompanyFilter: true });
        const dueDate = util().resolveReceivableDueDate(order.payment_status, cust?.credit_terms);
        if (dueDate) {
          const { error: arErr } = await db().insert('accounts_receivable', {
            order_id:    orderId,
            customer_id: _editCustPicked,
            amount:      order.total_value || 0,
            paid_amount: 0,
            status:      'unpaid',
            invoice_ref: order.order_number,
            due_date:    dueDate
          });
          if (!arErr) arCreated = true;
        }
      }
    }

    await logActivity(orderId, null, null, 'customer_edited', `Customer details edited by ${window.SM.AUTH.user?.name || 'Super Admin'}`);

    ui().closeModal();
    ui().toast(
      arCreated
        ? `Customer updated — receivable created in Accounts for ${util().formatCurrency(order.total_value || 0)}`
        : 'Customer details updated',
      'success', arCreated ? 6000 : 3000
    );

    // The same order appears in four separate views, each with its own
    // independent render, across two different modules:
    //   #ims-orders-tbody        → Order Management list      (load)
    //   #ims-hist-tbody          → Order History              (loadHistory)
    //   #ims-tracking-table-wrap → Logistics → Tracking       (IMS_LOGISTICS.loadTracking)
    //   #ims-lost-table-wrap     → Logistics → Lost Orders    (IMS_LOGISTICS.loadLostOrders)
    // Each render function bails out early if its own container isn't
    // mounted, so calling them all is safe — whichever view is actually on
    // screen refreshes, the rest no-op. Refreshing only the Orders views
    // meant an edit made from Logistics didn't show until a full reload.
    if (document.getElementById('ims-orders-tbody'))        load();
    if (document.getElementById('ims-hist-tbody'))          loadHistory();
    if (document.getElementById('ims-tracking-table-wrap')) window.IMS_LOGISTICS?.loadTracking?.();
    if (document.getElementById('ims-lost-table-wrap'))     window.IMS_LOGISTICS?.loadLostOrders?.();
  }

  // ─── PRINT ORDER ─────────────────────────────────────────────────
  async function printOrder(orderId) {
    const { data: order } = await db().select('orders', {
      select: '*, platforms(name)',
      eq: { id: orderId }, maybeSingle: true, noCompanyFilter: true
    });
    if (!order) return;

    // order_items RLS requires service role — embed in db() with noCompanyFilter
    const { data: _printOrd } = await db().select('orders', { select: 'order_items(*)', eq: { id: orderId }, maybeSingle: true, noCompanyFilter: true });
    const orderItems = _printOrd?.order_items || [];

    const isDropship = order.fulfillment_type === 'dropship';
    const docTitle = isDropship ? 'Dropship Order' : order.order_type === 'wholesale' ? 'Sales Order' : 'Order Confirmation';
    const statusLabels = {
      confirmed:'Confirmed', picking:'Picking', packed:'Packed', dispatched:'Dispatched',
      delivered:'Delivered', failed:'Failed delivery', lost:'Lost', cancelled:'Cancelled',
      return_requested:'Return Requested', return_received:'Return Received', refunded:'Refunded'
    };
    const statusLabel = statusLabels[order.status] || order.status || '—';

    const itemRows = (orderItems||[]).map(i=>[
      util().sanitize(i.product_name||'—'),
      util().sanitize(i.sku||'—'),
      String(i.quantity),
      `£${(i.unit_price||0).toFixed(2)}`,
      `£${(i.total_price||0).toFixed(2)}`
    ]);
    const itemTableHtml=window.PRINT.table({
      colWidths:['38%','18%','10%','16%','18%'],
      columns:[
        {label:'Product'},{label:'SKU'},{label:'Qty',align:'r'},
        {label:'Unit price',align:'r'},{label:'Total',align:'r'}
      ],
      rows:itemRows
    });

    const headerHtml=window.PRINT.header({
      title:docTitle,
      docRef:order.order_number,
      rightLabel:'Deliver to',
      rightName:util().sanitize(order.customer_name||order.end_customer_name||'—'),
      rightLines:[
        util().sanitize(order.address||order.end_address||''),
        util().sanitize(order.postcode||order.end_postcode||''),
        util().sanitize(order.phone||order.end_customer_phone||''),
        '',
        `<strong>Order date:</strong> ${util().formatDate(order.created_at)}`,
        `<strong>Platform:</strong> ${util().sanitize(order.platforms?.name||'—')}`,
        `<strong>Ref:</strong> ${util().sanitize(order.external_ref||'—')}`,
        `<strong>Status:</strong> ${statusLabel}`
      ]
    });

    const summaryHtml=`
      <table style="width:260px;margin-left:auto;border-collapse:collapse;font-size:10pt">
        <tr class="tr"><td style="padding:3px 6px"><strong>Order total</strong></td><td class="r" style="padding:3px 6px"><strong>£${(order.total_value||0).toFixed(2)}</strong></td></tr>
      </table>`;

    const notesHtml = order.notes
      ? `<div style="margin-top:12px;font-size:9pt"><strong>Notes:</strong> ${util().sanitize(order.notes)}</div>`
      : '';

    const page=`
      ${headerHtml}
      <div class="st">Order items</div>
      <hr class="rule">
      ${itemTableHtml}
      <hr class="rule">
      ${summaryHtml}
      <hr class="rule-dbl" style="width:260px;margin-left:auto;margin-top:2px">
      ${notesHtml}
      ${window.PRINT.pageFooter(['StockMaestro © 2026 ABSA Digital','Order '+(order.order_number||'')],1,1)}
    `;

    window.PRINT.renderDocument([page],`Order ${order.order_number}`);
  }

  // ─── IMPORT ORDERS ───────────────────────────────────────────────
  function showImport() {
    ui().openModal('Import orders', `
      <div class="ims__alert ims__alert--info"><i class="ti ti-info-circle"></i>
        <span>Upload a Shopify CSV export or a StockMaestro orders template. Download the template first to see the required format. Orders import with your specified date, reserve stock against the chosen warehouse, and land as Confirmed — ready to move through Picking → Packed → Logistics like any other order.</span>
      </div>
      <div class="ims__btn-group" style="margin-bottom:16px">
        <button class="ims__btn ims__btn--secondary" onclick="window.IMS_ORDERS.downloadTemplate()"><i class="ti ti-download"></i> Download template</button>
      </div>
      <div class="ims__form-group">
        <label class="ims__label">Select CSV file</label>
        <input type="file" accept=".csv" id="ims-import-file" class="ims__input" onchange="window.IMS_ORDERS.previewImport(this)">
      </div>
      <div id="ims-import-preview"></div>
    `, `
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" id="ims-import-commit-btn" style="display:none" onclick="window.IMS_ORDERS.commitImport()"><i class="ti ti-check"></i> Import orders</button>
    `, 'lg');
  }

  function downloadTemplate() {
    util().exportCSV(
      ['Order date (YYYY-MM-DD)','Customer name','Postcode','Address','Phone','Email','External ref','Platform','Warehouse','Payment status (Paid/COD/Unpaid)','Product name','SKU','Quantity','Unit price','Notes'],
      [
        ['2026-05-12','John Smith','SW1A 1AA','1 Example Street London','07700900000','john@email.com','BATCH-0001','Amazon UK','Wern Industrial Estate','Paid','Example Chair','CHR-001','2','35.00',''],
        ['2026-05-12','John Smith','SW1A 1AA','1 Example Street London','07700900000','john@email.com','BATCH-0001','Amazon UK','Wern Industrial Estate','Paid','Example Table','TBL-002','1','89.00','Second item, same order — same External ref']
      ],
      'orders_template'
    );
  }

  let _importRows = [];

  // Normalizes a CSV date value to YYYY-MM-DD. Accepts ISO (YYYY-MM-DD)
  // as-is, and UK-format DD/MM/YYYY (1 or 2 digit day/month) — Excel
  // commonly reformats date-like cells to the system locale (DD/MM/YYYY
  // for UK Excel) even if the cell was originally typed as YYYY-MM-DD,
  // so both must be accepted to avoid rejecting genuinely valid dates.
  function _importParseDate(raw) {
    const val = (raw || '').trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
    const m = val.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m) {
      const day = parseInt(m[1], 10), month = parseInt(m[2], 10), year = parseInt(m[3], 10);
      if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
        return `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
      }
    }
    return '';
  }

  async function previewImport(input) {
    const file = input.files?.[0];
    if (!file) return;
    const text = await file.text();
    const { headers, rows } = util().parseCSV(text);
    const preview = document.getElementById('ims-import-preview');
    const commitBtn = document.getElementById('ims-import-commit-btn');

    if (!rows.length) {
      if (preview) preview.innerHTML = '<div class="ims__alert ims__alert--danger"><i class="ti ti-alert-circle"></i><span>No data rows found in file</span></div>';
      return;
    }

    const isShopify = headers.includes('Name') && headers.includes('Lineitem name');
    const todayStr = new Date().toISOString().split('T')[0];
    _importRows = rows.map((row, i) => {
      const get = col => {
        const idx = headers.indexOf(col);
        return idx >= 0 ? row[idx] || '' : '';
      };
      if (isShopify) {
        return {
          order_date:    _importParseDate(get('Created at')?.split(' ')[0]) || todayStr,
          customer_name: get('Shipping Name') || get('Billing Name'),
          postcode:      get('Shipping Zip'),
          address:       [get('Shipping Address1'),get('Shipping Address2'),get('Shipping City')].filter(Boolean).join(', '),
          phone:         get('Shipping Phone') || get('Billing Phone'),
          email:         get('Email'),
          platform_name: 'Website / Own Store',
          warehouse_name: '',
          payment_status: 'paid',
          product_name:  get('Lineitem name'),
          sku:           get('Lineitem sku'),
          quantity:      parseInt(get('Lineitem quantity'))||1,
          unit_price:    parseFloat(get('Lineitem price'))||0,
          external_ref:  get('Name'),
          valid:         !!(get('Shipping Name') && get('Lineitem name'))
        };
      } else {
        const rawDate = get('Order date (YYYY-MM-DD)') || get('Order date');
        const parsedDate = _importParseDate(rawDate);
        return {
          order_date:    parsedDate,
          customer_name: get('Customer name'),
          postcode:      get('Postcode'),
          address:       get('Address'),
          phone:         get('Phone'),
          email:         get('Email'),
          external_ref:  get('External ref'),
          platform_name: get('Platform'),
          warehouse_name: get('Warehouse'),
          payment_status: (get('Payment status (Paid/COD/Unpaid)') || get('Payment status') || 'paid').toLowerCase(),
          product_name:  get('Product name'),
          sku:           get('SKU'),
          quantity:      parseInt(get('Quantity'))||1,
          unit_price:    parseFloat(get('Unit price'))||0,
          notes:         get('Notes'),
          valid:         !!(get('Customer name') && get('Product name') && parsedDate)
        };
      }
    });

    const valid   = _importRows.filter(r => r.valid).length;
    const invalid = _importRows.length - valid;

    if (preview) preview.innerHTML = `
      <div class="ims__alert ims__alert--success" style="margin-top:12px"><i class="ti ti-check"></i>
        <span>Found <strong>${_importRows.length} rows</strong> — ${valid} valid, ${invalid} will be skipped</span>
      </div>
      <div class="ims__table-wrap" style="max-height:200px;overflow-y:auto;margin-top:8px">
        <table class="ims__table">
          <thead><tr><th>Customer</th><th>Product</th><th>Qty</th><th>Price</th><th>Valid</th></tr></thead>
          <tbody>${_importRows.slice(0,10).map(r => `
            <tr>
              <td>${util().sanitize(r.customer_name||'—')}</td>
              <td>${util().sanitize(r.product_name||'—')}</td>
              <td>${r.quantity}</td>
              <td>${util().formatCurrency(r.unit_price)}</td>
              <td>${r.valid?'<span class="ims__pill ims__pill--success">✓</span>':'<span class="ims__pill ims__pill--danger">✗</span>'}</td>
            </tr>`).join('')}
            ${_importRows.length > 10 ? `<tr><td colspan="5" class="ims__table-empty">...and ${_importRows.length-10} more rows</td></tr>` : ''}
          </tbody>
        </table>
      </div>`;

    if (commitBtn) commitBtn.style.display = valid > 0 ? 'inline-flex' : 'none';
  }

  // Looks up a product by SKU first (exact match), then by name (fuzzy),
  // for linking bulk-imported order lines to real stock.
  async function _importLookupProduct(sku, name) {
    if (sku) {
      const { data } = await db().select('products', { eq: { sku: sku.trim() }, select: 'id,name,sku' });
      if (data?.[0]) return data[0];
    }
    if (name) {
      const { data } = await db().select('products', { ilike: { name: name.trim() }, select: 'id,name,sku', limit: 1 });
      if (data?.[0]) return data[0];
    }
    return null;
  }

  // Resolves unit price + cost price for a product, mirroring the
  // platform → order-type → fallback logic used in manual order entry.
  async function _importResolvePrice(client, productId, orderType, platformId) {
    let price = 0;
    if (platformId) {
      const { data } = await client.from('product_prices').select('price').eq('product_id', productId).eq('price_type', 'platform').eq('platform_id', platformId).limit(1);
      if (data?.[0]) price = data[0].price;
    }
    if (!price) {
      const priceType = orderType === 'retail' ? 'rrp' : 'wholesale';
      const { data } = await client.from('product_prices').select('price').eq('product_id', productId).eq('price_type', priceType).is('platform_id', null).limit(1);
      if (data?.[0]) price = data[0].price;
    }
    if (!price) {
      const fallbackType = orderType === 'retail' ? 'wholesale' : 'rrp';
      const { data } = await client.from('product_prices').select('price').eq('product_id', productId).eq('price_type', fallbackType).is('platform_id', null).limit(1);
      if (data?.[0]) price = data[0].price;
    }
    const { data: costRows } = await client.from('product_prices').select('price').eq('product_id', productId).eq('price_type', 'cost').is('platform_id', null).limit(1);
    const costPrice = costRows?.[0]?.price || 0;
    return { price: price || 0, costPrice };
  }

  async function commitImport() {
    const valid = _importRows.filter(r => r.valid);
    if (!valid.length) return;

    const commitBtn = document.getElementById('ims-import-commit-btn');
    if (commitBtn) { commitBtn.disabled = true; commitBtn.innerHTML = '<i class="ti ti-loader-2 ims__spin"></i> Importing...'; }

    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    const { data: platforms } = await client.from('platforms').select('id,name,order_type').eq('is_active', true);
    const platformMap = {};
    (platforms||[]).forEach(p => { platformMap[p.name.toLowerCase()] = p; });

    const { data: warehouses } = await db().select('warehouses', { eq: { is_active: true } });
    const warehouseMap = {};
    (warehouses||[]).forEach(w => { warehouseMap[w.name.toLowerCase()] = w.id; });
    const defaultWarehouseId = (warehouses||[]).find(w => w.is_default)?.id || (warehouses||[])[0]?.id || null;
    let defaultedWarehouseCount = 0;

    let created = 0, itemsLinked = 0, itemsUnmatched = 0, stockShortfalls = 0;
    const groups = {};
    valid.forEach(row => {
      const key = row.external_ref || `${row.customer_name}||${row.postcode}||${row.order_date}`;
      if (!groups[key]) groups[key] = { ...row, items: [] };
      groups[key].items.push({ product_name: row.product_name, sku: row.sku, quantity: row.quantity, unit_price: row.unit_price });
    });

    const groupEntries = Object.entries(groups);
    const totalGroups = groupEntries.length;

    // Inject a progress bar into the modal so a large import doesn't look
    // like it's frozen — this can take real time (many sequential product
    // lookups, price resolutions, and stock updates per order).
    const preview = document.getElementById('ims-import-preview');
    if (preview) {
      preview.insertAdjacentHTML('afterbegin', `
        <div id="ims-import-progress" style="margin-bottom:14px">
          <div style="display:flex;justify-content:space-between;font-size:12px;color:var(--ims-text-muted);margin-bottom:5px">
            <span id="ims-import-progress-label">Starting import...</span>
            <span id="ims-import-progress-pct">0%</span>
          </div>
          <div style="background:var(--ims-border);border-radius:999px;height:8px;overflow:hidden">
            <div id="ims-import-progress-bar" style="height:8px;width:0%;background:var(--ims-accent);border-radius:999px;transition:width .25s"></div>
          </div>
        </div>`);
    }
    const setImportProgress = (done, label) => {
      const pct = totalGroups > 0 ? Math.round((done/totalGroups)*100) : 0;
      const bar = document.getElementById('ims-import-progress-bar');
      const pctEl = document.getElementById('ims-import-progress-pct');
      const labelEl = document.getElementById('ims-import-progress-label');
      if (bar) bar.style.width = pct+'%';
      if (pctEl) pctEl.textContent = pct+'%';
      if (labelEl) labelEl.textContent = label;
    };

    // Filter to ORD- prefixed orders only — 'orders' also holds DSO- (dropship)
    // and other-prefixed rows, so an unfiltered lookup can pick up the wrong
    // prefix and generate numbers that collide with existing ORD- orders.
    const { data: lastOrder } = await db().select('orders', { select: 'order_number', ilike: { order_number: 'ORD-' }, order: { col: 'order_number', asc: false }, limit: 1 });
    let orderNum = lastOrder?.[0]?.order_number ? parseInt(lastOrder[0].order_number.split('-').pop()) || 0 : 0;

    let processed = 0;
    // Orders that could not be saved. Listed in the final message instead
    // of being skipped silently.
    const failedOrders = [];
    for (const [key, group] of groupEntries) {
      processed++;
      setImportProgress(processed - 1, `Importing order ${processed} of ${totalGroups}: ${group.customer_name}`);

      const platform = platformMap[(group.platform_name||'').toLowerCase()] || null;
      const platformId = platform?.id || null;
      const orderType = platform?.order_type || 'retail';

      let warehouseId = warehouseMap[(group.warehouse_name||'').toLowerCase()] || null;
      if (!warehouseId) { warehouseId = defaultWarehouseId; defaultedWarehouseCount++; }

      const paymentStatus = ['paid','cod','unpaid'].includes(group.payment_status) ? group.payment_status : 'paid';
      const unpaidExpiresAt = paymentStatus === 'unpaid' ? new Date(Date.now() + 7*24*60*60*1000).toISOString() : null;

      let matchedCustomerId = null;
      if (group.phone) {
        const { data: cust } = await db().select('customers', { eq: { phone: group.phone.trim(), is_active: true }, maybeSingle: true });   // deactivated customers are not matched
        if (cust) matchedCustomerId = cust.id;
      }

      // Same rule as manual order entry: a wholesale order that is not paid
      // in full must be linked to a registered customer (matched here by
      // phone), or nothing owed would ever reach Accounts. Skip it and list
      // it in the end message so it can be fixed and imported again.
      if (orderType === 'wholesale' && !matchedCustomerId && paymentStatus !== 'paid') {
        failedOrders.push(`${group.customer_name} (wholesale and not paid, but no registered customer matches its phone number)`);
        continue;
      }

      // Resolve each line item: link to a real product where possible,
      // fill in a standard price if the CSV left it blank.
      const resolvedItems = await Promise.all(group.items.map(async item => {
        const product = await _importLookupProduct(item.sku, item.product_name);
        let unitPrice = item.unit_price;
        let costPrice = 0;
        if (product) {
          itemsLinked++;
          if (!unitPrice) {
            const resolved = await _importResolvePrice(client, product.id, orderType, platformId);
            unitPrice = resolved.price;
            costPrice = resolved.costPrice;
          }
        } else {
          itemsUnmatched++;
        }
        return { ...item, product_id: product?.id || null, unit_price: unitPrice || 0, cost_price: costPrice };
      }));

      const total = resolvedItems.reduce((s, i) => s + (i.quantity * i.unit_price), 0);
      const orderDateTime = new Date(`${group.order_date}T${new Date().toTimeString().split(' ')[0]}`);

      // Retry-on-collision, same protection as manual order entry
      let order, error;
      for (let attempt = 0; attempt < 3; attempt++) {
        orderNum++;
        const orderNumber = `ORD-${String(orderNum).padStart(5,'0')}`;
        const result = await db().insert('orders', {
          order_number:      orderNumber,
          platform_id:       platformId,
          order_type:        orderType,
          fulfillment_type:  'retail',
          customer_name:     group.customer_name,
          customer_id:       matchedCustomerId,
          warehouse_id:      warehouseId,
          postcode:          group.postcode,
          address:           group.address,
          phone:             group.phone,
          email:             group.email,
          external_ref:      group.external_ref,
          notes:             group.notes,
          status:            'confirmed',
          created_at:        orderDateTime.toISOString(),
          payment_status:    paymentStatus,
          unpaid_expires_at: unpaidExpiresAt,
          total_value:       total,
          has_price_override: false,
          created_by:        window.SM.AUTH.user?.id
        });
        order = result.data; error = result.error;
        if (!error) break;
        const isDupe = error.code === '23505' || /duplicate key/i.test(error.message||'');
        if (!isDupe) break;
      }

      if (!error && order) {
        // Save every item line in ONE insert: either all lines save or none
        // do. order_items has no company_id column, so the raw client is used.
        const _impClient = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
        const { error: impItemsError } = await _impClient.from('order_items').insert(resolvedItems.map(item => ({
          order_id:     order.id,
          product_id:   item.product_id,
          product_name: item.product_name,
          sku:          item.sku,
          quantity:     item.quantity,
          unit_price:   item.unit_price,
          total_price:  item.quantity * item.unit_price,
          cost_price:   item.cost_price
        })));
        if (impItemsError) {
          // Undo the half-saved order so it never appears with no items.
          const { error: undoError } = await db().remove('orders', order.id);
          failedOrders.push(undoError
            ? `${group.customer_name} (${order.order_number} was left with no items, please cancel it)`
            : group.customer_name);
          continue;
        }

        // Reserve stock one line at a time, same as manual order entry, so
        // two lines of the same product cannot overwrite each other.
        for (const item of resolvedItems) {
          if (!item.product_id || !warehouseId) continue;
          const { data: sl } = await db().select('stock_levels', {
            eq: { product_id: item.product_id, warehouse_id: warehouseId }, maybeSingle: true, noCompanyFilter: true
          });
          if (sl) {
            const available = (sl.quantity||0) - (sl.reserved_qty||0);
            if (item.quantity > available) stockShortfalls++;
            await db().update('stock_levels', sl.id, { reserved_qty: (sl.reserved_qty||0) + item.quantity });
          } else {
            stockShortfalls++;
            await db().insert('stock_levels', { product_id: item.product_id, warehouse_id: warehouseId, quantity: 0, reserved_qty: item.quantity });
          }
        }

        await logActivity(order.id, 'Order created', '', 'confirmed', `Bulk imported. Customer: ${group.customer_name}, Value: £${total.toFixed(2)}`);
        created++;
      } else {
        failedOrders.push(group.customer_name);
      }
    }

    ui().closeModal();
    let msg = `${created} order(s) imported`;
    if (itemsUnmatched > 0) msg += ` — ${itemsUnmatched} item(s) had no matching product (stock not reserved for these)`;
    if (stockShortfalls > 0) msg += ` — ${stockShortfalls} item(s) exceeded available stock, please review`;
    if (defaultedWarehouseCount > 0) msg += ` — ${defaultedWarehouseCount} order(s) used the default warehouse`;
    if (failedOrders.length) msg += `. ${failedOrders.length} order(s) could not be saved and were skipped: ${failedOrders.join(', ')}. Please check these and import them again`;
    ui().toast(msg, failedOrders.length ? 'error' : (itemsUnmatched||stockShortfalls ? 'warning' : 'success'), failedOrders.length ? 15000 : 8000);
    _importRows = [];
    load();
  }

  // ─── BINDINGS ────────────────────────────────────────────────────
  window.addEventListener('ims:panel', e => {
    if (e.detail.panel === 'orders') {
      _orderItems = [];
      load();
      // Check for expired unpaid orders on every panel open
      setTimeout(checkUnpaidExpiry, 800);
    }
  });

  window.addEventListener('ims:ready', () => {
    document.getElementById('ims-orders-new-btn')?.addEventListener('click', showNewOrder);
    document.getElementById('ims-orders-import-btn')?.addEventListener('click', showImport);
    document.getElementById('ims-orders-history-btn')?.addEventListener('click', showHistory);
    ['ims-orders-search','ims-orders-status','ims-orders-platform','ims-orders-type'].forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      el.addEventListener('input', util().debounce(load, 350));
      el.addEventListener('change', load);
    });
  });

  window.IMS_ORDERS = { arrangeVanPickup, exportHistoryItems,
    load, openOrder, updateStatus, cancelOrder, showNewOrder, onPlatformChange,
    fillCustomer, fillCustomerFromAccount, clearCustomer, addItem, updateItem, removeItem, updateTotal, checkOverride,
    onWarehouseChange, toggleAllocationPanel, releaseFromAllocation, undoRelease,
    searchProduct, selectProduct, saveOrder,
    updateCustomerLock, clearPickedCustomer, addNewCustomerFromOrder,
    showQuickAddCustomer, saveQuickAddCustomer, cancelQuickAddCustomer,
    toggleAltDelivery,
    editOrderCustomer, searchEditCustomer, selectEditCustomer, saveOrderCustomer, amendOrder, addService,
    printOrder,
    showImport, downloadTemplate, previewImport, commitImport,
    markPaid, confirmPayment, showReturnModal, submitReturn,
    markReturnReceived, processRefund, showRefundNoReturn, confirmRefundNoReturn,
    showHistory, loadHistory, renderHistoryTable, goHistoryPage, viewOrderFromHistory,
    showActive, exportHistory, clearHistoryFilters, showActivityLog,
    checkUnpaidExpiry, convertUnpaid, cancelUnpaid,
    cancelBatch, showBatchPaymentReview, confirmBatchPayment,
    toggleSelectOrder, toggleSelectAll, clearSelection, applyBulkStatus,
    goPage: (p) => { _currentPage = p; renderTable(); }
  };

})();
